import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncActionButton from "../components/AsyncActionButton";
import { useLanguage } from "../i18n/LanguageContext";
import { formatMoneyInput, parseMoneyInput, formatMoneyDisplayNumber, moneyInputProps } from "../utils/priceFormat";
import { listLabel } from "../utils/i18nOptions";
import FinansOzetKarti from "../components/FinansOzetKarti";
import ExpandableText from "../components/ExpandableText";
import ReminderPanel, { ReminderWarning, reminderPayloadKontrolEt } from "../components/ReminderPanel";
import harcamalarAsset from "../assets/harcamalarasset.png";
import finansAsset from "../assets/finansasset.png";
import birikimAsset from "../assets/birikimasset.png";
import taksitlerAsset from "../assets/taksitlerasset.png";
import aboneliklerAsset from "../assets/aboneliklerasset.png";
import faturalarAsset from "../assets/faturalarasset.png";
import { appAlert, appConfirm } from "../components/AppDialog";
import { financeAmountInBase } from "../utils/financeCurrency";
import {
  MAX_INSTALLMENT_LEDGER_CHANGES,
  MAX_STANDARD_INSTALLMENT_TOTAL,
  validateStandardInstallmentCounts,
} from "../utils/financeInstallmentValidation";
import {
  addFinanceSaving,
  addHomeInstallment,
  addFinanceDebtPayment,
  deleteHomeInstallmentReminders,
  updateHomeInstallment,
  deleteFinanceSaving,
  deleteHomeInstallment,
  listenFinanceSavings,
  listenHomeInstallments,
  updateFinanceSaving,
  updateFinanceSavingsCurrentPriceByAsset,
  listenGlobalAssetPrices,
  addFinanceSubscription,
  updateFinanceSubscription,
  deleteFinanceSubscription,
  listenFinanceSubscriptions,
  addFinanceBill,
  updateFinanceBill,
  deleteFinanceBill,
  listenFinanceBills,
  getCachedFinanceToolItems,
} from "../services/financeToolsService";
import { readSensitiveItem, removeSensitiveItem } from "../utils/sensitiveSessionStorage";

const FINANCE_FEATURE_ASSETS = [birikimAsset, taksitlerAsset, aboneliklerAsset, faturalarAsset];
const FINANCE_PAYMENT_ASSET_BY_SOURCE = Object.freeze({
  installment: taksitlerAsset,
  subscription: aboneliklerAsset,
  bill: faturalarAsset,
});

function financeExpenseSourceAsset(item = {}) {
  if (item.ortakHarcama) return harcamalarAsset;
  if (item.linkedFinancePayment !== true) return null;
  return FINANCE_PAYMENT_ASSET_BY_SOURCE[String(item.financePaymentSourceType || "").toLowerCase()] || null;
}

function financeExpenseCategoryLabel(item = {}, lang, tList) {
  if (item.linkedFinancePayment === true) {
    const sourceType = String(item.financePaymentSourceType || "").toLowerCase();
    const labels = {
      installment: { tr: "Taksit", en: "Installment" },
      subscription: { tr: "Abonelik", en: "Subscription" },
      bill: { tr: "Fatura", en: "Bill" },
    };
    if (labels[sourceType]) return labels[sourceType][lang] || labels[sourceType].tr;
  }
  return listLabel(tList, item.tur === "Gelir" ? "const.incomeCategories" : "const.expenseCategories", item.kategori);
}

// Yerel paket dosyaları uygulama açılışında WebView cache ve decode sırasına alınır.
// Böylece finans kartları ilk görünümde uzaktan veya gecikmeli görsel yüklemez.
if (typeof document !== "undefined") {
  FINANCE_FEATURE_ASSETS.forEach((src) => {
    if (!document.head.querySelector(`link[data-finance-asset="${src}"]`)) {
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = src;
      preload.dataset.financeAsset = src;
      document.head.appendChild(preload);
    }
    const image = new Image();
    image.decoding = "sync";
    image.src = src;
  });
}


const FINANCE_LABELS = {
  "Gayrimenkul":"Real Estate", "Altın":"Gold", "Mevduat":"Deposit", "Borsa":"Stocks", "Kripto":"Crypto", "Döviz":"Foreign Currency", "Fon":"Fund", "Nakit":"Cash", "Diğer":"Other",
  "Gram Altın":"Gram Gold", "Çeyrek Altın":"Quarter Gold", "Yarım Altın":"Half Gold", "Tam Altın":"Full Gold", "Cumhuriyet Altını":"Republic Gold", "Ata Altın":"Ata Gold", "Bilezik":"Bracelet", "Ons Altın":"Gold Ounce",
  "Kredi Kartı":"Credit Card", "İhtiyaç Kredisi":"Consumer Loan", "Araç Kredisi":"Car Loan", "Ev Kredisi":"Mortgage", "Telefon":"Phone", "Beyaz Eşya":"White Goods", "Mobilya":"Furniture", "Eğitim":"Education",
  "Eğlence":"Entertainment", "Müzik":"Music", "Oyun":"Gaming", "Yazılım":"Software", "Bulut Depolama":"Cloud Storage", "Haber & Dergi":"News & Magazine", "Spor & Sağlık":"Sports & Health", "Alışveriş":"Shopping",
  "Aylık":"Monthly", "3 Aylık":"Quarterly", "6 Aylık":"Every 6 Months", "Yıllık":"Yearly",
  "Elektrik":"Electricity", "Doğalgaz":"Natural Gas", "Su":"Water", "İnternet":"Internet", "Telefon (Fatura)":"Phone Bill", "Kira":"Rent", "Aidat":"Dues", "Sigorta":"Insurance", "Vergi":"Tax",
  "Ödenmedi":"Unpaid", "Ödendi":"Paid", "Beklemede":"Pending", "Veri yok":"No data", "ödenmemiş fatura":"unpaid bills",
  "Birikim kaydı eklenemedi":"Saving record could not be added", "Birikim kaydı güncellenemedi":"Saving record could not be updated", "Taksit kaydı eklenemedi":"Installment record could not be added",
  "faturasının son ödeme tarihi yaklaşıyor!":"bill due date is approaching!",
  "Bu dönem en çok":"This period, most spending was in",
  "kategorisinde harcama yapılmış.":"category.",
  "Araç/Yakıt gideri bu dönem":"Vehicle/Fuel expense this period is",
  "seviyesinde.":"level.",
  "birikimini güncellemeyi unutma!":"do not forget to update your saving!",
  "Taksit Ödeme Hatırlatması":"Installment Payment Reminder",
  "taksit ödemesi yaklaşıyor!":"installment payment is approaching!",
  "Abonelik Yenileme Hatırlatması":"Subscription Renewal Reminder",
  "aboneliğin yakında yenileniyor!":"your subscription is renewing soon!",
  "Fatura Ödeme Hatırlatması":"Bill Payment Reminder",
  "Birikim kaydı eklenemedi. Lütfen tekrar dene.":"Saving record could not be added. Please try again.",
  "Taksit kaydı eklenemedi. Lütfen tekrar dene.":"Installment record could not be added. Please try again.",
  "Birikim kaydı güncellenemedi. Lütfen tekrar dene.":"Saving record could not be updated. Please try again.",
  "Birikim kaydı silinemedi. Lütfen tekrar dene.":"Saving record could not be deleted. Please try again.",
  "Taksit kaydı silinemedi. Lütfen tekrar dene.":"Installment record could not be deleted. Please try again.",
  "Abonelik eklenemedi. Lütfen tekrar dene.":"Subscription could not be added. Please try again.",
  "Abonelik silinemedi.":"Subscription could not be deleted.",
  "Fatura eklenemedi. Lütfen tekrar dene.":"Bill could not be added. Please try again.",
  "Fatura silinemedi.":"Bill could not be deleted.",
  "Akıllı Finans Uyarıları":"Smart Finance Alerts",
  "Birikim":"Savings",
  "Taksitler":"Installments",
  "Abonelikler":"Subscriptions",
  "Faturalar":"Bills",
  "Kâr":"Profit",
  "Zarar":"Loss",
  "aktif":"active",
  "Kalan":"Remaining",
  "Sil":"Delete",
  "Aylık maliyet":"Monthly cost", "Finansa Bağlı Aile Harcamaları":"Finance-Linked Family Expenses", "Ortak harcamalar otomatik olarak finans giderlerine bağlı kalır.":"Shared expenses stay automatically linked to finance expenses.", "Kişi Sayısı":"People Count", "Kayıt":"Record", "Dönem Filtresi":"Period Filter", "Tüm Aylar":"All Months", "Yıl":"Year",
  "Evet":"Yes", "Hayır":"No", "Maaş":"Salary", "Market":"Groceries", "Ev":"Home", "Yakıt":"Fuel", "Gym/Spor":"Gym/Sports",
  "Bekleyen":"Due", "bekleyen":"pending", "ödendi":"paid",
  "Daha fazla göster":"Show more", "Daha az göster":"Show less",
  "Gelir":"Income", "Gider":"Expenses",
};
const fl = (lang, text) => (lang === "en" ? (FINANCE_LABELS[text] || text) : text);

const FINANCE_REPORT_TEXTS = {
  tr: {
    thirtyDayIncome: "30 Gün Gelir",
    thirtyDayExpense: "30 Gün Gider",
    netResult: "Net Durum",
    comparisonTitle: "Gelir / Gider Performansı",
    comparisonDesc: "Son 30 günün gelir, gider ve net akışı",
    incomeBreakdown: "Gelir Dağılımı",
    topSpending: "Harcama Dağılımı",
    financialInsight: "Finans Asistanı",
    income: "Gelir",
    expense: "Gider",
    net: "Net",
    notEnoughData: "Yeterli veri bulunamadı.",
    noExpenseData: "Son 30 gün için gider verisi bulunamadı.",
    noIncomeData: "Son 30 gün için gelir verisi bulunamadı.",
    expenseRatio: "Son 30 günde giderlerin gelirlerinin %{ratio} kadar.",
    topCategory: "En yüksek harcama %{category} kategorisinde.",
    netPositive: "Net durum pozitif görünüyor.",
    netNegative: "Net durum negatif, harcamaları kontrol etmek faydalı olabilir.",
  },
  en: {
    thirtyDayIncome: "30 Day Income",
    thirtyDayExpense: "30 Day Expense",
    netResult: "Net Result",
    comparisonTitle: "Income / Expense Performance",
    comparisonDesc: "Income, expense and net flow over the last 30 days",
    incomeBreakdown: "Income Breakdown",
    topSpending: "Spending Breakdown",
    financialInsight: "Finance Assistant",
    income: "Income",
    expense: "Expense",
    net: "Net",
    notEnoughData: "Not enough data available.",
    noExpenseData: "No expense data found for the last 30 days.",
    noIncomeData: "No income data found for the last 30 days.",
    expenseRatio: "Expenses are %{ratio} of income over the last 30 days.",
    topCategory: "Highest spending is in %{category}.",
    netPositive: "Net result looks positive.",
    netNegative: "Net result is negative; reviewing expenses may help.",
  },
};

const financeReportText = (lang, key, params = {}) => {
  const dict = FINANCE_REPORT_TEXTS[lang === "en" ? "en" : "tr"] || FINANCE_REPORT_TEXTS.tr;
  let text = dict[key] || key;
  Object.entries(params).forEach(([paramKey, value]) => {
    text = text.replace(`%{${paramKey}}`, value);
  });
  return text;
};

const FINANCE_PRIVACY_STORAGE_KEY = "familyPlanner.finance.amountsHidden";
const FINANCE_BREAKDOWN_STORAGE_KEY = "familyPlanner.finance.breakdownView";
const financeBreakdownTypeStorageKey = (familyId, userUid) => (
  `familyPlanner.finance.breakdownType.${familyId || "none"}.${userUid || "guest"}`
);
const readFinanceBreakdownType = (storageKey) => {
  try { return localStorage.getItem(storageKey) === "income" ? "income" : "expense"; }
  catch (_) { return "expense"; }
};

function FinanceBreakdownPie({ items, total, paraFormatla, labelFormatter, emptyText }) {
  const colors = ["#7c3aed", "#ec4899", "#f59e0b", "#06b6d4", "#22c55e"];
  if (!items.length || total <= 0) return <div className="emptyState compact">{emptyText}</div>;
  let offset = 0;
  const segments = items.slice(0, 5).map((item, index) => {
    const value = Math.max(0, Number(item.tutar || 0));
    const ratio = value / total;
    const segment = { ...item, ratio, offset, color: colors[index % colors.length] };
    offset += ratio;
    return segment;
  });
  const circumference = 2 * Math.PI * 42;
  return (
    <div className="financeBreakdownPieLayout">
      <div className="financeBreakdownDonut" aria-label={labelFormatter(items[0].kategori)}>
        <svg viewBox="0 0 108 108" role="img">
          <circle className="donutTrack" cx="54" cy="54" r="42" />
          {segments.map((item) => (
            <circle
              key={item.kategori}
              className="donutSegment"
              cx="54" cy="54" r="42"
              stroke={item.color}
              strokeDasharray={`${item.ratio * circumference} ${circumference}`}
              strokeDashoffset={-item.offset * circumference}
            />
          ))}
        </svg>
        <div><strong>{Math.round((segments[0]?.ratio || 0) * 100)}%</strong><span>{labelFormatter(segments[0]?.kategori)}</span></div>
      </div>
      <div className="financeBreakdownLegend">
        {segments.map((item) => (
          <div key={item.kategori}>
            <i style={{ background: item.color }} />
            <span>{labelFormatter(item.kategori)}</span>
            <b>{paraFormatla(item.tutar)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}
const SAVING_TYPES = ["Altın", "Mevduat", "Borsa", "Kripto", "Döviz", "Fon", "Nakit", "Gayrimenkul"];
const REAL_ESTATE_TYPE_OPTIONS = ["apartment", "house", "villa", "land", "commercial", "office", "shop", "warehouse", "other"];
const REAL_ESTATE_USE_OPTIONS = ["owner_occupied", "rented", "vacant", "under_construction"];
const REAL_ESTATE_DEED_OPTIONS = ["full", "shared", "mortgaged", "other"];
const GOLD_TYPES_TR = ["Gram Altın", "Çeyrek Altın", "Yarım Altın", "Tam Altın", "Cumhuriyet Altını", "Ata Altın", "Bilezik", "Ons Altın"];
const GOLD_TYPES_EN = ["Gold Gram", "Gold Ounce", "Gold Bar", "Gold Coin", "Gold ETF", "Gold Fund", "Gold Jewelry", "Other Gold"];
const getGoldTypeOptions = (lang) => (lang === "en" ? GOLD_TYPES_EN : GOLD_TYPES_TR);
const GOLD_MARKET_KEYS = {
  "Gram Altın": "Altın:Gram",
  "Çeyrek Altın": "Altın:Çeyrek",
  "Ceyrek Altın": "Altın:Ceyrek",
  "Yarım Altın": "Altın:Yarım",
  "Yarim Altın": "Altın:Yarim",
  "Tam Altın": "Altın:Tam",
  "Cumhuriyet Altını": "Altın:Cumhuriyet",
  "Ata Altın": "Altın:Ata",
  "Bilezik": "Altın:Bilezik",
  "Ons Altın": "Altın:Ons",
  "Gold Gram": "Gold:Gram",
  "Gold Ounce": "Gold:Ounce",
  "Gold Bar": "Gold:Bar",
  "Gold Coin": "Gold:Coin",
  "Gold ETF": "Gold:ETF",
  "Gold Fund": "Gold:Fund",
  "Gold Jewelry": "Gold:Jewelry",
  "Other Gold": "Gold:Other",
};

const GOLD_DEBT_CATEGORY = "__gold_debt__";
const FX_DEBT_CATEGORY = "__fx_debt__";
const debtKindForCategory = (category) => category === GOLD_DEBT_CATEGORY ? "gold" : category === FX_DEBT_CATEGORY ? "foreign_currency" : "standard";
const normalizeCurrencyCode = (value, fallback = "TRY") => {
  const code = String(value || fallback).toUpperCase();
  return code === "TL" ? "TRY" : code;
};
const getDebtRegion = (lang, currency) => {
  const normalizedCurrency = normalizeCurrencyCode(currency, lang === "en" ? "EUR" : "TRY");
  if (lang === "tr" && normalizedCurrency === "TRY") return "tr";
  if (lang === "en" && normalizedCurrency === "USD") return "us";
  if (lang === "en" && normalizedCurrency === "EUR") return "eu";
  return "international";
};
const GOLD_DEBT_OPTIONS = {
  tr: [
    { value: "gram", assetKey: "Altın:Gram", unit: "gram" },
    { value: "quarter", assetKey: "Altın:Çeyrek", unit: "piece" },
    { value: "half", assetKey: "Altın:Yarım", unit: "piece" },
    { value: "full", assetKey: "Altın:Tam", unit: "piece" },
    { value: "republic", assetKey: "Altın:Cumhuriyet", unit: "piece" },
    { value: "ata", assetKey: "Altın:Ata", unit: "piece" },
    { value: "bracelet", assetKey: "Altın:Bilezik", unit: "gram", requiresKarat: true },
    { value: "other_jewelry", assetKey: "Altın:Gram", unit: "gram", requiresKarat: true },
  ],
  eu: [
    { value: "gram", assetKey: "Gold:Gram", unit: "gram" },
    { value: "bar", assetKey: "Gold:Gram", unit: "gram", requiresPurity: true },
    { value: "coin", assetKey: "Gold:Ounce", unit: "ounce", requiresPurity: true },
    { value: "jewelry", assetKey: "Gold:Gram", unit: "gram", requiresKarat: true },
    { value: "custom", assetKey: "Gold:Gram", unit: "gram", requiresPurity: true },
  ],
  us: [
    { value: "ounce", assetKey: "Gold:Ounce", unit: "ounce" },
    { value: "gram", assetKey: "Gold:Gram", unit: "gram" },
    { value: "bar", assetKey: "Gold:Gram", unit: "gram", requiresPurity: true },
    { value: "coin", assetKey: "Gold:Ounce", unit: "ounce", requiresPurity: true },
    { value: "jewelry", assetKey: "Gold:Gram", unit: "gram", requiresKarat: true },
    { value: "custom", assetKey: "Gold:Ounce", unit: "ounce", requiresPurity: true },
  ],
  international: [
    { value: "gram", assetKey: "Gold:Gram", unit: "gram" },
    { value: "ounce", assetKey: "Gold:Ounce", unit: "ounce" },
    { value: "bar", assetKey: "Gold:Gram", unit: "gram", requiresPurity: true },
    { value: "coin", assetKey: "Gold:Ounce", unit: "ounce", requiresPurity: true },
    { value: "jewelry", assetKey: "Gold:Gram", unit: "gram", requiresKarat: true },
  ],
};
const newGoldDebtItem = (region = "tr") => {
  const option = (GOLD_DEBT_OPTIONS[region] || GOLD_DEBT_OPTIONS.international)[0];
  return { id: `gold-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, type: option.value, assetKey: option.assetKey, unit: option.unit, quantity: "", karat: "", purity: "", label: "" };
};
const emptyInstallmentDraft = (category, region, currency) => withFinanceNotificationTargetDefaults({
  ad: "", kategori: category || "", debtKind: debtKindForCategory(category),
  toplam: "", taksitSayisi: "", odenenTaksit: "", aylikOdeme: "", baslangic: "", odemeGunu: "", not: "",
  valuationCurrency: normalizeCurrencyCode(currency), monthlyPaymentAmount: "", monthlyPaymentCurrency: normalizeCurrencyCode(currency),
  goldItems: [newGoldDebtItem(region)], debtCurrency: normalizeCurrencyCode(currency) === "TRY" ? "USD" : "USD", foreignPrincipal: "",
});

const marketEntry = (prices, key) => {
  const raw = prices?.[key];
  if (raw === undefined || raw === null) return null;
  const price = extractMarketPrice(raw);
  if (!price) return null;
  return { price, currency: normalizeCurrencyCode(typeof raw === "object" ? raw.currency : "TRY") };
};
const fxTryRate = (prices, currency) => {
  const code = normalizeCurrencyCode(currency);
  if (code === "TRY") return 1;
  const direct = marketEntry(prices, `Döviz:${code}`)?.price;
  if (direct) return direct;
  if (code === "USD") {
    const trGram = marketEntry(prices, "Altın:Gram")?.price;
    const usdGram = marketEntry(prices, "Gold:Gram")?.price;
    return trGram && usdGram ? trGram / usdGram : null;
  }
  return null;
};
const convertDebtCurrency = (amount, from, to, prices) => {
  const number = Number(amount);
  if (!Number.isFinite(number)) return null;
  const source = normalizeCurrencyCode(from);
  const target = normalizeCurrencyCode(to);
  if (source === target) return number;
  const sourceTry = fxTryRate(prices, source);
  const targetTry = fxTryRate(prices, target);
  return sourceTry && targetTry ? (number * sourceTry) / targetTry : null;
};
const goldPurityFactor = (item) => {
  if (Number(item?.karat) > 0) return Math.min(1, Number(item.karat) / 24);
  if (Number(item?.purity) > 0) return Math.min(1, Number(item.purity) > 1 ? Number(item.purity) / 1000 : Number(item.purity));
  return 1;
};
const goldDebtValue = (items, prices, targetCurrency) => {
  let total = 0;
  for (const item of (items || [])) {
    const entry = marketEntry(prices, item.assetKey);
    if (!entry) return null;
    const purity = item.karat || item.purity || /bracelet|jewelry|other_jewelry/.test(item.type) ? goldPurityFactor(item) : 1;
    const converted = convertDebtCurrency(Number(item.quantity || 0) * entry.price * purity, entry.currency, targetCurrency, prices);
    if (converted === null) return null;
    total += converted;
  }
  return total;
};
const debtMoneyFormat = (value, currency, lang) => new Intl.NumberFormat(lang === "en" ? "en-US" : "tr-TR", {
  style: "currency", currency: normalizeCurrencyCode(currency), maximumFractionDigits: 2,
}).format(Number(value || 0));

const LEGACY_GOLD_KEY_MAP = {
  "Altın:GRAM ALTIN": "Altın:Gram",
  "Altın:CEYREK ALTIN": "Altın:Ceyrek",
  "Altın:ÇEYREK ALTIN": "Altın:Çeyrek",
  "Altın:YARIM ALTIN": "Altın:Yarım",
  "Altın:YARIM": "Altın:Yarım",
  "Altın:TAM ALTIN": "Altın:Tam",
  "Altın:CUMHURIYET ALTINI": "Altın:Cumhuriyet",
  "Altın:CUMHURİYET ALTINI": "Altın:Cumhuriyet",
  "Altın:ATA ALTIN": "Altın:Ata",
  "Altın:BILEZIK": "Altın:Bilezik",
  "Altın:BİLEZİK": "Altın:Bilezik",
  "Altın:ONS ALTIN": "Altın:Ons",
  "Gold:GOLD GRAM": "Gold:Gram",
  "Gold:GRAM GOLD": "Gold:Gram",
  "Gold:GOLD OUNCE": "Gold:Ounce",
  "Gold:OUNCE GOLD": "Gold:Ounce",
  "Gold:GOLD BAR": "Gold:Bar",
  "Gold:GOLD COIN": "Gold:Coin",
  "Gold:GOLD ETF": "Gold:ETF",
  "Gold:GOLD FUND": "Gold:Fund",
  "Gold:GOLD JEWELRY": "Gold:Jewelry",
  "Gold:OTHER GOLD": "Gold:Other",
};

const normalizeMarketAssetKey = (saving = {}) => {
  if (!saving) return "";
  if (isGoldSavingType(saving)) {
    const goldName = saving.altinTuru || saving.assetName || saving.ad || "Gram Altın";
    const exact = GOLD_MARKET_KEYS[goldName];
    if (exact) return exact;
    const legacy = `Altın:${normalizeAssetPart(goldName)}`;
    return LEGACY_GOLD_KEY_MAP[legacy] || legacy;
  }
  if (isCurrencySavingType(saving)) {
    const code = saving.dovizCinsi || saving.paraBirimi || saving.assetName || saving.ad || "USD";
    return `Döviz:${normalizeAssetPart(code)}`;
  }
  if (saving.assetKey && LEGACY_GOLD_KEY_MAP[saving.assetKey]) return LEGACY_GOLD_KEY_MAP[saving.assetKey];
  return saving.assetKey || getSavingAssetInfo(saving).assetKey;
};


const emptySavingDraft = (defaultCurrency = "TRY") => ({
  ad: "",
  tur: "Altın",
  altinTuru: "Gram Altın",
  paraBirimi: defaultCurrency,
  miktar: "",
  alisFiyati: "",
  guncelFiyat: "",
  tarih: "",
  gayrimenkulTuru: "",
  sehir: "",
  ilce: "",
  mahalle: "",
  brutMetrekare: "",
  odaSayisi: "",
  kullanimDurumu: "owner_occupied",
  aylikKiraGeliri: "",
  kiraBaslangic: "",
  tapuDurumu: "full",
  sahiplikPayi: "100",
  kalanKrediBorcu: "",
  aylikKrediOdemesi: "",
  banka: "",
  faizOrani: "",
  vadeBaslangic: "",
  vadeBitis: "",
  hisseKodu: "",
  hisseAdi: "",
  coinSembol: "",
  coinAdi: "",
  platform: "",
  dovizCinsi: "USD",
  fonKodu: "",
  fonAdi: "",
  saklamaYeri: "",
  not: "",
});

const normalizeAssetPart = (value) => String(value || "").trim().toUpperCase();

const isGoldSavingType = (saving = {}) => saving?.tur === "Altın" || saving?.tur === "Gold";
const isCurrencySavingType = (saving = {}) => saving?.tur === "Döviz" || saving?.tur === "Foreign Currency";
const isAutoMarketSavingType = (saving = {}) => isGoldSavingType(saving) || isCurrencySavingType(saving);

const getSavingAssetInfo = (saving = {}) => {
  const tur = saving.tur || "Altın";
  if (tur === "Gayrimenkul" || tur === "Real Estate") {
    const name = saving.ad || saving.assetName || (tur === "Real Estate" ? "Property" : "Gayrimenkul");
    return { assetKey: `Gayrimenkul:${normalizeAssetPart(name)}`, assetName: name };
  }
  if (tur === "Altın" || tur === "Gold") {
    const name = saving.altinTuru || saving.assetName || saving.ad || (tur === "Gold" ? "Gold Gram" : "Gram Altın");
    return { assetKey: GOLD_MARKET_KEYS[name] || `${tur === "Gold" ? "Gold" : "Altın"}:${normalizeAssetPart(name)}`, assetName: name };
  }
  if (tur === "Mevduat") {
    const bank = saving.banka || saving.ad || "Mevduat";
    const currency = saving.paraBirimi || "TRY";
    return { assetKey: `Mevduat:${normalizeAssetPart(bank)}:${normalizeAssetPart(currency)}`, assetName: `${bank} ${currency}`.trim() };
  }
  if (tur === "Borsa") {
    const code = saving.hisseKodu || saving.assetName || saving.ad || "HISSE";
    return { assetKey: `Borsa:${normalizeAssetPart(code)}`, assetName: normalizeAssetPart(code) };
  }
  if (tur === "Kripto") {
    const code = saving.coinSembol || saving.assetName || saving.ad || "COIN";
    return { assetKey: `Kripto:${normalizeAssetPart(code)}`, assetName: normalizeAssetPart(code) };
  }
  if (tur === "Döviz" || tur === "Foreign Currency") {
    const code = saving.dovizCinsi || saving.paraBirimi || saving.assetName || saving.ad || "USD";
    return { assetKey: `Döviz:${normalizeAssetPart(code)}`, assetName: normalizeAssetPart(code) };
  }
  if (tur === "Fon") {
    const code = saving.fonKodu || saving.assetName || saving.ad || "FON";
    return { assetKey: `Fon:${normalizeAssetPart(code)}`, assetName: normalizeAssetPart(code) };
  }
  if (tur === "Nakit") {
    const code = saving.paraBirimi || saving.assetName || "TRY";
    return { assetKey: `Nakit:${normalizeAssetPart(code)}`, assetName: normalizeAssetPart(code) };
  }
  return { assetKey: `${tur}:${normalizeAssetPart(saving.ad || tur)}`, assetName: saving.ad || tur };
};

const getSavingDisplayName = (saving = {}) => {
  if (saving.ad) return saving.ad;
  if (saving.tur === "Altın") return saving.altinTuru || "Gram Altın";
  if (saving.tur === "Mevduat") return saving.banka || "Mevduat";
  if (saving.tur === "Borsa") return saving.hisseKodu || saving.hisseAdi || "Hisse";
  if (saving.tur === "Kripto") return saving.coinSembol || saving.coinAdi || "Kripto";
  if (saving.tur === "Döviz") return saving.dovizCinsi || "Döviz";
  if (saving.tur === "Fon") return saving.fonKodu || saving.fonAdi || "Fon";
  return saving.tur || "Birikim";
};

const getRealEstateShareFactor = (saving = {}) => {
  const share = Number(saving.sahiplikPayi);
  if (!Number.isFinite(share) || share <= 0) return 1;
  return Math.min(100, share) / 100;
};

const buildSavingPayload = (draft) => {
  const asset = getSavingAssetInfo(draft);
  const displayName = getSavingDisplayName(draft);
  const isRealEstate = draft.tur === "Gayrimenkul" || draft.tur === "Real Estate";
  return {
    ...draft,
    ...asset,
    ad: displayName,
    tarih: draft.tarih || new Date().toISOString().slice(0, 10),
    miktar: isRealEstate ? getRealEstateShareFactor(draft) : parseMoneyInput(draft.miktar),
    alisFiyati: parseMoneyInput(draft.alisFiyati),
    guncelFiyat: parseMoneyInput(draft.guncelFiyat || draft.alisFiyati),
    faizOrani: draft.faizOrani === "" ? "" : parseMoneyInput(draft.faizOrani),
    brutMetrekare: draft.brutMetrekare === "" ? "" : parseMoneyInput(draft.brutMetrekare),
    sahiplikPayi: draft.sahiplikPayi === "" ? "" : parseMoneyInput(draft.sahiplikPayi),
    aylikKiraGeliri: draft.aylikKiraGeliri === "" ? "" : parseMoneyInput(draft.aylikKiraGeliri),
    kalanKrediBorcu: draft.kalanKrediBorcu === "" ? "" : parseMoneyInput(draft.kalanKrediBorcu),
    aylikKrediOdemesi: draft.aylikKrediOdemesi === "" ? "" : parseMoneyInput(draft.aylikKrediOdemesi),
  };
};

const handleMoneyDraftChange = (setter, key) => (e) => setter((prev) => ({ ...prev, [key]: formatMoneyInput(e.target.value) }));
const moneyValue = (value) => formatMoneyInput(value);

const extractMarketPrice = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null;
  if (typeof value === "string") {
    const parsed = parseMoneyInput(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  if (typeof value === "object") {
    return extractMarketPrice(value.price ?? value.value ?? value.rate ?? value.currentPrice);
  }
  return null;
};

const financeDisplayLabel = (tList, lang, listKey, value) => {
  if (!value) return value;
  const translated = listLabel(tList, listKey, value);
  if (translated && translated !== value) return translated;
  return fl(lang, value);
};

const DateInputField = ({ label, value, onChange, className = "", errorKey = "" }) => (
  <label className={`dateInputField ${value ? "hasValue" : ""} ${className}`.trim()} data-finance-error={errorKey || undefined}>
    <span>{label}</span>
    <input
      className="darkInput"
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
    />
  </label>
);


const cleanFinancePatch = (draft = {}) => {
  const { id, createdAt, updatedAt, createdByUid, createdByName, reminderId, reminderIds, ...patch } = draft || {};
  return patch;
};

const financeNotificationTargetDefaults = {
  notificationTargetType: "admins",
  notificationTargetUids: [],
};

const withFinanceNotificationTargetDefaults = (draft = {}) => ({
  ...draft,
  notificationTargetType: draft.notificationTargetType || draft.financeNotificationTargetType || financeNotificationTargetDefaults.notificationTargetType,
  notificationTargetUids: Array.isArray(draft.notificationTargetUids)
    ? draft.notificationTargetUids
    : Array.isArray(draft.financeNotificationTargetUids)
      ? draft.financeNotificationTargetUids
      : financeNotificationTargetDefaults.notificationTargetUids,
});

// AYLAR: component içinde tList("common.months") kullanılır
const AYLAR_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

function tarihOkunakli(str, aylar = AYLAR_TR) {
  if (!str) return "";
  const [y, m, d] = str.split("-");
  return `${parseInt(d)} ${aylar[parseInt(m) - 1]} ${y}`;
}

function ayBaslangici(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function oncekiAyBaslangici(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

function monthDateWithClampedDay(year, month, day) {
  const lastDay = new Date(year, month + 1, 0).getDate();
  const safeDay = Math.min(Math.max(1, Number(day) || 1), lastDay);
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(safeDay).padStart(2, "0")}`;
}

function financeBillIdentity(bill = {}) {
  return [bill.kategori, bill.ad || bill.title || ""]
    .map((value) => String(value || "").trim().toLocaleLowerCase("tr-TR"))
    .join("::");
}

function sameMonth(tarih, ref = new Date()) {
  if (!tarih) return false;
  const d = new Date(tarih);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
}

function sortNewest(a, b) {
  return new Date(b.tarih || 0) - new Date(a.tarih || 0);
}

function sortFinanceCreatedNewest(a, b) {
  const bTime = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
  const aTime = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
  return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
}

function categorySummary(items, typeFilter = null) {
  const map = {};
  items.forEach((item) => {
    if (typeFilter && item.tur !== typeFilter) return;
    const kategori = item.kategori || "Diğer";
    map[kategori] = (map[kategori] || 0) + Number(item.tutar || 0);
  });
  return Object.entries(map)
    .map(([kategori, tutar]) => ({ kategori, tutar }))
    .sort((a, b) => b.tutar - a.tutar);
}

function MiniBarList({ items, total, paraFormatla, emptyText = "Veri yok", lang = "tr", labelFormatter = null }) {
  if (!items.length) return <div className="emptyState compact">{emptyText}</div>;
  return (
    <div className="miniBarList">
      {items.slice(0, 5).map((item) => {
        const pct = total > 0 ? Math.min(100, (item.tutar / total) * 100) : 0;
        return (
          <div className="miniBarItem" key={item.kategori || item.isim || item.ad}>
            <div className="miniBarTop">
              <span>{labelFormatter ? labelFormatter(item.kategori || item.isim || item.ad) : fl(lang, item.kategori || item.isim || item.ad)}</span>
              <b>{paraFormatla(item.tutar)}</b>
            </div>
            <div className="miniBarTrack"><span style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function FinancePage({
  finansFormAcik, setFinansFormAcik,
  finansBaslik, setFinansBaslik, finansTutar, setFinansTutar,
  finansTur, setFinansTur, finansKategori, setFinansKategori,
  finansTarih, setFinansTarih, finansEkle,
  finansTekrar = { enabled: false, frequency: "monthly", endDate: "" }, setFinansTekrar,
  finansPeriyot, setFinansPeriyot,
  finansSeciliAy, setFinansSeciliAy,
  finansSeciliYil, setFinansSeciliYil,
  toplamGelir, toplamGider, netBakiye, gelirYuzde,
  finansFiltreli, finansSil,
  gelirKategorileri, giderKategorileri,
  ekKategori, setEkKategori, finansKategoriEkle,
  paraFormatla, permissions, user, familyId, uyeler = [],
  paraBirimi = "TRY",
  tumFinanslar = [], buAyHarcama = [], toplamOrtakHarcama = 0,
  kisiBazli = [], borcOzeti = [], navigateTo,
  onReminderKur,
  isPremium = false, premiumConfig = {}, onPremiumLock, onPremiumLimitHit, onInternalForwardTransition,
  isBgInstance = false,
}) {
  const { t, tList, lang } = useLanguage();
  const AYLAR = tList("common.months");
  const normalizedBaseCurrency = normalizeCurrencyCode(paraBirimi, lang === "en" ? "EUR" : "TRY");
  const debtRegion = getDebtRegion(lang, normalizedBaseCurrency);

  const canAdd       = permissions?.actions?.addFinance;
  const canDeleteOwn = permissions?.actions?.deleteOwnFinance;
  const canDeleteAll = permissions?.actions?.deleteAllFinance;
  const canAddCat    = permissions?.actions?.addCategory;

  const lockedFeatures = premiumConfig?.lockedFeatures || {};
  const handleSekmeClick = (id) => {
    if (!isPremium) {
      const lockMap = {
        rapor: "finansRapor",
        birikim: "finansBirikim",
        taksitler: "finansTaksit",
        abonelikler: "finansAbonelik",
        faturalar: "finansFatura",
      };
      const lockKey = lockMap[id];
      if (lockKey && lockedFeatures[lockKey]) {
        const titleMap = {
          rapor: t("finance.tab.reports"),
          birikim: t("finance.tab.savings"),
          taksitler: t("finance.tab.installments"),
          abonelikler: t("finance.tab.subscriptions"),
          faturalar: t("finance.tab.bills"),
        };
        onPremiumLock && onPremiumLock(
          titleMap[id],
          t("finance.premiumDesc"),
          [t("finance.premiumBenefit1"), t("finance.premiumBenefit2"), t("finance.premiumBenefit3"), t("finance.premiumBenefit4")]
        );
        return;
      }
    }
    const commit = () => {
      setAktifSekme(id);
      requestAnimationFrame(() => {
        const el = document.querySelector(".financeProPage");
        if (el?.scrollTo) el.scrollTo({ top: 0, behavior: "auto" });
        window.scrollTo?.({ top: 0, behavior: "auto" });
      });
    };

    if (id !== "genel" && typeof onInternalForwardTransition === "function") {
      onInternalForwardTransition(commit);
      return;
    }

    commit();
  };

  const [aktifSekme, setAktifSekme] = useState("genel");
  const [savingFormAcik, setSavingFormAcik] = useState(false);
  const [installmentFormAcik, setInstallmentFormAcik] = useState(false);
  const [savingUpdateDrafts, setSavingUpdateDrafts] = useState({});
  const [savingUpdatePanelId, setSavingUpdatePanelId] = useState(null);
  const [savingEditId, setSavingEditId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(() => emptySavingDraft(paraBirimi));
  const [installmentDraft, setInstallmentDraft] = useState(() => emptyInstallmentDraft(lang === "en" ? "Credit Card" : "Kredi Kartı", getDebtRegion(lang, paraBirimi), paraBirimi));

  const [savings, setSavings] = useState(() => getCachedFinanceToolItems({ familyId, type: "saving" }));
  const [marketPrices, setMarketPrices] = useState({});
  const [marketUpdatedAt, setMarketUpdatedAt] = useState(null);
  const [installments, setInstallments] = useState(() => getCachedFinanceToolItems({ familyId, type: "installment" }));
  const [savingsReady, setSavingsReady] = useState(() => getCachedFinanceToolItems({ familyId, type: "saving" }).length > 0);
  const [installmentsReady, setInstallmentsReady] = useState(() => getCachedFinanceToolItems({ familyId, type: "installment" }).length > 0);

  const [subscriptions, setSubscriptions] = useState(() => getCachedFinanceToolItems({ familyId, type: "subscription" }));
  const [bills, setBills] = useState(() => getCachedFinanceToolItems({ familyId, type: "bill" }));
  const baseAmountFor = useCallback((item) => financeAmountInBase(item, normalizedBaseCurrency, marketPrices), [normalizedBaseCurrency, marketPrices]);

  // Abonelik form state
  const [subFormAcik, setSubFormAcik] = useState(false);
  const [subDraft, setSubDraft] = useState(() => withFinanceNotificationTargetDefaults({
    ad: "", kategori: "Eğlence", tutar: "", paraBirimi: paraBirimi,
    periyot: "Aylık", sonrakiOdeme: "", odemeSahibi: "", aktif: "Evet", not: "",
  }));

  // Fatura form state
  const [billFormAcik, setBillFormAcik] = useState(false);
  const [billDraft, setBillDraft] = useState(() => withFinanceNotificationTargetDefaults({
    ad: "", kategori: "Elektrik", tutar: "", paraBirimi: paraBirimi,
    sonOdemeTarihi: "", odemeSahibi: "", durum: "Ödenmedi", periyot: "Aylık", not: "",
  }));

  const [installmentEditId, setInstallmentEditId] = useState(null);
  const [subscriptionEditId, setSubscriptionEditId] = useState(null);
  const [billEditId, setBillEditId] = useState(null);
  const [billFilterMode, setBillFilterMode] = useState("currentMonth");
  const [billFilterMonth, setBillFilterMonth] = useState(new Date().getMonth());
  const [billFilterYear, setBillFilterYear] = useState(new Date().getFullYear());
  const [quickBillDrafts, setQuickBillDrafts] = useState({});
  const [quickBillAddingId, setQuickBillAddingId] = useState(null);
  const [financeActionMenuId, setFinanceActionMenuId] = useState(null);
  const [aktifTrendIndex, setAktifTrendIndex] = useState(null);
  const [financeAmountsHidden, setFinanceAmountsHidden] = useState(() => {
    try { return localStorage.getItem(FINANCE_PRIVACY_STORAGE_KEY) === "true"; } catch (_) { return false; }
  });
  const [breakdownView, setBreakdownView] = useState(() => {
    try { return localStorage.getItem(FINANCE_BREAKDOWN_STORAGE_KEY) === "pie" ? "pie" : "bars"; } catch (_) { return "bars"; }
  });
  const breakdownTouchStartRef = useRef(null);
  const financeUserUid = String(user?.firebase_uid || user?.uid || user?.id || "").trim();
  const breakdownTypeStorageKey = financeBreakdownTypeStorageKey(familyId, financeUserUid);
  const [breakdownType, setBreakdownType] = useState(() => readFinanceBreakdownType(breakdownTypeStorageKey));
  const [installmentProgressPanelId, setInstallmentProgressPanelId] = useState(null);
  const [debtPaymentPanelId, setDebtPaymentPanelId] = useState(null);
  const [debtPaymentDrafts, setDebtPaymentDrafts] = useState({});
  const [debtPaymentSubmittingId, setDebtPaymentSubmittingId] = useState(null);
  const [financeFormErrors, setFinanceFormErrors] = useState({
    main: [], saving: [], installment: [], subscription: [], bill: [],
  });
  const [financeSubmitting, setFinanceSubmitting] = useState({
    main: false, saving: false, installment: false, subscription: false, bill: false,
  });
  const financeSubmitLocksRef = useRef(new Set());
  const financeDeleteLocksRef = useRef(new Set());

  const financeMoneyFormat = useCallback((value) => (
    financeAmountsHidden ? "••••••" : paraFormatla(value)
  ), [financeAmountsHidden, paraFormatla]);
  const toggleFinanceAmounts = useCallback(() => {
    setFinanceAmountsHidden((current) => {
      const next = !current;
      try { localStorage.setItem(FINANCE_PRIVACY_STORAGE_KEY, String(next)); } catch (_) { /* storage may be unavailable */ }
      return next;
    });
  }, []);
  const selectBreakdownView = useCallback((next) => {
    const normalized = next === "pie" ? "pie" : "bars";
    setBreakdownView(normalized);
    try { localStorage.setItem(FINANCE_BREAKDOWN_STORAGE_KEY, normalized); } catch (_) { /* storage may be unavailable */ }
  }, []);
  const handleBreakdownTouchStart = useCallback((event) => {
    breakdownTouchStartRef.current = event.touches?.[0]?.clientX ?? null;
  }, []);
  const handleBreakdownTouchEnd = useCallback((event) => {
    const startX = breakdownTouchStartRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    breakdownTouchStartRef.current = null;
    if (startX == null || endX == null || Math.abs(endX - startX) < 42) return;
    selectBreakdownView(endX < startX ? "pie" : "bars");
  }, [selectBreakdownView]);
  useEffect(() => {
    setBreakdownType(readFinanceBreakdownType(breakdownTypeStorageKey));
  }, [breakdownTypeStorageKey]);
  const selectBreakdownType = useCallback((next) => {
    const normalized = next === "income" ? "income" : "expense";
    setBreakdownType(normalized);
    try { localStorage.setItem(breakdownTypeStorageKey, normalized); } catch (_) { /* storage may be unavailable */ }
  }, [breakdownTypeStorageKey]);

  // ── Hatırlatma state'leri (her bölüm için ayrı) ──
  const [savingReminder, setSavingReminder]   = useState({ aktif: false, tarih: "", saat: "09:00", tekrar: "none" });
  const [savingReminderError, setSavingReminderError] = useState("");
  const [installmentReminder, setInstallmentReminder] = useState({ aktif: false, tarih: "", saat: "09:00", tekrar: "none" });
  const [installmentReminderError, setInstallmentReminderError] = useState("");
  const [gelirGenisletildi, setGelirGenisletildi] = useState(false);
  const [giderGenisletildi, setGiderGenisletildi] = useState(false);

  const hasFinanceFieldError = (section, field) => financeFormErrors[section]?.includes(field);
  const financeFieldClass = (section, field, base = "") => `${base} ${hasFinanceFieldError(section, field) ? "financeFieldInvalid" : ""}`.trim();
  const clearFinanceFieldError = (section, field) => {
    setFinanceFormErrors((current) => ({
      ...current,
      [section]: (current[section] || []).filter((item) => item !== field),
    }));
  };
  const updateFinanceDraftField = (setter, section, field, value) => {
    setter((current) => ({ ...current, [field]: value }));
    clearFinanceFieldError(section, field);
  };
  const markFinanceFormErrors = (section, fields) => {
    const uniqueFields = Array.from(new Set(fields.filter(Boolean)));
    setFinanceFormErrors((current) => ({ ...current, [section]: uniqueFields }));
    if (uniqueFields.length > 0) {
      window.requestAnimationFrame(() => {
        const target = document.querySelector(`[data-finance-error="${section}-${uniqueFields[0]}"]`);
        const focusTarget = target?.matches?.("input, select, textarea, button")
          ? target
          : target?.querySelector?.("input, select, textarea, button");
        focusTarget?.focus?.({ preventScroll: true });
        target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      });
    }
    return uniqueFields.length === 0;
  };
  const renderFinanceValidationMessage = (section, labels, options = {}) => {
    if (!financeFormErrors[section]?.length) return null;
    const messages = (options.messages || []).filter(Boolean);
    return (
      <div className="financeValidationMessage" role="alert" aria-live="assertive">
        <span aria-hidden="true">!</span>
        <div>
          <b>{options.title || t("finance.validation.title")}</b>
          {messages.length > 0
            ? messages.map((message, index) => <small key={`${section}-validation-${index}`}>{message}</small>)
            : <small>{t("finance.validation.fillFields")} {labels.join(", ")}.</small>}
        </div>
      </div>
    );
  };

  const installmentDebtKind = debtKindForCategory(installmentDraft.kategori);
  const isDebtInstallmentDraft = installmentDebtKind !== "standard";
  const goldDebtOptions = GOLD_DEBT_OPTIONS[debtRegion] || GOLD_DEBT_OPTIONS.international;
  const installmentCategoryLabel = (category) => category === GOLD_DEBT_CATEGORY
    ? t("finance.debt.gold.category")
    : category === FX_DEBT_CATEGORY
      ? t("finance.debt.fx.category")
      : fl(lang, category);
  const goldTypeLabel = (type) => t(`finance.debt.gold.type.${type}`);

  const getStandardInstallmentCountValidation = () => {
    const previousItem = installmentEditId
      ? installments.find((candidate) => candidate.id === installmentEditId)
      : null;
    return validateStandardInstallmentCounts({
      total: installmentDraft.taksitSayisi,
      paid: !installmentEditId && installmentDraft.odenenTaksit === "" ? 0 : installmentDraft.odenenTaksit,
      previousPaid: previousItem?.odenenTaksit,
    });
  };

  const renderInstallmentValidationMessage = () => {
    const activeFields = financeFormErrors.installment || [];
    if (!activeFields.length) return null;
    const countFields = new Set(["taksitSayisi", "odenenTaksit"]);
    const countValidation = installmentDebtKind === "standard"
      ? getStandardInstallmentCountValidation()
      : { issues: [] };
    const countMessages = countValidation.issues
      .filter((issue) => activeFields.includes(issue.startsWith("total") ? "taksitSayisi" : "odenenTaksit"))
      .map((issue) => t(`finance.validation.installmentCounts.${issue}`, {
        max: MAX_STANDARD_INSTALLMENT_TOTAL,
        limit: MAX_INSTALLMENT_LEDGER_CHANGES,
      }));
    const otherLabels = activeFields
      .filter((field) => !countFields.has(field))
      .map((field) => {
        if (field.startsWith("goldItem")) return t("finance.debt.gold.amount");
        if (field.startsWith("goldKarat")) return t("finance.debt.gold.karat");
        if (field.startsWith("goldPurity")) return t("finance.debt.gold.purity");
        return ({ ad: t("finance.validation.installmentName"), toplam: t("finance.installments.totalPlaceholder"), monthlyPaymentAmount: t("finance.debt.monthlyPayment"), odemeGunu: t("finance.installments.dueDayPlaceholder"), foreignPrincipal: t("finance.debt.fx.principal"), debtCurrency: t("finance.debt.fx.currency") }[field]);
      })
      .filter(Boolean);
    const messages = [...countMessages];
    if (otherLabels.length) messages.push(`${t("finance.validation.fillFields")} ${otherLabels.join(", ")}.`);
    return renderFinanceValidationMessage("installment", otherLabels, {
      title: countMessages.length ? t("finance.validation.installmentCounts.title") : undefined,
      messages,
    });
  };

  const handleInstallmentCategoryChange = (category) => {
    const nextKind = debtKindForCategory(category);
    if (nextKind === "standard") {
      setInstallmentReminder({ aktif: false, tarih: "", saat: "09:00", tekrar: "none" });
      setInstallmentReminderError("");
    }
    setInstallmentDraft((current) => {
      if (nextKind === "standard") return { ...emptyInstallmentDraft(category, debtRegion, normalizedBaseCurrency), ad: current.ad, not: current.not, notificationTargetType: current.notificationTargetType, notificationTargetUids: current.notificationTargetUids };
      return {
        ...current,
        kategori: category,
        debtKind: nextKind,
        valuationCurrency: normalizedBaseCurrency,
        paraBirimi: normalizedBaseCurrency,
        monthlyPaymentCurrency: current.monthlyPaymentCurrency || normalizedBaseCurrency,
        goldItems: current.goldItems?.length ? current.goldItems : [newGoldDebtItem(debtRegion)],
      };
    });
    setFinanceFormErrors((current) => ({ ...current, installment: [] }));
  };

  const updateGoldDebtItem = (index, patch) => {
    setInstallmentDraft((current) => {
      const items = [...(current.goldItems || [])];
      const previous = items[index] || newGoldDebtItem(debtRegion);
      let next = { ...previous, ...patch };
      if (patch.type) {
        const option = goldDebtOptions.find((candidate) => candidate.value === patch.type) || goldDebtOptions[0];
        next = { ...next, type: option.value, assetKey: option.assetKey, unit: option.unit, karat: option.requiresKarat ? next.karat : "", purity: option.requiresPurity ? next.purity : "" };
      }
      items[index] = next;
      return { ...current, goldItems: items };
    });
    clearFinanceFieldError("installment", `goldItem${index}`);
    clearFinanceFieldError("installment", `goldKarat${index}`);
  };
  const addGoldDebtItem = () => setInstallmentDraft((current) => ({ ...current, goldItems: [...(current.goldItems || []), newGoldDebtItem(debtRegion)] }));
  const removeGoldDebtItem = (index) => setInstallmentDraft((current) => ({ ...current, goldItems: current.goldItems.length > 1 ? current.goldItems.filter((_, itemIndex) => itemIndex !== index) : current.goldItems }));

  const buildInstallmentPayload = () => {
    if (installmentDebtKind === "standard") {
      const toplam = parseMoneyInput(installmentDraft.toplam);
      const taksitSayisi = Number(installmentDraft.taksitSayisi);
      return {
        ...cleanFinancePatch(installmentDraft), debtKind: "standard", customReminder: { enabled: false }, goldItems: null, remainingGoldItems: null, foreignPrincipal: null, remainingForeignAmount: null, toplam, taksitSayisi,
        odenenTaksit: Number(installmentDraft.odenenTaksit),
        aylikOdeme: installmentDraft.aylikOdeme ? parseMoneyInput(installmentDraft.aylikOdeme) : (toplam / Math.max(1, taksitSayisi)),
        baslangic: installmentDraft.baslangic || new Date().toISOString().slice(0, 10),
        odemeGunu: installmentDraft.odemeGunu ? Number(installmentDraft.odemeGunu) : "",
      };
    }
    const monthlyPaymentAmount = parseMoneyInput(installmentDraft.monthlyPaymentAmount);
    const payload = {
      ...cleanFinancePatch(installmentDraft),
      debtKind: installmentDebtKind,
      kategori: installmentDebtKind === "gold" ? GOLD_DEBT_CATEGORY : FX_DEBT_CATEGORY,
      valuationCurrency: normalizedBaseCurrency,
      paraBirimi: normalizedBaseCurrency,
      monthlyPaymentAmount,
      monthlyPaymentCurrency: normalizeCurrencyCode(installmentDraft.monthlyPaymentCurrency || normalizedBaseCurrency),
      aylikOdeme: monthlyPaymentAmount,
      odemeGunu: Number(installmentDraft.odemeGunu),
      baslangic: installmentDraft.baslangic || new Date().toISOString().slice(0, 10),
      regionProfile: debtRegion,
      customReminder: {
        enabled: installmentReminder.aktif,
        date: installmentReminder.tarih,
        time: installmentReminder.saat,
        repeat: installmentReminder.tekrar,
      },
    };
    if (installmentDebtKind === "gold") {
      payload.goldItems = (installmentDraft.goldItems || []).map((item) => ({
        ...item,
        quantity: parseMoneyInput(item.quantity),
        karat: item.karat === "" ? null : Number(item.karat),
        purity: item.purity === "" ? null : Number(item.purity),
      }));
      if (installmentEditId) payload.remainingGoldItems = payload.goldItems;
    } else {
      payload.debtCurrency = normalizeCurrencyCode(installmentDraft.debtCurrency || "USD");
      payload.foreignPrincipal = parseMoneyInput(installmentDraft.foreignPrincipal);
      if (installmentEditId) payload.remainingForeignAmount = payload.foreignPrincipal;
    }
    return payload;
  };

  const validateInstallmentDraft = () => {
    const errors = [!installmentDraft.ad?.trim() && "ad"];
    if (installmentDebtKind === "standard") {
      const countValidation = getStandardInstallmentCountValidation();
      errors.push(!installmentDraft.toplam && "toplam", ...countValidation.errors);
    } else {
      const paymentDay = Number(installmentDraft.odemeGunu);
      errors.push(!(parseMoneyInput(installmentDraft.monthlyPaymentAmount) > 0) && "monthlyPaymentAmount", (!(paymentDay >= 1 && paymentDay <= 31)) && "odemeGunu");
      if (installmentDebtKind === "gold") {
        (installmentDraft.goldItems || []).forEach((item, index) => {
          if (!(parseMoneyInput(item.quantity) > 0)) errors.push(`goldItem${index}`);
          const option = goldDebtOptions.find((candidate) => candidate.value === item.type);
          if (option?.requiresKarat && !item.karat) errors.push(`goldKarat${index}`);
          if (option?.requiresPurity && !item.purity) errors.push(`goldPurity${index}`);
        });
      } else {
        errors.push(!(parseMoneyInput(installmentDraft.foreignPrincipal) > 0) && "foreignPrincipal", !installmentDraft.debtCurrency && "debtCurrency");
      }
    }
    return markFinanceFormErrors("installment", errors);
  };

  const customDebtReminderPayload = () => {
    if (!isDebtInstallmentDraft || !installmentReminder.aktif || !onReminderKur) return null;
    return reminderPayloadKontrolEt({
      mod: "datetime", tarih: installmentReminder.tarih, saat: installmentReminder.saat, tekrar: installmentReminder.tekrar,
    }, t, lang, setInstallmentReminderError);
  };

  const syncDebtCustomReminder = async (item, reminderPayload) => {
    if (!item?.id || !isDebtInstallmentDraft) return;
    if (!installmentReminder.aktif || !reminderPayload || !onReminderKur) {
      if (installmentEditId && (item.reminderId || item.reminderIds?.length)) {
        await deleteHomeInstallmentReminders({ familyId, installmentId: item.id, installment: item });
        await updateHomeInstallment({ familyId, installmentId: item.id, patch: { reminderId: null, reminderIds: [], customReminder: { enabled: false } } });
      }
      return;
    }
    const reminderId = await onReminderKur({
      baslik: t("finance.debt.customReminderTitle"),
      aciklama: t("finance.debt.customReminderBody", { name: installmentDraft.ad.trim() }),
      baslikKey: "finance.debt.customReminderTitle",
      aciklamaKey: "finance.debt.customReminderBody",
      aciklamaVars: { name: installmentDraft.ad.trim() },
      kaynak: "finance",
      kaynakId: item.id,
      kaynakTuru: "financeDebt",
      bildirimType: "finance",
      url: "/?pushPage=finans",
      idempotencyKey: `finance_debt_custom_${item.id}`,
      extraData: { financeReminderKind: "installments", debtKind: installmentDebtKind },
      ...reminderPayload,
    });
    if (reminderId) {
      await updateHomeInstallment({
        familyId,
        installmentId: item.id,
        patch: {
          reminderId,
          reminderIds: [reminderId],
          customReminder: { enabled: true, date: installmentReminder.tarih, time: installmentReminder.saat, repeat: installmentReminder.tekrar },
        },
      });
    }
  };

  const runFinanceSubmission = async (section, action) => {
    if (financeSubmitLocksRef.current.has(section)) return false;
    financeSubmitLocksRef.current.add(section);
    setFinanceSubmitting((current) => ({ ...current, [section]: true }));
    try {
      const result = await action();
      return result === false ? false : true;
    } finally {
      financeSubmitLocksRef.current.delete(section);
      setFinanceSubmitting((current) => ({ ...current, [section]: false }));
    }
  };

  const handleMainFinanceAdd = async () => {
    if (!markFinanceFormErrors("main", [
      !finansBaslik?.trim() && "baslik",
      !finansTutar && "tutar",
    ])) return;
    return runFinanceSubmission("main", finansEkle);
  };

  const handleMainFinanceDelete = async (item) => {
    if (!await appConfirm(t("finance.recurring.deleteConfirm"), { danger: true })) return;
    let scope = "current";
    if (item?.recurrenceRuleId) {
      const stopFuture = await appConfirm(t("finance.recurring.deleteFutureQuestion"), {
        confirmText: t("finance.recurring.thisAndFuture"),
        cancelText: t("finance.recurring.onlyThis"),
        danger: true,
      });
      scope = stopFuture ? "future" : "current";
    }
    await finansSil(item, scope);
  };

  const deleteFinanceItemOptimistically = async ({ section, id, items, setItems, remove, errorMessage }) => {
    const lockKey = `${section}:${id}`;
    if (!id || financeDeleteLocksRef.current.has(lockKey)) return;
    financeDeleteLocksRef.current.add(lockKey);

    try {
      if (!await appConfirm(t("finance.confirmDelete"))) return;
      const removedIndex = items.findIndex((item) => item.id === id);
      const removedItem = removedIndex >= 0 ? items[removedIndex] : null;
      if (!removedItem) return;

      setItems((current) => current.filter((item) => item.id !== id));
      try {
        await remove(removedItem);
      } catch (error) {
        setItems((current) => {
          if (current.some((item) => item.id === id)) return current;
          const restored = [...current];
          restored.splice(Math.min(removedIndex, restored.length), 0, removedItem);
          return restored;
        });
        console.error(error);
        appAlert(errorMessage);
      }
    } finally {
      financeDeleteLocksRef.current.delete(lockKey);
    }
  };


  useEffect(() => {
    if (isBgInstance) return undefined;
    if (!familyId) {
      setSavings([]);
      setInstallments([]);
      setSubscriptions([]);
      setBills([]);
      setSavingsReady(false);
      setInstallmentsReady(false);
      return;
    }

    const cachedSavings = getCachedFinanceToolItems({ familyId, type: "saving" });
    const cachedInstallments = getCachedFinanceToolItems({ familyId, type: "installment" });
    const cachedSubscriptions = getCachedFinanceToolItems({ familyId, type: "subscription" });
    const cachedBills = getCachedFinanceToolItems({ familyId, type: "bill" });

    if (cachedSavings.length) {
      setSavings(cachedSavings);
      setSavingsReady(true);
    }
    if (cachedInstallments.length) {
      setInstallments(cachedInstallments);
      setInstallmentsReady(true);
    }
    if (cachedSubscriptions.length) setSubscriptions(cachedSubscriptions);
    if (cachedBills.length) setBills(cachedBills);
  }, [familyId]);

  const birikimTurleri = SAVING_TYPES;
  const goldTypeOptions = getGoldTypeOptions(lang);
  const taksitKategorileri = useMemo(() => (
    lang === "en"
      ? ["Credit Card", "Personal Loan", "Auto Loan", "Mortgage", "Buy Now Pay Later", "Electronics", "Furniture", "Education", "Medical", "Travel", "Other", GOLD_DEBT_CATEGORY, FX_DEBT_CATEGORY]
      : ["Kredi Kartı", "İhtiyaç Kredisi", "Araç Kredisi", "Ev Kredisi", "Telefon", "Beyaz Eşya", "Mobilya", "Eğitim", "Diğer", GOLD_DEBT_CATEGORY, FX_DEBT_CATEGORY]
  ), [lang]);

  const abonelikKategorileri = useMemo(() => (
    lang === "en"
      ? ["Streaming", "Music", "Gaming", "Software", "Cloud Storage", "News & Magazine", "Sports & Fitness", "Education", "Shopping", "Security", "Productivity", "Other"]
      : ["Eğlence", "Müzik", "Oyun", "Yazılım", "Bulut Depolama", "Haber & Dergi", "Spor & Sağlık", "Eğitim", "Alışveriş", "Diğer"]
  ), [lang]);
  const abonelikPlatformlari = useMemo(() => (
    lang === "en"
      ? [
        "Netflix", "Spotify", "Apple TV+", "Disney+", "YouTube Premium",
        "Amazon Prime", "Hulu", "Max", "Paramount+", "Peacock",
        "Apple Music", "Apple One", "iCloud+", "Google One", "Microsoft 365", "Adobe Creative Cloud",
        "Notion", "ChatGPT", "LinkedIn", "Gym/Fitness", "Other",
      ]
      : [
        "Netflix", "Spotify", "Apple TV+","IP Tv", "Disney+", "YouTube Premium",
        "Amazon Prime", "Exxen", "BluTV", "Apple Music", "Apple One",
        "iCloud+", "Google One", "Microsoft 365", "Adobe CC",
        "Notion", "ChatGPT", "LinkedIn", "Gym/Spor", "Diğer",
      ]
  ), [lang]);
  const paraBirimleri = useMemo(() => (lang === "en" ? ["USD", "GBP", "EUR", "TRY"] : ["TRY", "USD", "EUR", "GBP"]), [lang]);
  const defaultFormCurrency = paraBirimleri.includes(paraBirimi) ? paraBirimi : (lang === "en" ? "USD" : "TRY");
  const periyotlar = useMemo(() => (lang === "en" ? ["Monthly", "Quarterly", "Every 6 Months", "Yearly"] : ["Aylık", "3 Aylık", "6 Aylık", "Yıllık"]), [lang]);

  const financeMemberOptions = useMemo(() => {
    const map = new Map();
    (uyeler || []).forEach((member) => {
      const uid = member?.uid || member?.id || member?.userUid || member?.uyeUid;
      if (!uid || map.has(uid)) return;
      const name = [member?.ad, member?.soyad].filter(Boolean).join(" ").trim()
        || member?.displayName
        || member?.isim
        || member?.name
        || member?.email
        || t("finance.notifyTarget.member");
      map.set(uid, { uid, name });
    });
    if (user?.uid && !map.has(user.uid)) {
      map.set(user.uid, {
        uid: user.uid,
        name: user.displayName || user.email || t("finance.notifyTarget.member"),
      });
    }
    return Array.from(map.values());
  }, [uyeler, user, t]);

  const financeTargetOptions = useMemo(() => ([
    { value: "admins", label: t("finance.notifyTarget.admins") },
    { value: "self", label: t("finance.notifyTarget.self") },
    { value: "selected", label: t("finance.notifyTarget.selected") },
    { value: "all", label: t("finance.notifyTarget.all") },
  ]), [t]);

  const setFinanceNotificationTarget = (setter, type, uid = null) => {
    setter((prev) => {
      const current = withFinanceNotificationTargetDefaults(prev);
      if (uid) {
        const selected = new Set(current.notificationTargetUids || []);
        if (selected.has(uid)) selected.delete(uid);
        else selected.add(uid);
        return { ...current, notificationTargetType: "selected", notificationTargetUids: Array.from(selected) };
      }
      return {
        ...current,
        notificationTargetType: type,
        notificationTargetUids: type === "selected" ? (current.notificationTargetUids || []) : [],
      };
    });
  };

  const renderFinanceNotificationTarget = (draft, setter) => {
    const normalizedDraft = withFinanceNotificationTargetDefaults(draft);
    const selectedUids = Array.isArray(normalizedDraft.notificationTargetUids)
      ? normalizedDraft.notificationTargetUids
      : [];

    return (
      <div className="inputLabelGroup financeNotifyTargetBox noSwipeBack" data-no-swipe-back="true">
        <label className="darkLabel">{t("finance.notifyTarget.title")}</label>
        <select
          className="darkInput"
          value={normalizedDraft.notificationTargetType}
          onChange={(e) => setFinanceNotificationTarget(setter, e.target.value)}
        >
          {financeTargetOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        {normalizedDraft.notificationTargetType === "selected" && (
          <div className="financeNotifyMemberList">
            {financeMemberOptions.length === 0 ? (
              <span className="financeNotifyHint">{t("finance.notifyTarget.noMembers")}</span>
            ) : financeMemberOptions.map((member) => (
              <label key={member.uid} className="financeNotifyMemberOption">
                <input
                  type="checkbox"
                  checked={selectedUids.includes(member.uid)}
                  onChange={() => setFinanceNotificationTarget(setter, "selected", member.uid)}
                />
                <span>{member.name}</span>
              </label>
            ))}
          </div>
        )}

        <small className="financeNotifyHint">{t("finance.notifyTarget.hint")}</small>
      </div>
    );
  };

  const FinanceRowActionMenu = ({ menuId, onEdit, onDelete, children, menuClassName = "" }) => {
    const isOpen = financeActionMenuId === menuId;

    return (
      <div
        style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        className="noSwipeBack"
        data-no-swipe-back="true"
      >
        <button
          type="button"
          className="financeMoreActionBtn"
          aria-label={lang === "en" ? "Record actions" : "Kayıt işlemleri"}
          title={lang === "en" ? "Record actions" : "Kayıt işlemleri"}
          onClick={(e) => {
            e.stopPropagation();
            setFinanceActionMenuId(isOpen ? null : menuId);
          }}
        >
          ⋯
        </button>

        {isOpen && (
          <div
            className={`darkCard financeActionDropdown ${menuClassName}`.trim()}
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              zIndex: 30,
              minWidth: 130,
              padding: 6,
              display: "grid",
              gap: 6,
              boxShadow: "0 18px 38px rgba(0,0,0,0.28)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="smallPremiumBtn compact"
              onClick={() => {
                setFinanceActionMenuId(null);
                onEdit?.();
              }}
              style={{ width: "100%", justifyContent: "flex-start" }}
            >
              ✏️ {t("common.edit")}
            </button>
            {children}
            <button
              type="button"
              className="detailDeleteBtn assetTileDelete"
              onClick={() => {
                setFinanceActionMenuId(null);
                onDelete?.();
              }}
              style={{ width: "100%", justifyContent: "flex-start" }}
            >
              🗑️ {t("common.delete")}
            </button>
          </div>
        )}
      </div>
    );
  };


  const faturaKategorileri = useMemo(() => (
    lang === "en"
      ? ["Electricity", "Gas", "Water", "Internet", "Mobile Phone", "Rent", "Mortgage", "Insurance", "Property Tax", "Council Tax", "Utilities", "Other"]
      : ["Elektrik", "Doğalgaz", "Su", "İnternet", "Telefon (Fatura)", "Kira", "Aidat", "Sigorta", "Vergi", "Diğer"]
  ), [lang]);
  const faturaDurumlar = useMemo(() => (lang === "en" ? ["Unpaid", "Paid", "Pending"] : ["Ödenmedi", "Ödendi", "Beklemede"]), [lang]);

  const isOtherValue = (value) => value === "Diğer" || value === "Other";
  const isPaidValue = (value) => value === "Ödendi" || value === "Paid";
  const isInactiveValue = (value) => value === "Hayır" || value === "No";
  const monthlyCostFromPeriod = (amount, period) => {
    const value = Number(amount || 0);
    if (period === "Yıllık" || period === "Yearly") return value / 12;
    if (period === "6 Aylık" || period === "Every 6 Months") return value / 6;
    if (period === "3 Aylık" || period === "Quarterly") return value / 3;
    return value;
  };
  const yearlyCostFromPeriod = (amount, period) => {
    const value = Number(amount || 0);
    if (period === "Aylık" || period === "Monthly") return value * 12;
    if (period === "3 Aylık" || period === "Quarterly") return value * 4;
    if (period === "6 Aylık" || period === "Every 6 Months") return value * 2;
    return value;
  };

  useEffect(() => {
    setInstallmentDraft((prev) => taksitKategorileri.includes(prev.kategori) ? prev : { ...prev, kategori: taksitKategorileri[0] || "" });
    setSavingDraft((prev) => ({ ...prev, paraBirimi: defaultFormCurrency, dovizCinsi: prev.dovizCinsi || defaultFormCurrency }));
    setSubDraft((prev) => ({
      ...prev,
      kategori: abonelikKategorileri.includes(prev.kategori) ? prev.kategori : (abonelikKategorileri[0] || ""),
      ad: prev.ad && abonelikPlatformlari.includes(prev.ad) ? prev.ad : "",
      paraBirimi: defaultFormCurrency,
      periyot: periyotlar.includes(prev.periyot) ? prev.periyot : (periyotlar[0] || ""),
      aktif: prev.aktif === "Hayır" || prev.aktif === "No" ? (lang === "en" ? "No" : "Hayır") : (lang === "en" ? "Yes" : "Evet"),
    }));
    setBillDraft((prev) => ({
      ...prev,
      kategori: faturaKategorileri.includes(prev.kategori) ? prev.kategori : (faturaKategorileri[0] || ""),
      ad: prev.ad && !isOtherValue(prev.kategori) ? prev.kategori : prev.ad,
      paraBirimi: defaultFormCurrency,
      periyot: periyotlar.includes(prev.periyot) ? prev.periyot : (periyotlar[0] || ""),
      durum: faturaDurumlar.includes(prev.durum) ? prev.durum : (faturaDurumlar[0] || ""),
    }));
  }, [lang, defaultFormCurrency, abonelikKategorileri, abonelikPlatformlari, faturaDurumlar, faturaKategorileri, periyotlar, taksitKategorileri]);

  useEffect(() => {
    // Swipe arka plan kopyası yalnızca görsel snapshot üretir; canlı instance ile
    // aynı finans listener'larını açması aynı endpoint'leri çoğaltıyordu.
    if (isBgInstance) return undefined;
    if (!familyId) {
      setSubscriptions([]);
      return undefined;
    }
    const cachedItems = getCachedFinanceToolItems({ familyId, type: "subscription" });
    if (cachedItems.length > 0) setSubscriptions(cachedItems);
    return listenFinanceSubscriptions({
      familyId,
      onData: setSubscriptions,
      onError: (e) => console.error("Abonelik kayıtları okunamadı:", e),
    });
  }, [familyId, isBgInstance]);

  useEffect(() => {
    if (isBgInstance) return undefined;
    if (!familyId) {
      setBills([]);
      return undefined;
    }
    const cachedItems = getCachedFinanceToolItems({ familyId, type: "bill" });
    if (cachedItems.length > 0) setBills(cachedItems);
    return listenFinanceBills({
      familyId,
      onData: setBills,
      onError: (e) => console.error("Fatura kayıtları okunamadı:", e),
    });
  }, [familyId, isBgInstance]);

  useEffect(() => {
    if (isBgInstance) return undefined;
    const unsubscribe = listenGlobalAssetPrices({
      onData: (data) => {
        setMarketPrices(data?.prices || {});
        setMarketUpdatedAt(data?.updatedAt || null);
      },
      onError: (error) => console.error("Piyasa fiyatları okunamadı:", error),
    });
    return unsubscribe;
  }, [isBgInstance]);

  useEffect(() => {
    if (installmentEditId || debtKindForCategory(installmentDraft.kategori) === "standard") return;
    setInstallmentDraft((current) => {
      const hasEnteredGold = (current.goldItems || []).some((item) => String(item.quantity || "").trim());
      return {
        ...current,
        valuationCurrency: normalizedBaseCurrency,
        monthlyPaymentCurrency: current.monthlyPaymentCurrency || normalizedBaseCurrency,
        goldItems: hasEnteredGold ? current.goldItems : [newGoldDebtItem(debtRegion)],
      };
    });
  }, [debtRegion, normalizedBaseCurrency, installmentDraft.kategori, installmentEditId]);


  useEffect(() => {
    if (savingDraft?.tur !== "Altın") return;
    const options = getGoldTypeOptions(lang);
    if (!options.includes(savingDraft.altinTuru)) {
      setSavingDraft((draft) => ({ ...draft, altinTuru: options[0] }));
    }
  }, [lang, savingDraft?.tur, savingDraft.altinTuru]);

  const getMarketPriceForSaving = useCallback((saving) => {
    if (!isAutoMarketSavingType(saving)) return null;

    const canonicalKey = normalizeMarketAssetKey(saving);
    const legacyKey = saving.assetKey || getSavingAssetInfo(saving).assetKey;
    const possibleKeys = [canonicalKey, legacyKey, saving?.assetKey];

    if (isGoldSavingType(saving)) {
      possibleKeys.push(
        saving?.assetName,
        saving?.ad,
        GOLD_MARKET_KEYS[saving?.altinTuru],
      );
    } else if (isCurrencySavingType(saving)) {
      possibleKeys.push(
        saving?.dovizCinsi,
        saving?.paraBirimi,
        saving?.assetName,
        saving?.ad,
      );
    }

    let foundPrice = null;

    possibleKeys.filter(Boolean).forEach((key) => {
      if (foundPrice) return;

      if (marketPrices?.[key] != null) {
        foundPrice = marketPrices[key];
        return;
      }

      const normalizedKey = String(key)
        .replace(/İ/g, "I")
        .replace(/ı/g, "i")
        .toLowerCase()
        .trim();

      Object.entries(marketPrices || {}).forEach(([marketKey, value]) => {
        const normalizedMarketKey = String(marketKey)
          .replace(/İ/g, "I")
          .replace(/ı/g, "i")
          .toLowerCase()
          .trim();

        if (normalizedMarketKey === normalizedKey) {
          foundPrice = value;
        }
      });
    });

    return extractMarketPrice(foundPrice);
  }, [marketPrices]);

  const getSavingCurrentUnitPrice = useCallback((saving) => {
    const marketPrice = getMarketPriceForSaving(saving);
    return Number(marketPrice || saving.guncelFiyat || saving.alisFiyati || 0);
  }, [getMarketPriceForSaving]);

  const isAutoMarketPricedSaving = useCallback((saving) => {
    const marketPrice = getMarketPriceForSaving(saving);
    return isAutoMarketSavingType(saving) && marketPrice != null && Number.isFinite(Number(marketPrice));
  }, [getMarketPriceForSaving]);

  useEffect(() => {
    if (isBgInstance) return undefined;
    if (!familyId) {
      setSavings([]);
      setSavingsReady(false);
      return undefined;
    }

    const cachedItems = getCachedFinanceToolItems({ familyId, type: "saving" });
    if (cachedItems.length > 0) {
      setSavings(cachedItems);
      setSavingsReady(true);
    } else {
      setSavingsReady(false);
    }

    return listenFinanceSavings({
      familyId,
      onData: (items) => {
        setSavings(items);
        setSavingsReady(true);
      },
      onError: (error) => {
        console.error("Birikim kayıtları okunamadı:", error);
        setSavingsReady(true);
      },
    });
  }, [familyId, isBgInstance]);

  useEffect(() => {
    if (isBgInstance) return undefined;
    if (!familyId) {
      setInstallments([]);
      setInstallmentsReady(false);
      return undefined;
    }

    const cachedItems = getCachedFinanceToolItems({ familyId, type: "installment" });
    if (cachedItems.length > 0) {
      setInstallments(cachedItems);
      setInstallmentsReady(true);
    } else {
      setInstallmentsReady(false);
    }

    return listenHomeInstallments({
      familyId,
      onData: (items) => {
        setInstallments(items);
        setInstallmentsReady(true);
      },
      onError: (error) => {
        console.error("Taksit kayıtları okunamadı:", error);
        setInstallmentsReady(true);
      },
    });
  }, [familyId, isBgInstance]);

  useEffect(() => {
    if (!familyId || !savingsReady || savings.length > 0) return;

    const migrationKey = `familyPlanner.migrated.financeSavings.${familyId}`;
    if (localStorage.getItem(migrationKey)) return;

    try {
      const legacyKey = "familyPlanner.financeSavings";
      const raw = readSensitiveItem(legacyKey);
      const oldItems = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(oldItems) || oldItems.length === 0) {
        localStorage.setItem(migrationKey, "empty");
        return;
      }

      Promise.all(oldItems.map(({ id, ...saving }) => addFinanceSaving({ familyId, saving, user })))
        .then(() => {
          removeSensitiveItem(legacyKey);
          localStorage.setItem(migrationKey, "done");
        })
        .catch((error) => console.error("Birikim local kayıtları backend'e aktarılamadı:", error));
    } catch (error) {
      console.error("Birikim local kayıtları okunamadı:", error);
      localStorage.setItem(migrationKey, "error");
    }
  }, [familyId, savingsReady, savings.length, user]);

  useEffect(() => {
    if (!familyId || !installmentsReady || installments.length > 0) return;

    const migrationKey = `familyPlanner.migrated.homeInstallments.${familyId}`;
    if (localStorage.getItem(migrationKey)) return;

    try {
      const legacyKey = "familyPlanner.homeInstallments";
      const raw = readSensitiveItem(legacyKey);
      const oldItems = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(oldItems) || oldItems.length === 0) {
        localStorage.setItem(migrationKey, "empty");
        return;
      }

      Promise.all(oldItems.map(({ id, ...installment }) => addHomeInstallment({ familyId, installment, user })))
        .then(() => {
          removeSensitiveItem(legacyKey);
          localStorage.setItem(migrationKey, "done");
        })
        .catch((error) => console.error("Taksit local kayıtları backend'e aktarılamadı:", error));
    } catch (error) {
      console.error("Taksit local kayıtları okunamadı:", error);
      localStorage.setItem(migrationKey, "error");
    }
  }, [familyId, installmentsReady, installments.length, user]);

  // __financeSubpageBack: render-time senkron güncelle — sadece aktif instance
  // isBgInstance=true olan bg render'ı window'a dokunmaz
  const _finansAltSayfa = aktifSekme !== "genel" && aktifSekme !== "rapor";
  if (!isBgInstance) {
    window.__financeSubpageBack = _finansAltSayfa
      ? () => setAktifSekme("genel")
      : null;
  }

  // Unmount'ta temizle (sadece aktif instance)
  useEffect(() => {
    if (isBgInstance) return;
    return () => { window.__financeSubpageBack = null; };
  }, [isBgInstance]);

  useEffect(() => {
    setFinanceActionMenuId(null);
  }, [aktifSekme]);


  const now = new Date();
  const yilSecenekleri = [];
  for (let y = now.getFullYear() - 4; y <= now.getFullYear() + 1; y++) yilSecenekleri.push(y);

  const gelirler = useMemo(() => finansFiltreli.filter((f) => f.tur === "Gelir").sort(sortNewest), [finansFiltreli]);
  const giderler = useMemo(() => finansFiltreli.filter((f) => f.tur === "Gider").sort(sortNewest), [finansFiltreli]);
  const gorunenGelirler = gelirler;
  const gorunenGiderler = giderler;
  const finansFiltreliBaz = useMemo(() => finansFiltreli.map((item) => ({
    ...item,
    tutar: baseAmountFor(item) ?? 0,
  })), [finansFiltreli, baseAmountFor]);
  const tumFinanslarBaz = useMemo(() => tumFinanslar.map((item) => ({
    ...item,
    tutar: baseAmountFor(item) ?? 0,
  })), [tumFinanslar, baseAmountFor]);
  const giderKategoriOzet = useMemo(() => categorySummary(finansFiltreliBaz, "Gider"), [finansFiltreliBaz]);
  const gelirKategoriOzet = useMemo(() => categorySummary(finansFiltreliBaz, "Gelir"), [finansFiltreliBaz]);
  const ortakKategoriOzet = useMemo(() => categorySummary(buAyHarcama), [buAyHarcama]);
  const ortakToplam = toplamOrtakHarcama;
  const enYogunKategori = giderKategoriOzet[0] || null;

  const oncekiDonemNet = useMemo(() => {
    const start = oncekiAyBaslangici(now);
    const end = ayBaslangici(now);
    const onceki = tumFinanslarBaz.filter((f) => {
      const d = new Date(f.tarih);
      return d >= start && d < end;
    });
    const gelir = onceki.filter((f) => f.tur === "Gelir").reduce((a, b) => a + Number(b.tutar || 0), 0);
    const gider = onceki.filter((f) => f.tur === "Gider").reduce((a, b) => a + Number(b.tutar || 0), 0);
    return gelir - gider;
  }, [tumFinanslarBaz]); // eslint-disable-line react-hooks/exhaustive-deps

  const thisMonthExpenses = useMemo(() => tumFinanslarBaz.filter((f) => f.tur === "Gider" && sameMonth(f.tarih)), [tumFinanslarBaz]);
  const thisMonthExpenseTotal = thisMonthExpenses.reduce((a, b) => a + Number(b.tutar || 0), 0);
  const thisMonthIncomeTotal = tumFinanslarBaz.filter((f) => f.tur === "Gelir" && sameMonth(f.tarih)).reduce((a, b) => a + Number(b.tutar || 0), 0);
  const sonOtuzGun = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 29);
    const map = {};
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      map[key] = { key, tarih: key, gelir: 0, gider: 0 };
    }
    tumFinanslarBaz.forEach((f) => {
      const d = new Date(f.tarih);
      if (Number.isNaN(d.getTime())) return;
      d.setHours(0, 0, 0, 0);
      if (d < start || d > today) return;
      const key = d.toISOString().slice(0, 10);
      if (!map[key]) map[key] = { key, tarih: key, gelir: 0, gider: 0 };
      map[key][f.tur === "Gelir" ? "gelir" : "gider"] += Number(f.tutar || 0);
    });
    return Object.values(map).sort((a, b) => new Date(a.tarih) - new Date(b.tarih));
  }, [tumFinanslarBaz]);
  const trendMax = Math.max(1, ...sonOtuzGun.map((g) => Math.max(g.gelir, g.gider)));
  const formatReportDay = useCallback((dateStr, withMonth = true) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr || "";
    const day = d.getDate();
    const month = AYLAR[d.getMonth()] || String(d.getMonth() + 1).padStart(2, "0");
    return withMonth ? `${day} ${month}` : String(day);
  }, [AYLAR]);
  const premiumTrendChart = useMemo(() => {
    const width = 360;
    const height = 190;
    const paddingX = 24;
    const paddingTop = 20;
    const paddingBottom = 40;
    const dailyData = sonOtuzGun.length > 0 ? sonOtuzGun : [];
    const data = [];
    for (let index = 0; index < dailyData.length; index += 5) {
      const days = dailyData.slice(index, index + 5);
      data.push({
        tarih: days[0]?.tarih,
        bitisTarihi: days[days.length - 1]?.tarih,
        gelir: days.reduce((sum, item) => sum + Number(item.gelir || 0), 0),
        gider: days.reduce((sum, item) => sum + Number(item.gider || 0), 0),
      });
    }
    const maxValue = Math.max(1, ...data.map((g) => Math.max(Number(g.gelir || 0), Number(g.gider || 0))));
    const chartHeight = height - paddingTop - paddingBottom;
    const availableWidth = width - paddingX * 2;
    const groupWidth = data.length ? availableWidth / data.length : availableWidth;
    const barWidth = Math.min(14, Math.max(9, groupWidth * 0.25));
    const baseline = height - paddingBottom;
    const getY = (rawValue) => paddingTop + chartHeight - (Math.max(0, rawValue) / maxValue) * chartHeight;
    const bars = data.map((item, index) => {
      const centerX = paddingX + groupWidth * index + groupWidth / 2;
      const incomeHeight = Math.max(item.gelir > 0 ? 3 : 0, (item.gelir / maxValue) * chartHeight);
      const expenseHeight = Math.max(item.gider > 0 ? 3 : 0, (item.gider / maxValue) * chartHeight);
      return {
        item,
        index,
        centerX,
        income: { x: centerX - barWidth - 2, y: baseline - incomeHeight, width: barWidth, height: incomeHeight },
        expense: { x: centerX + 2, y: baseline - expenseHeight, width: barWidth, height: expenseHeight },
      };
    });
    return {
      width,
      height,
      paddingX,
      paddingTop,
      paddingBottom,
      baseline,
      data,
      bars,
      gridValues: [maxValue, maxValue * 0.66, maxValue * 0.33, 0].map((value) => ({ value, y: getY(value) })),
    };
  }, [sonOtuzGun]);
  const aktifTrend = useMemo(() => {
    if (!premiumTrendChart.data?.length || aktifTrendIndex == null) return null;
    const index = Math.max(0, Math.min(aktifTrendIndex, premiumTrendChart.data.length - 1));
    const item = premiumTrendChart.data[index];
    if (!item) return null;
    return {
      index,
      item,
      label: `${formatReportDay(item.tarih)} – ${formatReportDay(item.bitisTarihi)}`,
      bar: premiumTrendChart.bars[index],
      income: Number(item.gelir || 0),
      expense: Number(item.gider || 0),
      net: Number(item.gelir || 0) - Number(item.gider || 0),
    };
  }, [aktifTrendIndex, premiumTrendChart, formatReportDay]);
  const handleTrendPointerMove = useCallback((event) => {
    const count = premiumTrendChart.data?.length || 0;
    if (!count) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / Math.max(1, rect.width)));
    setAktifTrendIndex(Math.round(ratio * (count - 1)));
  }, [premiumTrendChart]);
  const sonOtuzGunToplam = useMemo(() => {
    const gelir = sonOtuzGun.reduce((sum, item) => sum + Number(item.gelir || 0), 0);
    const gider = sonOtuzGun.reduce((sum, item) => sum + Number(item.gider || 0), 0);
    return { gelir, gider, net: gelir - gider };
  }, [sonOtuzGun]);

  const sonOtuzGunFinansKayitlari = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    return tumFinanslarBaz.filter((f) => {
      const d = new Date(f.tarih);
      return !Number.isNaN(d.getTime()) && d >= start && d <= end;
    });
  }, [tumFinanslarBaz]);
  const sonOtuzGunGelirKategoriOzet = useMemo(
    () => categorySummary(sonOtuzGunFinansKayitlari, "Gelir"),
    [sonOtuzGunFinansKayitlari]
  );
  const sonOtuzGunGiderKategoriOzet = useMemo(
    () => categorySummary(sonOtuzGunFinansKayitlari, "Gider"),
    [sonOtuzGunFinansKayitlari]
  );
  const breakdownIsIncome = breakdownType === "income";
  const aktifDagilimOzet = breakdownIsIncome ? sonOtuzGunGelirKategoriOzet : sonOtuzGunGiderKategoriOzet;
  const aktifDagilimToplam = breakdownIsIncome ? sonOtuzGunToplam.gelir : sonOtuzGunToplam.gider;
  const aktifDagilimBaslik = financeReportText(lang, breakdownIsIncome ? "incomeBreakdown" : "topSpending");
  const aktifDagilimBosMetin = financeReportText(lang, breakdownIsIncome ? "noIncomeData" : "noExpenseData");
  const aktifDagilimKategoriListesi = breakdownIsIncome ? "const.incomeCategories" : "const.expenseCategories";

  const finansRaporYorumu = useMemo(() => {
    const parts = [];
    const topCategory = sonOtuzGunGiderKategoriOzet[0];
    if (sonOtuzGunToplam.gelir > 0 && sonOtuzGunToplam.gider > 0) {
      const ratio = `${Math.round((sonOtuzGunToplam.gider / sonOtuzGunToplam.gelir) * 100)}%`;
      parts.push(financeReportText(lang, "expenseRatio", { ratio }));
    }
    if (topCategory) {
      const category = financeDisplayLabel(tList, lang, "const.expenseCategories", topCategory.kategori);
      parts.push(financeReportText(lang, "topCategory", { category }));
    }
    if (sonOtuzGunToplam.gelir > 0 || sonOtuzGunToplam.gider > 0) {
      parts.push(financeReportText(lang, sonOtuzGunToplam.net >= 0 ? "netPositive" : "netNegative"));
    }
    return parts.slice(0, 2).join(" ") || financeReportText(lang, "notEnoughData");
  }, [sonOtuzGunToplam, sonOtuzGunGiderKategoriOzet, lang, tList]);

  const akilliFinansMesaji = useMemo(() => {
    const alerts = [];
    if (thisMonthIncomeTotal > 0 && thisMonthExpenseTotal > thisMonthIncomeTotal * 0.85) alerts.push(t("finance.alert.highExpense"));
    if (enYogunKategori) alerts.push(`${fl(lang, "Bu dönem en çok")} ${fl(lang, enYogunKategori.kategori)} ${fl(lang, "kategorisinde harcama yapılmış.")}`);
    const arac = giderKategoriOzet.find((x) => x.kategori.includes("Araç") || x.kategori === "Yakıt");
    if (arac) alerts.push(`${fl(lang, "Araç/Yakıt gideri bu dönem")} ${financeMoneyFormat(arac.tutar)} ${fl(lang, "seviyesinde.")}`);
    return alerts[0] || t("finance.alert.insufficientData");
  }, [thisMonthIncomeTotal, thisMonthExpenseTotal, enYogunKategori, giderKategoriOzet, financeMoneyFormat, lang, t]);

  const savingsSummary = useMemo(() => {
    const toplamMaliyet = savings.reduce((a, s) => a + Number(s.miktar || 0) * Number(s.alisFiyati || 0), 0);
    const toplamDeger = savings.reduce((a, s) => a + Number(s.miktar || 0) * getSavingCurrentUnitPrice(s), 0);
    const kar = toplamDeger - toplamMaliyet;
    const enBuyuk = [...savings].sort((a, b) => (Number(b.miktar || 0) * getSavingCurrentUnitPrice(b)) - (Number(a.miktar || 0) * getSavingCurrentUnitPrice(a)))[0];
    return { toplamMaliyet, toplamDeger, kar, enBuyuk };
  }, [savings, getSavingCurrentUnitPrice]);

  const newestSavings = useMemo(() => [...savings].sort(sortFinanceCreatedNewest), [savings]);
  const newestInstallments = useMemo(() => [...installments].sort(sortFinanceCreatedNewest), [installments]);
  const newestSubscriptions = useMemo(() => [...subscriptions].sort(sortFinanceCreatedNewest), [subscriptions]);
  const marketPriceIsStale = !marketUpdatedAt || Number.isNaN(new Date(marketUpdatedAt).getTime()) || (Date.now() - new Date(marketUpdatedAt).getTime() > 2 * 60 * 60 * 1000);

  const currentDebtValueFor = useCallback((item) => {
    if (item?.debtKind === "gold") {
      const remainingItems = item.remainingGoldItems || item.goldItems || [];
      const liveValue = goldDebtValue(remainingItems, marketPrices, item.valuationCurrency || normalizedBaseCurrency);
      return (liveValue !== null && (liveValue > 0 || remainingItems.length === 0)) ? liveValue : Number(item.currentDebtValue || item.toplam || 0);
    }
    if (item?.debtKind === "foreign_currency") {
      return convertDebtCurrency(Number(item.remainingForeignAmount ?? item.foreignPrincipal ?? 0), item.debtCurrency || "USD", item.valuationCurrency || normalizedBaseCurrency, marketPrices) || Number(item.currentDebtValue || 0);
    }
    const monthly = Number(item?.aylikOdeme || 0) || (Number(item?.toplam || 0) / Math.max(1, Number(item?.taksitSayisi || 1)));
    return Math.max(0, (Number(item?.taksitSayisi || 0) - Number(item?.odenenTaksit || 0)) * monthly);
  }, [marketPrices, normalizedBaseCurrency]);

  const installmentSummary = useMemo(() => {
    const aktif = installments.filter((item) => item.debtKind ? item.status !== "completed" : Number(item.odenenTaksit || 0) < Number(item.taksitSayisi || 0));
    const kalan = installments.reduce((sum, item) => sum + currentDebtValueFor(item), 0);
    const buAy = aktif.reduce((sum, item) => sum + Number(item.monthlyPaymentAmount || item.aylikOdeme || 0), 0);
    const yakin = [...aktif].sort((a, b) => Number(a.odemeGunu || 31) - Number(b.odemeGunu || 31))[0];
    return { aktif, kalan, buAy, yakin };
  }, [installments, currentDebtValueFor]);

  const subscriptionSummary = useMemo(() => {
    const aktif = subscriptions.filter((s) => !isInactiveValue(s.aktif));
    const aylikToplam = aktif.reduce((acc, s) => {
      const tutar = baseAmountFor(s);
      if (tutar === null) return acc;
      return acc + monthlyCostFromPeriod(tutar, s.periyot);
    }, 0);
    const yakin = [...aktif]
      .filter((s) => s.sonrakiOdeme)
      .sort((a, b) => new Date(a.sonrakiOdeme) - new Date(b.sonrakiOdeme))[0];
    return { aktif, aylikToplam, yakin };
  }, [subscriptions, baseAmountFor]);

  const billSummary = useMemo(() => {
    const odenmemis = bills.filter((b) => !isPaidValue(b.durum));
    const odenmis = bills.filter((b) => isPaidValue(b.durum));
    const toplam = odenmemis.reduce((acc, b) => acc + (baseAmountFor(b) ?? 0), 0);
    const odenmisToplam = odenmis.reduce((acc, b) => acc + (baseAmountFor(b) ?? 0), 0);
    const yakin = [...odenmemis]
      .filter((b) => b.sonOdemeTarihi)
      .sort((a, b) => new Date(a.sonOdemeTarihi) - new Date(b.sonOdemeTarihi))[0];
    return { odenmemis, odenmis, toplam, odenmisToplam, yakin };
  }, [bills, baseAmountFor]);

  const filteredBills = useMemo(() => {
    return [...bills].filter((b) => {
      if (!b.sonOdemeTarihi) return billFilterMode === "allYear";
      const d = new Date(b.sonOdemeTarihi);
      if (Number.isNaN(d.getTime())) return billFilterMode === "allYear";

      if (billFilterMode === "currentMonth") {
        const current = new Date();
        return d.getFullYear() === current.getFullYear() && d.getMonth() === current.getMonth();
      }

      if (billFilterMode === "previousMonth") {
        const previous = new Date();
        previous.setMonth(previous.getMonth() - 1);
        return d.getFullYear() === previous.getFullYear() && d.getMonth() === previous.getMonth();
      }

      if (billFilterMode === "allYear") {
        return d.getFullYear() === Number(billFilterYear);
      }

      return d.getFullYear() === Number(billFilterYear) && d.getMonth() === Number(billFilterMonth);
    }).sort(sortFinanceCreatedNewest);
  }, [bills, billFilterMode, billFilterMonth, billFilterYear]);

  const carriedOverBills = useMemo(() => {
    if (billFilterMode !== "currentMonth") return [];

    const now = new Date();
    const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;

    return bills
      .filter((bill) => {
        if (isPaidValue(bill.durum)) return false;
        const dueDate = String(bill.sonOdemeTarihi || "").slice(0, 10);
        return /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && dueDate < currentMonthStart;
      })
      .sort((a, b) => String(a.sonOdemeTarihi).localeCompare(String(b.sonOdemeTarihi)));
  }, [bills, billFilterMode]);

  const carriedOverBillKeys = useMemo(
    () => new Set(carriedOverBills.map(financeBillIdentity)),
    [carriedOverBills]
  );

  const quickBillCandidates = useMemo(() => {
    const now = new Date();
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentKeys = new Set(
      bills
        .filter((bill) => String(bill.sonOdemeTarihi || "").slice(0, 7) === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`)
        .map(financeBillIdentity)
    );
    const uniquePrevious = new Map();

    bills.forEach((bill) => {
      const dateText = String(bill.sonOdemeTarihi || "");
      const [year, month] = dateText.split("-").map(Number);
      if (year !== previous.getFullYear() || month !== previous.getMonth() + 1) return;
      const key = financeBillIdentity(bill);
      if (!uniquePrevious.has(key)) uniquePrevious.set(key, bill);
    });

    return Array.from(uniquePrevious.entries())
      .filter(([key]) => !currentKeys.has(key) && !carriedOverBillKeys.has(key))
      .map(([, bill]) => bill);
  }, [bills, carriedOverBillKeys]);

  useEffect(() => {
    if (!quickBillCandidates.length) return;
    const now = new Date();
    setQuickBillDrafts((current) => {
      const next = { ...current };
      quickBillCandidates.forEach((bill) => {
        if (next[bill.id]) return;
        const previousDay = Number(String(bill.sonOdemeTarihi || "").split("-")[2] || now.getDate());
        next[bill.id] = {
          tutar: moneyValue(bill.tutar),
          sonOdemeTarihi: monthDateWithClampedDay(now.getFullYear(), now.getMonth(), previousDay),
        };
      });
      return next;
    });
  }, [quickBillCandidates]);

  const resetInstallmentDraft = () => {
    setInstallmentEditId(null);
    setInstallmentDraft(emptyInstallmentDraft(taksitKategorileri[0] || "", debtRegion, normalizedBaseCurrency));
    setInstallmentReminder({ aktif: false, tarih: "", saat: "09:00", tekrar: "none" });
    setInstallmentReminderError("");
    setFinanceFormErrors((current) => ({ ...current, installment: [] }));
  };

  const resetSubscriptionDraft = () => {
    setSubscriptionEditId(null);
    setSubDraft(withFinanceNotificationTargetDefaults({ ad: "", kategori: abonelikKategorileri[0] || "", tutar: "", paraBirimi: defaultFormCurrency, periyot: periyotlar[0] || "", sonrakiOdeme: "", odemeSahibi: "", aktif: lang === "en" ? "Yes" : "Evet", not: "" }));
    setFinanceFormErrors((current) => ({ ...current, subscription: [] }));
  };

  const resetBillDraft = () => {
    setBillEditId(null);
    setBillDraft(withFinanceNotificationTargetDefaults({ ad: "", kategori: faturaKategorileri[0] || "", tutar: "", paraBirimi: defaultFormCurrency, sonOdemeTarihi: "", odemeSahibi: "", durum: faturaDurumlar[0] || "", periyot: periyotlar[0] || "", not: "" }));
    setFinanceFormErrors((current) => ({ ...current, bill: [] }));
  };

  const startEditInstallment = (item) => {
    setFinanceFormErrors((current) => ({ ...current, installment: [] }));
    setInstallmentEditId(item.id);
    setInstallmentDraft(withFinanceNotificationTargetDefaults({
      ...item,
      toplam: moneyValue(item.toplam),
      taksitSayisi: item.taksitSayisi === undefined ? "" : String(item.taksitSayisi),
      odenenTaksit: item.odenenTaksit === undefined ? "" : String(item.odenenTaksit),
      aylikOdeme: moneyValue(item.aylikOdeme),
      monthlyPaymentAmount: moneyValue(item.monthlyPaymentAmount ?? item.aylikOdeme),
      foreignPrincipal: moneyValue(item.foreignPrincipal),
      goldItems: (item.remainingGoldItems || item.goldItems || [newGoldDebtItem(debtRegion)]).map((goldItem) => ({ ...goldItem, quantity: moneyValue(goldItem.quantity) })),
      odemeGunu: item.odemeGunu === undefined ? "" : String(item.odemeGunu),
    }));
    const customReminder = item.customReminder || {};
    setInstallmentReminder({
      aktif: customReminder.enabled === true,
      tarih: customReminder.date || "",
      saat: customReminder.time || "09:00",
      tekrar: customReminder.repeat || "none",
    });
    setInstallmentReminderError("");
    setInstallmentFormAcik(true);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const startEditSubscription = (item) => {
    setFinanceFormErrors((current) => ({ ...current, subscription: [] }));
    setSubscriptionEditId(item.id);
    setSubDraft(withFinanceNotificationTargetDefaults({ ...item, tutar: moneyValue(item.tutar) }));
    setSubFormAcik(true);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const startEditBill = (item) => {
    setFinanceFormErrors((current) => ({ ...current, bill: [] }));
    setBillEditId(item.id);
    setBillDraft(withFinanceNotificationTargetDefaults({ ...item, tutar: moneyValue(item.tutar) }));
    setBillFormAcik(true);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const handleFinanceBack = () => {
    setAktifSekme("genel");
  };

  const saveSaving = async () => {
    if (!familyId) return;
    const isRealEstateDraft = savingDraft.tur === "Gayrimenkul" || savingDraft.tur === "Real Estate";
    const savingMissingFields = isRealEstateDraft
      ? [
          !savingDraft.ad?.trim() && "ad",
          !savingDraft.gayrimenkulTuru && "gayrimenkulTuru",
          !savingDraft.sehir?.trim() && "sehir",
          !savingDraft.alisFiyati && "alisFiyati",
          !savingDraft.guncelFiyat && "guncelFiyat",
        ]
      : [
          !savingDraft.miktar && "miktar",
          !["Mevduat", "Nakit"].includes(savingDraft.tur) && !savingDraft.alisFiyati && "alisFiyati",
        ];
    if (!markFinanceFormErrors("saving", savingMissingFields)) return;

    const payloadSaving = buildSavingPayload(savingDraft);
    let reminderPayload = null;
    if (!savingEditId && savingReminder.aktif && onReminderKur) {
      reminderPayload = reminderPayloadKontrolEt({
        mod: "datetime",
        tarih: savingReminder.tarih,
        saat: savingReminder.saat,
        tekrar: savingReminder.tekrar,
      }, t, lang, setSavingReminderError);
      if (!reminderPayload) return;
    }

    return runFinanceSubmission("saving", async () => {
    try {
      if (savingEditId) {
        await updateFinanceSaving({ familyId, savingId: savingEditId, patch: cleanFinancePatch(payloadSaving) });

        // Eski kayıtlar assetKey içermese bile düzenlenen kayıt yeni yapıya dahil olur.
        // Aynı varlığa ait eski/yeni tüm kayıtların güncel değeri de birlikte güncellenir.
        const matchingSavings = savings.filter((item) => {
          if (item.id === savingEditId) return false;
          return normalizeMarketAssetKey(item) === normalizeMarketAssetKey(payloadSaving);
        });
        if (matchingSavings.length > 0) {
          await Promise.all(matchingSavings.map((item) => updateFinanceSaving({
            familyId,
            savingId: item.id,
            patch: { ...getSavingAssetInfo(item), guncelFiyat: Number(payloadSaving.guncelFiyat || 0) },
          })));
        }
      } else {
        const yeniSavingRef = await addFinanceSaving({ familyId, user, saving: payloadSaving });
        payloadSaving.id = yeniSavingRef?.id || yeniSavingRef;
      }

      // Hatırlatma
      if (!savingEditId && payloadSaving.id && reminderPayload && onReminderKur) {
        await onReminderKur({
            baslik: t("finance.savings.reminderTitle"),
            aciklama: lang === "en" ? `Don't forget to update your "${payloadSaving.ad}" saving!` : `"${payloadSaving.ad}" birikimini güncellemeyi unutma!`,
            baslikKey: "finance.savings.reminderTitle",
            aciklamaKey: "finance.savings.reminderBody",
            aciklamaVars: { name: payloadSaving.ad },
            kaynak: "financeSaving",
            kaynakId: payloadSaving.id,
            kaynakTuru: "financeSaving",
            url: "/?pushPage=finans",
            ...reminderPayload,
          }).catch(() => {});
        setSavingReminder({ aktif: false, tarih: "", saat: "09:00", tekrar: "none" });
        setSavingReminderError("");
      }

      setSavingFormAcik(false);
      setSavingDraft(emptySavingDraft(defaultFormCurrency));
      setSavingEditId(null);
      setFinanceFormErrors((current) => ({ ...current, saving: [] }));
    } catch (error) {
      console.error("Birikim kaydı eklenemedi:", error);
      throw error;
    }
    });
  };

  const editSaving = (saving) => {
    setFinanceFormErrors((current) => ({ ...current, saving: [] }));
    setSavingEditId(saving.id);
    setSavingDraft({
      ...emptySavingDraft(defaultFormCurrency),
      ...saving,
      miktar: moneyValue(saving.miktar),
      alisFiyati: moneyValue(saving.alisFiyati),
      guncelFiyat: moneyValue(saving.guncelFiyat),
      faizOrani: saving.faizOrani === "" || saving.faizOrani === undefined ? "" : moneyValue(saving.faizOrani),
    });
    setSavingFormAcik(true);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const cancelSavingEdit = () => {
    setSavingEditId(null);
    setSavingDraft(emptySavingDraft(defaultFormCurrency));
    setFinanceFormErrors((current) => ({ ...current, saving: [] }));
  };

  const addInstallment = async () => {
    if (!familyId) return;
    if (!validateInstallmentDraft()) return;
    const reminderPayload = customDebtReminderPayload();
    if (isDebtInstallmentDraft && installmentReminder.aktif && !reminderPayload) return;
    const payload = buildInstallmentPayload();
    return runFinanceSubmission("installment", async () => {
    try {
      const item = await addHomeInstallment({ familyId, user, installment: payload });
      if (isDebtInstallmentDraft) {
        try { await syncDebtCustomReminder(item, reminderPayload); }
        catch (reminderError) {
          console.error("Borç hatırlatıcısı oluşturulamadı:", reminderError);
          await appAlert(t("finance.debt.reminderSaveFailed"));
        }
      }
      setInstallmentFormAcik(false);
      resetInstallmentDraft();
    } catch (error) {
      console.error("Taksit kaydı eklenemedi:", error);
      throw error;
    }
    });
  };

  const saveInstallment = async () => {
    if (!installmentEditId) return addInstallment();
    if (!familyId) return;
    if (!validateInstallmentDraft()) return;
    const reminderPayload = customDebtReminderPayload();
    if (isDebtInstallmentDraft && installmentReminder.aktif && !reminderPayload) return;
    const payload = buildInstallmentPayload();

    return runFinanceSubmission("installment", async () => {
    try {
      const item = await updateHomeInstallment({
        familyId,
        installmentId: installmentEditId,
        patch: payload,
      });
      if (isDebtInstallmentDraft) {
        try { await syncDebtCustomReminder(item, reminderPayload); }
        catch (reminderError) {
          console.error("Borç hatırlatıcısı güncellenemedi:", reminderError);
          await appAlert(t("finance.debt.reminderSaveFailed"));
        }
      } else {
        const previousItem = installments.find((candidate) => candidate.id === installmentEditId);
        if (previousItem && ["gold", "foreign_currency"].includes(previousItem.debtKind)) {
          await deleteHomeInstallmentReminders({ familyId, installmentId: item.id, installment: previousItem });
          await updateHomeInstallment({ familyId, installmentId: item.id, patch: { reminderId: null, reminderIds: [], customReminder: { enabled: false } } });
        }
      }

      setInstallmentFormAcik(false);
      resetInstallmentDraft();
    } catch (error) {
      console.error("Taksit kaydı güncellenemedi:", error);
      throw error;
    }
    });
  };

  const updateSaving = async (saving, patch) => {
    if (!familyId || !saving?.id) return;

    try {
      if (Object.prototype.hasOwnProperty.call(patch, "guncelFiyat")) {
        const nextPrice = parseMoneyInput(patch.guncelFiyat);

        if (isAutoMarketPricedSaving(saving)) {
          const assetKey = normalizeMarketAssetKey(saving);
          const matchingSavings = savings.filter((item) => isAutoMarketSavingType(item) && normalizeMarketAssetKey(item) === assetKey);

          if (saving.assetKey) {
            await updateFinanceSavingsCurrentPriceByAsset({ familyId, assetKey, guncelFiyat: nextPrice });
          }

          await Promise.all(matchingSavings.map((item) => updateFinanceSaving({
            familyId,
            savingId: item.id,
            patch: { ...getSavingAssetInfo(item), guncelFiyat: nextPrice },
          })));
        } else {
          await updateFinanceSaving({
            familyId,
            savingId: saving.id,
            patch: { ...getSavingAssetInfo(saving), guncelFiyat: nextPrice },
          });
        }
      } else {
        await updateFinanceSaving({ familyId, savingId: saving.id, patch });
      }
    } catch (error) {
      console.error("Birikim kaydı güncellenemedi:", error);
      appAlert(fl(lang, "Birikim kaydı güncellenemedi. Lütfen tekrar dene."));
    }
  };

  const deleteSaving = async (id) => {
    if (!await appConfirm(t("finance.confirmDelete"))) return;

    try {
      await deleteFinanceSaving({ familyId, savingId: id, saving: savings.find((item) => item.id === id) });
    } catch (error) {
      console.error("Birikim kaydı silinemedi:", error);
      appAlert(fl(lang, "Birikim kaydı silinemedi. Lütfen tekrar dene."));
    }
  };

  const deleteInstallment = async (id) => {
    await deleteFinanceItemOptimistically({
      section: "installment",
      id,
      items: installments,
      setItems: setInstallments,
      remove: (item) => deleteHomeInstallment({ familyId, installmentId: id, installment: item }),
      errorMessage: fl(lang, "Taksit kaydı silinemedi. Lütfen tekrar dene."),
    });
  };

  const updateInstallmentPaidCount = async (installment, paidCount) => {
    if (!familyId || !installment?.id) return;

    const totalCount = Math.max(0, Number(installment.taksitSayisi || 0));
    const nextPaidCount = Math.min(totalCount, Math.max(0, Number(paidCount || 0)));

    try {
      await updateHomeInstallment({
        familyId,
        installmentId: installment.id,
        patch: { odenenTaksit: nextPaidCount },
      });
    } catch (error) {
      console.error("Taksit ödeme durumu güncellenemedi:", error);
      appAlert(lang === "en" ? "Installment payment status could not be updated. Please try again." : "Taksit ödeme durumu güncellenemedi. Lütfen tekrar dene.");
    }
  };

  const debtPaymentDraftFor = (installment) => {
    const existing = debtPaymentDrafts[installment.id];
    if (existing) return existing;
    const remaining = installment.remainingGoldItems || installment.goldItems || [];
    const firstGold = remaining[0] ? { ...remaining[0], quantity: "" } : newGoldDebtItem(debtRegion);
    return {
      method: installment.debtKind === "gold" ? "money" : "money",
      amount: "",
      currency: installment.monthlyPaymentCurrency || installment.valuationCurrency || normalizedBaseCurrency,
      paidAt: new Date().toISOString().slice(0, 10),
      goldItems: [firstGold],
      note: "",
    };
  };
  const setDebtPaymentDraft = (installmentId, updater) => setDebtPaymentDrafts((current) => {
    const installment = installments.find((item) => item.id === installmentId) || {};
    const previous = current[installmentId] || debtPaymentDraftFor(installment);
    return { ...current, [installmentId]: typeof updater === "function" ? updater(previous) : updater };
  });
  const openDebtPayment = (installment) => {
    setDebtPaymentDrafts((current) => current[installment.id] ? current : ({ ...current, [installment.id]: debtPaymentDraftFor(installment) }));
    setDebtPaymentPanelId((current) => current === installment.id ? null : installment.id);
  };
  const updateDebtPaymentGoldItem = (installment, index, identity) => {
    const source = (installment.remainingGoldItems || installment.goldItems || []).find((item) => `${item.assetKey}|${item.unit}|${item.karat || ""}|${item.purity || ""}|${item.label || ""}` === identity);
    if (!source) return;
    setDebtPaymentDraft(installment.id, (current) => {
      const items = [...(current.goldItems || [])];
      items[index] = { ...source, quantity: "" };
      return { ...current, goldItems: items };
    });
  };
  const saveDebtPayment = async (installment) => {
    if (debtPaymentSubmittingId) return;
    const draft = debtPaymentDraftFor(installment);
    const needsMoney = draft.method === "money" || draft.method === "mixed";
    const needsGold = draft.method === "gold" || draft.method === "mixed";
    if ((needsMoney && !draft.amount) || (needsGold && !(draft.goldItems || []).some((item) => item.quantity))) {
      await appAlert(t("finance.debt.paymentRequired"));
      return;
    }
    setDebtPaymentSubmittingId(installment.id);
    try {
      await addFinanceDebtPayment({
        familyId,
        installmentId: installment.id,
        payment: {
          method: draft.method,
          amount: needsMoney ? parseMoneyInput(draft.amount) : null,
          currency: normalizeCurrencyCode(draft.currency),
          paidAt: draft.paidAt,
          goldItems: needsGold ? draft.goldItems.filter((item) => item.quantity).map((item) => ({ ...item, quantity: parseMoneyInput(item.quantity) })) : [],
          note: draft.note,
          idempotencyKey: `debt_payment_${installment.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        },
      });
      setDebtPaymentPanelId(null);
      setDebtPaymentDrafts((current) => {
        const next = { ...current };
        delete next[installment.id];
        return next;
      });
      return true;
    } catch (error) {
      console.error("Borç ödemesi kaydedilemedi:", error);
      throw error;
    } finally {
      setDebtPaymentSubmittingId(null);
    }
  };

  const addSubscription = async () => {
    const adEtkili = isOtherValue(subDraft.ad) ? (subDraft.adManuel?.trim() || (lang === "en" ? "Other" : "Diğer")) : subDraft.ad;
    if (!familyId) return;
    if (!markFinanceFormErrors("subscription", [
      !subDraft.ad && "ad",
      isOtherValue(subDraft.ad) && !subDraft.adManuel?.trim() && "adManuel",
      !subDraft.tutar && "tutar",
    ])) return;
    return runFinanceSubmission("subscription", async () => {
    try {
      const { adManuel, ...temizDraft } = subDraft;
      await addFinanceSubscription({
        familyId, user,
        subscription: {
          ...temizDraft,
          ad: adEtkili,
          tutar: parseMoneyInput(subDraft.tutar),
        },
      });
      setSubFormAcik(false);
      resetSubscriptionDraft();
    } catch (e) {
      console.error("Abonelik ekleme hatası:", e);
      throw e;
    }
    });
  };

  const saveSubscriptionEdit = async () => {
    if (!subscriptionEditId) return addSubscription();
    const adEtkili = isOtherValue(subDraft.ad) ? (subDraft.adManuel?.trim() || (lang === "en" ? "Other" : "Diğer")) : subDraft.ad;
    if (!familyId) return;
    if (!markFinanceFormErrors("subscription", [
      !subDraft.ad && "ad",
      isOtherValue(subDraft.ad) && !subDraft.adManuel?.trim() && "adManuel",
      !subDraft.tutar && "tutar",
    ])) return;

    return runFinanceSubmission("subscription", async () => {
    try {
      const { adManuel, ...temizDraft } = subDraft;
      await updateFinanceSubscription({
        familyId,
        subscriptionId: subscriptionEditId,
        patch: {
          ...cleanFinancePatch(temizDraft),
          ad: adEtkili,
          tutar: parseMoneyInput(subDraft.tutar),
        },
      });

      setSubFormAcik(false);
      resetSubscriptionDraft();
    } catch (e) {
      console.error("Abonelik güncelleme hatası:", e);
      throw e;
    }
    });
  };

  const deleteSubscription = async (id) => {
    await deleteFinanceItemOptimistically({
      section: "subscription",
      id,
      items: subscriptions,
      setItems: setSubscriptions,
      remove: (item) => deleteFinanceSubscription({ familyId, subscriptionId: id, subscription: item }),
      errorMessage: fl(lang, "Abonelik silinemedi."),
    });
  };

  const toggleSubscriptionAktif = async (sub) => {
    try {
      await updateFinanceSubscription({
        familyId,
        subscriptionId: sub.id,
        patch: { aktif: isInactiveValue(sub.aktif) ? (lang === "en" ? "Yes" : "Evet") : (lang === "en" ? "No" : "Hayır") },
      });
    } catch (e) { console.error(e); }
  };

  const addBill = async () => {
    const adEtkili = billDraft.ad || billDraft.kategori;
    if (!familyId) return;
    if (!markFinanceFormErrors("bill", [
      isOtherValue(billDraft.kategori) && !billDraft.ad?.trim() && "ad",
      !billDraft.tutar && "tutar",
      !billDraft.sonOdemeTarihi && "sonOdemeTarihi",
    ])) return;
    return runFinanceSubmission("bill", async () => {
    try {
      await addFinanceBill({
        familyId, user,
        bill: {
          ...billDraft,
          ad: adEtkili,
          tutar: parseMoneyInput(billDraft.tutar),
        },
      });
      setBillFormAcik(false);
      resetBillDraft();
    } catch (e) {
      console.error(e);
      throw e;
    }
    });
  };

  const addQuickBill = async (sourceBill) => {
    if (!familyId || !sourceBill?.id || quickBillAddingId) return;
    const draft = quickBillDrafts[sourceBill.id] || {};
    if (!draft.tutar || !draft.sonOdemeTarihi) {
      appAlert(t("finance.bills.quick.validation"));
      return;
    }

    const excludedKeys = new Set([
      "id", "firebaseId", "itemType", "createdAt", "updatedAt", "durum",
      "sonOdemeTarihi", "reminderId", "reminderIds", "financeAutoPaidAt",
    ]);
    const copied = Object.fromEntries(
      Object.entries(sourceBill).filter(([key]) => !excludedKeys.has(key))
    );

    try {
      setQuickBillAddingId(sourceBill.id);
      await addFinanceBill({
        familyId,
        user,
        bill: {
          ...copied,
          ad: sourceBill.ad || sourceBill.kategori,
          tutar: parseMoneyInput(draft.tutar),
          sonOdemeTarihi: draft.sonOdemeTarihi,
          durum: lang === "en" ? "Unpaid" : "Ödenmedi",
          copiedFromBillId: sourceBill.id,
          copiedFromMonth: String(sourceBill.sonOdemeTarihi || "").slice(0, 7),
        },
      });
      setBillFilterMode("currentMonth");
      setQuickBillDrafts((current) => {
        const next = { ...current };
        delete next[sourceBill.id];
        return next;
      });
      return true;
    } catch (error) {
      console.error("Hızlı fatura ekleme hatası:", error);
      throw error;
    } finally {
      setQuickBillAddingId(null);
    }
  };

  const saveBillEdit = async () => {
    if (!billEditId) return addBill();
    const adEtkili = billDraft.ad || billDraft.kategori;
    if (!familyId) return;
    if (!markFinanceFormErrors("bill", [
      isOtherValue(billDraft.kategori) && !billDraft.ad?.trim() && "ad",
      !billDraft.tutar && "tutar",
      !billDraft.sonOdemeTarihi && "sonOdemeTarihi",
    ])) return;

    return runFinanceSubmission("bill", async () => {
    try {
      await updateFinanceBill({
        familyId,
        billId: billEditId,
        patch: {
          ...cleanFinancePatch(billDraft),
          ad: adEtkili,
          tutar: parseMoneyInput(billDraft.tutar),
        },
      });

      setBillFormAcik(false);
      resetBillDraft();
    } catch (e) {
      console.error("Fatura güncelleme hatası:", e);
      throw e;
    }
    });
  };

  const deleteBill = async (id) => {
    await deleteFinanceItemOptimistically({
      section: "bill",
      id,
      items: bills,
      setItems: setBills,
      remove: (item) => deleteFinanceBill({ familyId, billId: id, bill: item }),
      errorMessage: fl(lang, "Fatura silinemedi."),
    });
  };

  const toggleBillDurum = async (bill) => {
    const yeniDurum = isPaidValue(bill.durum)
      ? (lang === "en" ? "Unpaid" : "Ödenmedi")
      : (lang === "en" ? "Paid" : "Ödendi");
    try {
      await updateFinanceBill({ familyId, billId: bill.id, patch: { durum: yeniDurum } });
    } catch (e) { console.error(e); }
  };

  const renderFinanceRow = (f, idx, last) => {
    const sourceAsset = financeExpenseSourceAsset(f);
    return (
    <div key={f.id} className={`tableRow financeProRow ${last ? "tableRowLast" : ""}`}>
      <div className={`financeIndicator ${f.tur === "Gelir" ? "incomeBar" : "expenseBar"}`} />
      <div className="tableRowContent">
        <span className="tableRowTitle" style={{display:"flex",alignItems:"center",gap:4}}>
          {sourceAsset && <span className="financeExpenseSourceAsset" aria-hidden="true"><img src={sourceAsset} alt="" loading="eager" decoding="sync" /></span>}
          <ExpandableText text={f.ortakHarcama ? String(f.baslik||"").replace(/^Ortak Harcama:\s*/i,"") : f.baslik} className="" maxLength={34} />
        </span>
        <span className="tableRowSub">{financeExpenseCategoryLabel(f, lang, tList)} · {tarihOkunakli(f.tarih, AYLAR)} · {f.ekleyen}</span>
      </div>
      <span className={`financeAmount ${f.tur === "Gelir" ? "green" : "red"}`}>
        {financeAmountsHidden ? "••••••" : <>{f.tur === "Gelir" ? "+" : "-"}{baseAmountFor(f) === null ? `${formatMoneyDisplayNumber(f.tutar, 2)} ${f.paraBirimi || normalizedBaseCurrency}` : financeMoneyFormat(baseAmountFor(f))}</>}
      </span>
      {(canDeleteAll || (canDeleteOwn && f.ekleyenUid === user?.uid)) && (
        <button className="rowDeleteBtn" onClick={() => handleMainFinanceDelete(f)}>{fl(lang, "Sil")}</button>
      )}
    </div>
  );
  };

  const renderBillTile = (bill, { carriedOver = false } = {}) => {
    const tutar = Number(bill.tutar || 0);
    const bazTutar = baseAmountFor(bill);
    const odendi = isPaidValue(bill.durum);
    const beklemede = bill.durum === "Beklemede" || bill.durum === "Pending";
    const faturaAdi = bill.kategori === "Diğer" && bill.ad ? bill.ad : fl(lang, bill.kategori);
    const faturaIcon = {
      Elektrik: "⚡", Doğalgaz: "🔥", Su: "💧", İnternet: "📡",
      "Telefon (Fatura)": "📱", Kira: "🏠", Aidat: "🏢",
      Sigorta: "🛡️", Vergi: "📊",
    }[bill.kategori] || "📋";

    let tarihDurumu = null;
    if (bill.sonOdemeTarihi && !odendi) {
      const gun = Math.ceil((new Date(bill.sonOdemeTarihi) - new Date()) / 86400000);
      if (gun < 0) tarihDurumu = { renk: "amountRed", metin: `${Math.abs(gun)} ${t("finance.bills.overdue")}` };
      else if (gun <= 3) tarihDurumu = { renk: "amountRed", metin: `${gun} ${t("finance.bills.daysLeft")}` };
      else if (gun <= 7) tarihDurumu = { renk: "amountNeutral", metin: `${gun} ${t("finance.bills.daysLeft")}` };
      else tarihDurumu = { renk: "", metin: `${gun} ${t("finance.bills.daysLeft")}` };
    }

    return (
      <div
        className={`premiumBillTile ${odendi ? "billOdendi" : ""} ${carriedOver ? "billCarriedOver" : ""}`}
        key={`${carriedOver ? "carried" : "period"}-${bill.id}`}
      >
        <div className="assetTileTop">
          <div className={`assetAvatar bill ${odendi ? "odendi" : beklemede ? "beklemede" : "odenmedi"}`}>{faturaIcon}</div>
          <div className="assetTitleBlock">
            <span className="assetTileTitle">{faturaAdi}</span>
            <span>{fl(lang, bill.periyot)}{bill.odemeSahibi ? ` · ${bill.odemeSahibi}` : ""}</span>
            {carriedOver && <span className="billCarryoverBadge">↗ {t("finance.bills.carryover.badge")}</span>}
          </div>
          <div className="subTileActions">
            <button
              className={`billToggleBtn ${odendi ? "odendi" : "odenmedi"}`}
              onClick={() => toggleBillDurum(bill)}
              title={odendi ? t("finance.bills.markUnpaid") : t("finance.bills.markPaid")}
            >{odendi ? t("finance.bills.paidBtn") : t("finance.bills.payBtn")}</button>
            <FinanceRowActionMenu
              menuId={`bill-${bill.id}`}
              onEdit={() => startEditBill(bill)}
              onDelete={() => deleteBill(bill.id)}
            />
          </div>
        </div>
        <div className="assetMetricGrid">
          <div>
            <span>{t("common.amount")}</span>
            <b className={odendi ? "amountGreen" : "amountRed"}>{financeAmountsHidden ? "••••••" : `${formatMoneyDisplayNumber(tutar, 2)} ${bill.paraBirimi || "TRY"}`}</b>
            {normalizeCurrencyCode(bill.paraBirimi || normalizedBaseCurrency) !== normalizedBaseCurrency && (
              <small>{bazTutar === null ? t("finance.currency.rateUnavailable") : `≈ ${financeMoneyFormat(bazTutar)}`}</small>
            )}
          </div>
          <div>
            <span>{t("finance.bills.dueDateLabel")}</span>
            <b className={tarihDurumu?.renk || ""}>{bill.sonOdemeTarihi ? tarihOkunakli(bill.sonOdemeTarihi, AYLAR) : "—"}</b>
          </div>
          <div>
            <span>{t("finance.bills.statusLabel")}</span>
            <b className={odendi ? "amountGreen" : beklemede ? "" : "amountRed"}>{fl(lang, bill.durum)}</b>
          </div>
        </div>
        {tarihDurumu && (
          <div className="billUyari">
            <span className={tarihDurumu.renk}>⏰ {tarihDurumu.metin}</span>
          </div>
        )}
        {bill.not && <ExpandableText text={bill.not} className="assetNoteText" maxLength={56} />}
      </div>
    );
  };

  return (
    <div className={`pageContent financeProPage ${aktifSekme === "rapor" ? "financeReportMode" : "financeGeneralMode"}`}>
      <style>{`
        .financeProPage .financeToolEmptyPremium {
          min-height: 188px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          padding: 20px 22px;
          text-align: center;
        }
        .financeProPage .financeToolEmptyPremiumImage {
          width: clamp(66px, 20vw, 92px);
          height: clamp(66px, 20vw, 92px);
          object-fit: contain;
          display: block;
          opacity: 0.5;
          flex: 0 0 auto;
          user-select: none;
          -webkit-user-drag: none;
          pointer-events: none;
        }
        .financeProPage .financeToolEmptyPremiumTitle {
          margin: 10px 0 0;
          color: var(--text);
          font-size: 14px;
          line-height: 1.25;
          font-weight: 750;
          letter-spacing: -0.01em;
        }
        .financeProPage .financeToolEmptyPremiumText {
          max-width: 290px;
          margin: 7px 0 0;
          color: var(--muted);
          font-size: 12px;
          line-height: 1.42;
          font-weight: 500;
        }
        @media (max-height: 700px) {
          .financeProPage .financeToolEmptyPremium {
            min-height: 154px;
            padding: 14px 18px;
          }
          .financeProPage .financeToolEmptyPremiumImage {
            width: 62px;
            height: 62px;
          }
          .financeProPage .financeToolEmptyPremiumTitle { margin-top: 7px; }
          .financeProPage .financeToolEmptyPremiumText { margin-top: 5px; }
        }
        .financeProPage {
          padding-bottom: max(8px, env(safe-area-inset-bottom));
        }

        .financeProPage .subTileActions {
          align-items: center;
        }


        .financeProPage .financeNotifyTargetBox {
          display: grid;
          gap: 7px;
        }

        .financeProPage .financeNotifyMemberList {
          display: grid;
          gap: 6px;
          padding: 8px;
          border-radius: 14px;
          background: rgba(148, 163, 184, 0.10);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.14);
        }

        .financeProPage .financeNotifyMemberOption {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: 30px;
          font-size: 13px;
          cursor: pointer;
        }

        .financeProPage .financeNotifyMemberOption input {
          width: 16px;
          height: 16px;
          accent-color: #22c55e;
        }

        .financeProPage .financeNotifyHint {
          font-size: 11px;
          line-height: 1.35;
          opacity: 0.72;
        }

        .dark .financeProPage .financeNotifyMemberList,
        .financeProPage.dark .financeNotifyMemberList {
          background: rgba(15, 23, 42, 0.30);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
        }

        .financeProPage .financeTabBar {
          background: transparent;
        }

        .financeProPage .financeTabBar button {
          background: rgba(148, 163, 184, 0.10);
          color: inherit;
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);
        }

        .financeProPage .financeTabBar button.active {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(16, 185, 129, 0.16));
          color: inherit;
          box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.28);
        }

        .dark .financeProPage .financeTabBar button,
        .financeProPage.dark .financeTabBar button {
          background: rgba(15, 23, 42, 0.22);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.14);
        }

        .dark .financeProPage .financeTabBar button.active,
        .financeProPage.dark .financeTabBar button.active {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.26), rgba(16, 185, 129, 0.18));
          box-shadow: inset 0 0 0 1px rgba(74, 222, 128, 0.28);
        }

        .financeProPage .financeMoreActionBtn {
          height: 32px;
          min-height: 32px;
          width: 42px;
          min-width: 42px;
          padding: 0 10px;
          border: none;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          line-height: 1;
          font-size: 18px;
          font-weight: 800;
          cursor: pointer;
          color: inherit;
          background: var(--premium-green-bg, rgba(34, 197, 94, 0.16));
          box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.24);
          -webkit-tap-highlight-color: transparent;
        }

        .financeProPage .financeMoreActionBtn:active {
          transform: scale(0.98);
        }

        .dark .financeProPage .financeMoreActionBtn,
        .financeProPage.dark .financeMoreActionBtn {
          background: rgba(34, 197, 94, 0.18);
          box-shadow: inset 0 0 0 1px rgba(74, 222, 128, 0.24);
        }

        @media (max-width: 430px) {
          .financeProPage .financeMoreActionBtn {
            height: 30px;
            min-height: 30px;
            width: 40px;
            min-width: 40px;
            padding: 0 9px;
            font-size: 17px;
          }
        }
        .financeProPage .billFeatureAmountLine {
          display: inline-flex;
          align-items: baseline;
          justify-content: center;
          gap: 5px;
          flex-wrap: wrap;
          line-height: 1.1;
        }

        .financeProPage .billFeatureSubLine {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  gap: 2px;
}
          .financeProPage .billPaidBreak {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin-left: 0;
  padding-left: 0;
}

        .financeProPage .billFeatureAmountLine .amountRed,
        .financeProPage .billFeatureSubLine .amountGreen {
          font-weight: 800;
        }

        .financeProPage .financeCompactFilterCard {
          padding: 7px 8px;
          margin-bottom: 10px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .financeProPage .financeCompactFilterCard::-webkit-scrollbar {
          display: none;
        }

        .financeProPage .financeCompactFilterHead {
          display: inline-flex;
          align-items: center;
          flex: 0 0 auto;
          margin: 0;
        }

        .financeProPage .financeCompactFilterHead .cardHeaderTitle {
          font-size: 12px;
          line-height: 1;
          white-space: nowrap;
          opacity: 0.82;
        }

        .financeProPage .financeCompactFilterBody {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          min-width: max-content;
        }

        .financeProPage .financePeriodPills {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 2px;
          border-radius: 999px;
          background: rgba(148, 163, 184, 0.14);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.14);
          flex: 0 0 auto;
        }

        .financeProPage .financePeriodPills .periodBtn {
          min-height: 28px;
          padding: 0 9px;
          border-radius: 999px;
          font-size: 11px;
          line-height: 1;
          white-space: nowrap;
        }

        .financeProPage .compactFilterInputGroup {
          min-width: 0;
          flex: 0 0 auto;
        }

        .financeProPage .compactFilterInputGroup .darkLabel {
          display: none;
        }

        .financeProPage .compactFilterInputGroup .darkInput {
          min-height: 30px;
          height: 30px;
          padding: 4px 26px 4px 9px;
          font-size: 12px;
          border-radius: 999px;
          max-width: 116px;
        }

        .financeProPage .compactFilterInputGroup:last-child .darkInput {
          min-width: 92px;
          max-width: 96px;
        }

        .financeProPage .financeBillFilterBar {
          display: flex;
          align-items: center;
          gap: 6px;
          min-height: 42px;
          margin: 0 0 10px;
          padding: 6px 8px;
          overflow-x: auto;
          overflow-y: hidden;
          border-radius: 16px;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }

        .financeProPage .financeBillFilterBar::-webkit-scrollbar {
          display: none;
        }

        .financeProPage .financeBillFilterLabel {
          flex: 0 0 auto;
          padding: 0 2px;
          color: var(--muted);
          font-size: 9.5px;
          font-weight: 850;
          letter-spacing: .08em;
          line-height: 1;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .financeProPage .financeBillFilterControl {
          min-width: 0;
          flex: 0 0 auto;
        }

        .financeProPage .financeBillFilterControl .darkInput {
          width: auto;
          min-width: 0;
          height: 30px;
          min-height: 30px;
          padding: 3px 28px 3px 10px;
          border-radius: 999px;
          font-size: 11.5px;
          line-height: 1;
        }

        .financeProPage .financeBillFilterControl.mode .darkInput {
          width: clamp(148px, 45vw, 186px);
        }

        .financeProPage .financeBillFilterControl.year .darkInput {
          width: 76px;
        }

        .financeProPage .financeBillFilterControl.month .darkInput {
          width: clamp(92px, 28vw, 116px);
        }

        .financeProPage .financeHeroCard {
          border-radius: 18px;
          padding: 15px;
          margin-bottom: 8px;
        }

        .financeProPage .financeHeroTop {
          gap: 10px;
          align-items: stretch;
        }

        .financeProPage .financeHeroTop > div:first-child {
          min-width: 0;
          flex: 0 1 auto;
        }

        .financeProPage .financeEyebrow {
          font-size: 10px;
          margin-bottom: 4px;
        }

        .financeProPage .financeHeroCard h2 {
          font-size: 25px;
          letter-spacing: -0.45px;
        }

        .financeProPage .financeHeroCard p {
          margin-top: 4px;
          font-size: 12px;
        }

        .financeProPage .financeHeroStats {
          gap: 6px;
          margin-top: 12px;
        }

        .financeProPage .financeHeroStats div {
          border-radius: 13px;
          padding: 8px;
        }

        .financeProPage .financeHeroStats span {
          font-size: 9.8px;
          margin-bottom: 3px;
        }

        .financeProPage .financeHeroStats b {
          font-size: 12.2px;
        }

        .financeProPage .financeHeroSmartMessage {
          flex: 1 1 0;
          min-width: 0;
          max-width: none;
          align-self: stretch;
          padding: 8px 10px;
          border-radius: 15px;
          display: flex;
          align-items: flex-start;
          gap: 6px;
          color: rgba(255,255,255,0.88);
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.16);
          backdrop-filter: blur(10px);
        }

        .financeProPage .financeHeroSmartMessage span {
          flex: 0 0 auto;
          font-size: 12px;
          line-height: 1.25;
        }

        .financeProPage .financeHeroSmartMessage b {
          min-width: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          font-size: 11.8px;
          line-height: 1.28;
          font-weight: 800;
          letter-spacing: -0.01em;
        }

        body[data-tema="light"] .financeProPage .financeHeroSmartMessage,
        html[data-tema="light"] .financeProPage .financeHeroSmartMessage {
          color: #24314f;
          background: rgba(255,255,255,0.76);
          border-color: rgba(99,102,241,0.18);
          box-shadow: 0 8px 20px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.82);
        }

        .financeProPage .financeFeatureGridBeforeTabs {
          gap: 8px;
          margin-bottom: 10px;
        }

        .financeProPage .financeFeatureCard {
          min-height: 86px;
          padding: 11px 10px;
          border-radius: 17px;
        }

        .financeProPage .financeFeatureCard .featureIcon {
          font-size: 19px;
          margin-bottom: 5px;
        }

        .financeProPage .financeFeatureCard .financeFeatureAsset {
          width: 40px;
          height: 40px;
          margin-bottom: 6px;
          border-radius: 0;
        }

        .financeProPage .financeFeatureCard small {
          font-size: 10px;
        }

        .financeProPage .financeFeatureCard strong {
          font-size: 15px;
        }

        .financeProPage .financeFeatureCard em {
          font-size: 10.5px;
          line-height: 1.25;
        }

        .financeProPage .financeSplitList {
          overflow: visible !important;
          border-radius: var(--r-lg);
        }
        .financeProPage .financeScrollSub {
          --finance-visible-row-count: 5;
          --finance-row-visible-height: 60px;
          max-height: calc(var(--finance-visible-row-count) * var(--finance-row-visible-height));
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          touch-action: pan-y;
          min-height: 0;
          transition: max-height 0.28s cubic-bezier(0.4,0,0.2,1);
        }
        .financeProPage .financeScrollSub.genisletildi {
          --finance-visible-row-count: 10;
        }
        .financeProPage .financeScrollSub {
          scrollbar-width: none;
        }
        .financeProPage .financeScrollSub::-webkit-scrollbar {
          display: none;
        }
        .financeProPage .financeListeBaslik {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: background 0.15s;
        }
        .financeProPage .financeListeBaslik:active {
          background: rgba(139, 92, 246, 0.07);
        }
        .financeProPage .financeListeBtn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          flex-shrink: 0;
          font-size: 14px;
          transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), background 0.15s;
          background: linear-gradient(135deg, rgba(16,185,129,0.18), rgba(5,150,105,0.12));
          color: #34d399;
        }
        .financeProPage .financeListeBtn.gider {
          background: linear-gradient(135deg, rgba(239,68,68,0.18), rgba(220,38,38,0.12));
          color: #f87171;
        }
        .financeProPage .financeListeBtn.acik {
          transform: rotate(180deg);
        }
        body[data-tema="light"] .financeProPage .financeListeBtn {
          background: linear-gradient(135deg, rgba(16,185,129,0.14), rgba(5,150,105,0.10));
          color: #059669;
        }
        body[data-tema="light"] .financeProPage .financeListeBtn.gider {
          background: linear-gradient(135deg, rgba(239,68,68,0.14), rgba(220,38,38,0.10));
          color: #dc2626;
        }

        .financeProPage .financeSplitList::-webkit-scrollbar {
          width: 5px;
        }

        .financeProPage .financeSplitList::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.36);
          border-radius: 999px;
        }

        .financeProPage .financeProRow {
          min-height: 58px;
          padding: 9px 10px;
        }

        .financeProPage .tableRowMain strong,
        .financeProPage .tableRowAmount,
        .financeProPage .tableRowActions button {
          font-size: 12.2px;
        }

        .financeProPage .tableRowSub,
        .financeProPage .tableCardHeader,
        .financeProPage .emptyState.compact {
          font-size: 11.2px;
        }

        .financeProPage .familyExpensePanel {
          border-radius: 18px;
          padding: 13px;
          margin-bottom: 9px;
        }

        .financeProPage .familyExpenseHeader {
          gap: 10px;
          align-items: flex-start;
        }

        .financeProPage .familyExpenseHeader h3 {
          font-size: 23px;
          letter-spacing: -0.4px;
        }



        .financeProPage .unifiedPremiumAddCard {
          position: relative;
          margin: 6px 0;
          border-radius: 14px;
          overflow: hidden;
          background: var(--card-bg, rgba(255,255,255,0.90));
          border: 1px solid var(--border-color, rgba(148, 163, 184, 0.20));
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
        }

        .financeProPage .unifiedPremiumAddHeader {
          width: 100%;
          min-height: 42px;
          padding: 5px 8px;
          border: 0;
          background: transparent;
          display: grid;
          grid-template-columns: 26px 1fr 18px;
          align-items: center;
          gap: 7px;
          text-align: left;
          color: var(--text-main, #0f172a);
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }

        .financeProPage .unifiedPremiumAddIcon {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #1e3a8a;
          color: #fff;
          font-size: 16px;
          font-weight: 800;
          line-height: 1;
          box-shadow: 0 5px 10px rgba(30, 58, 138, 0.18);
        }

        .financeProPage .unifiedPremiumAddTitle {
          min-width: 0;
          color: var(--text-main, #0f172a);
          font-size: 14px;
          font-weight: 850;
          line-height: 1.15;
          letter-spacing: -0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .financeProPage .unifiedPremiumAddArrow {
          color: #3b82f6;
          font-size: 16px;
          font-weight: 900;
          text-align: center;
          line-height: 1;
        }

        .financeProPage .unifiedPremiumAddCard .formBody {
          padding: 0 8px 8px;
          margin-top: 0;
        }

        body.dark .financeProPage .unifiedPremiumAddCard,
        [data-theme="dark"] .financeProPage .unifiedPremiumAddCard,
        body[data-theme="dark"] .financeProPage .unifiedPremiumAddCard,
        body[data-tema="dark"] .financeProPage .unifiedPremiumAddCard {
          background: rgba(15, 23, 42, 0.72) !important;
          border-color: rgba(148, 163, 184, 0.18) !important;
          box-shadow: 0 14px 28px rgba(0,0,0,0.24), inset 0 0 0 1px rgba(255,255,255,0.04) !important;
        }

        body.dark .financeProPage .unifiedPremiumAddTitle,
        [data-theme="dark"] .financeProPage .unifiedPremiumAddTitle,
        body[data-theme="dark"] .financeProPage .unifiedPremiumAddTitle,
        body[data-tema="dark"] .financeProPage .unifiedPremiumAddTitle {
          color: #f8fafc !important;
        }

        @media (max-width: 390px) {
          .financeProPage .unifiedPremiumAddHeader { grid-template-columns: 24px 1fr 18px; min-height: 40px; padding: 5px 8px; }
          .financeProPage .unifiedPremiumAddIcon { width: 24px; height: 24px; font-size: 15px; }
          .financeProPage .unifiedPremiumAddTitle { font-size: 13.5px; }
        }

        .financeProPage .familyExpenseHeader p {
          font-size: 11.8px;
          line-height: 1.3;
          margin-top: 4px;
        }

        .financeProPage .familyExpenseHeader .smallPremiumBtn {
          min-height: 32px;
          padding: 0 10px;
          font-size: 11.2px;
          border-radius: 999px;
        }

        .financeProPage .familyExpenseGrid {
          gap: 6px;
          margin-top: 10px;
        }

        .financeProPage .familyExpenseGrid div {
          border-radius: 13px;
          padding: 8px;
        }

        .financeProPage .familyExpenseGrid span,
        .financeProPage .miniBarItem span,
        .financeProPage .compactDebtList span {
          font-size: 10.5px;
        }

        .financeProPage .familyExpenseGrid b,
        .financeProPage .miniBarItem b {
          font-size: 12.2px;
        }

        .financeProPage .financeMainFormCard {
          flex-shrink: 0;
          padding: 10px 12px;
          margin-bottom: 8px;
          border-radius: 16px;
          overflow: hidden;
        }

        .financeProPage .financeMainFormCard .cardHeader {
          padding: 0;
          min-height: 0;
        }

        .financeProPage .financeMainFormCard .cardHeaderTitle {
          font-size: 14px;
        }

        .financeProPage .financeMainFormCard .miniToggle {
          width: 28px;
          height: 28px;
          font-size: 16px;
        }

        .financeProPage .financeMainFormCard .formBody {
          padding: 0;
          margin-top: 8px;
          gap: 7px;
        }

        .financeProPage .financeMainFormCard .formRow {
          display: flex;
          gap: 7px;
          align-items: flex-end;
        }

        .financeProPage .financeMainFormCard .darkLabel {
          font-size: 9px;
          margin-bottom: 3px;
          letter-spacing: 0.25px;
        }

        .financeProPage .financeMainFormCard .darkInput {
          min-height: 40px;
          height: 40px;
          padding: 10px 11px;
          border-radius: 12px;
          font-size: 13px;
        }

        .financeProPage .financeMainFormCard .formRow .inputLabelGroup input.darkInput,
        .financeProPage .financeMainFormCard .formRow .inputLabelGroup select.darkInput {
          height: 40px;
          min-height: 40px;
          padding-top: 0;
          padding-bottom: 0;
        }

        .financeProPage .financeMainFormCard .primaryButton {
          min-height: 40px;
          padding: 10px 12px;
          border-radius: 13px;
          font-size: 13px;
          margin-top: 2px;
        }

        .financeProPage .financeSplitList {
          margin-bottom: 0;
        }

        .financeProPage .financeListRevealWrap {
          display: none;
        }


        .financeProPage.financeReportMode {
          padding-bottom: max(14px, env(safe-area-inset-bottom));
          overflow: auto;
        }

        .financeProPage .financeReportPanel {
          display: grid;
          grid-template-rows: auto auto 1fr;
          gap: 12px;
          min-height: 0;
          overflow: visible;
        }

        .financeProPage .financeReportSummaryRow {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 7px;
          min-width: 0;
        }

        .financeProPage .financeReportSummaryCard {
          min-width: 0;
          padding: 8px 9px;
          border-radius: 15px;
          overflow: hidden;
        }

        .financeProPage .financeReportSummaryCard span {
          display: block;
          font-size: 10.4px;
          line-height: 1.15;
          opacity: 0.74;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .financeProPage .financeReportSummaryCard b {
          display: block;
          margin-top: 3px;
          font-size: 12.2px;
          line-height: 1.15;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .financeProPage .financeTabBar {
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: minmax(88px, 1fr);
          gap: 6px;
          padding: 5px;
          margin: 16px 0 18px;
          border-radius: 20px;
          overflow-x: auto;
          background: linear-gradient(180deg, rgba(255,255,255,0.26), rgba(148, 163, 184, 0.12));
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.20), 0 10px 22px rgba(15, 23, 42, 0.08);
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }

        .financeProPage .financeTabBar::-webkit-scrollbar {
          display: none;
        }

        .financeProPage .financeTabBar button,
        .financeProPage .financeTabBar button:not(.active),
        .financeProPage .financeTabBar button:not(.active):focus,
        .financeProPage .financeTabBar button:not(.active):active {
          min-height: 34px;
          padding: 0 12px;
          border: 0 !important;
          border-radius: 15px;
          background: rgba(255,255,255,0.34) !important;
          color: var(--text-primary, rgba(30, 41, 59, 0.92)) !important;
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12) !important;
          outline: none !important;
          font-size: 13.4px;
          font-weight: 900;
          white-space: nowrap;
          -webkit-tap-highlight-color: transparent;
          appearance: none;
        }

        .financeProPage .financeTabBar button.active,
        .financeProPage .financeTabBar button.active:focus,
        .financeProPage .financeTabBar button.active:active {
          background: linear-gradient(135deg, var(--primary, #7c3aed), var(--primary-2, #a855f7)) !important;
          color: #fff !important;
          box-shadow: 0 12px 24px rgba(124, 58, 237, 0.30) !important;
        }

        .dark .financeProPage .financeTabBar,
        .financeProPage.dark .financeTabBar {
          background: linear-gradient(180deg, rgba(30, 41, 59, 0.76), rgba(15, 23, 42, 0.48));
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.18), 0 12px 26px rgba(0, 0, 0, 0.12);
        }

        .dark .financeProPage .financeTabBar button:not(.active),
        .dark .financeProPage .financeTabBar button:not(.active):focus,
        .dark .financeProPage .financeTabBar button:not(.active):active,
        .financeProPage.dark .financeTabBar button:not(.active),
        .financeProPage.dark .financeTabBar button:not(.active):focus,
        .financeProPage.dark .financeTabBar button:not(.active):active {
          color: rgba(241, 245, 249, 0.86) !important;
          background: rgba(255, 255, 255, 0.075) !important;
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12) !important;
        }

        .dark .financeProPage .financeTabBar button.active,
        .dark .financeProPage .financeTabBar button.active:focus,
        .dark .financeProPage .financeTabBar button.active:active,
        .financeProPage.dark .financeTabBar button.active,
        .financeProPage.dark .financeTabBar button.active:focus,
        .financeProPage.dark .financeTabBar button.active:active {
          background: linear-gradient(135deg, var(--primary, #7c3aed), var(--primary-2, #a855f7)) !important;
          color: #fff !important;
          box-shadow: 0 12px 24px rgba(124, 58, 237, 0.34) !important;
        }

        .financeProPage .financeTabBar button,
        .financeProPage .financeTabBar button *,
        .financeProPage .financeTabBar button::before,
        .financeProPage .financeTabBar button::after {
          background-clip: padding-box;
          text-decoration: none !important;
        }

        .financeProPage .financeCategoryChips {
          flex-shrink: 0;
          gap: 6px;
          padding: 2px 0 8px;
          margin: 8px 0 10px;
          overflow-x: auto;
          scrollbar-width: none;
        }

        .financeProPage .financeCategoryChips::-webkit-scrollbar {
          display: none;
        }

        .financeProPage .financeCategoryChips .chip,
        .financeProPage .financeCategoryChips .chip:focus,
        .financeProPage .financeCategoryChips .chip:active {
          min-height: 31px;
          padding: 6px 11px;
          border: 1.5px solid var(--border);
          border-radius: var(--r-full);
          background: var(--bg-elevated);
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 800;
          line-height: 1.1;
          white-space: nowrap;
          box-shadow: none;
          outline: none;
          appearance: none;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
        }

        .financeProPage .financeCategoryChips .chip:hover {
          background: var(--bg-overlay);
          color: var(--text-primary);
          border-color: var(--border-hover);
        }

        .financeProPage .financeCategoryChips .chip.activeBlue,
        .financeProPage .financeCategoryChips .chip.activeBlue:focus,
        .financeProPage .financeCategoryChips .chip.activeBlue:active {
          background: var(--brand-grad) !important;
          color: #fff !important;
          border-color: transparent !important;
          box-shadow: 0 4px 14px rgba(99,102,241,0.35) !important;
        }

        .dark .financeProPage .financeCategoryChips .chip:not(.activeBlue),
        .financeProPage.dark .financeCategoryChips .chip:not(.activeBlue) {
          background: var(--bg-elevated);
          color: var(--text-secondary);
          border-color: var(--border);
        }

        .financeProPage .financeReportChartCard,
        .financeProPage .financeReportMiniCard {
          padding: 12px;
          border-radius: 18px;
          overflow: hidden;
          position: relative;
        }

        .financeProPage .financeReportChartCard::before,
        .financeProPage .financeReportInsightCard::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(circle at 12% 0%, rgba(124, 58, 237, 0.14), transparent 34%), radial-gradient(circle at 88% 18%, rgba(34, 197, 94, 0.10), transparent 30%);
        }

        .financeProPage .financeReportPanel .cardHeader {
          margin-bottom: 8px;
          min-height: 0;
          position: relative;
          z-index: 1;
        }

        .financeProPage .financeReportPanel .cardHeaderTitle {
          font-size: 14px;
          line-height: 1.15;
          font-weight: 900;
        }

        .financeProPage .financeReportChartHeader {
          align-items: flex-start;
          gap: 10px;
        }

        .financeProPage .financeReportChartHeader p {
          margin: 4px 0 0;
          font-size: 12px;
          line-height: 1.25;
          opacity: 0.72;
        }

        .financeProPage .financeReportLegend {
          display: inline-flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 6px 8px;
          flex: 0 0 auto;
          max-width: 52%;
          font-size: 12px;
          line-height: 1.1;
          opacity: 0.92;
          font-weight: 800;
        }

        .financeProPage .financeReportLegend span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .financeProPage .financeReportLegend i {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          display: inline-block;
          box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.12);
        }

        .financeProPage .financeReportLegend .incomeDot {
          background: var(--success, #22c55e);
        }

        .financeProPage .financeReportLegend .expenseDot {
          background: var(--danger, #ef4444);
        }

        .financeProPage .financeReportLegend .netDot {
          background: var(--primary, #7c3aed);
        }

        .financeProPage .premiumTrendChart {
          position: relative;
          z-index: 1;
          height: clamp(190px, 28dvh, 245px);
          min-height: 190px;
          border-radius: 22px;
          padding: 10px 8px 8px;
          background: linear-gradient(180deg, rgba(148, 163, 184, 0.14), rgba(15, 23, 42, 0.05));
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.14);
          touch-action: pan-y;
          user-select: none;
        }

        .financeProPage .premiumTrendSvg {
          width: 100%;
          height: 100%;
          display: block;
          overflow: visible;
        }

        .financeProPage .chartGridLine {
          stroke: rgba(148, 163, 184, 0.20);
          stroke-width: 1;
          stroke-dasharray: 3 5;
        }

        .financeProPage .chartDayLabel {
          fill: var(--text-muted, rgba(100, 116, 139, 0.86));
          font-size: 10.5px;
          font-weight: 800;
          dominant-baseline: middle;
        }

        .dark .financeProPage .chartDayLabel,
        .financeProPage.dark .chartDayLabel {
          fill: rgba(226, 232, 240, 0.70);
        }

        .financeProPage .chartActiveGuide {
          stroke: rgba(148, 163, 184, 0.42);
          stroke-width: 1.25;
          stroke-dasharray: 5 5;
        }

        .financeProPage .incomeArea {
          fill: rgba(34, 197, 94, 0.16);
        }

        .financeProPage .expenseArea {
          fill: rgba(239, 68, 68, 0.12);
        }

        .financeProPage .incomeLine,
        .financeProPage .expenseLine,
        .financeProPage .netLine {
          fill: none;
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          filter: drop-shadow(0 5px 8px rgba(15, 23, 42, 0.12));
        }

        .financeProPage .incomeLine {
          stroke: var(--success, #22c55e);
        }

        .financeProPage .expenseLine {
          stroke: var(--danger, #ef4444);
        }

        .financeProPage .netLine {
          stroke: var(--primary, #7c3aed);
          stroke-width: 3;
          stroke-dasharray: 7 6;
        }

        .financeProPage .chartPoint {
          stroke: var(--card-bg, #fff);
          stroke-width: 3;
          filter: drop-shadow(0 4px 6px rgba(15, 23, 42, 0.20));
        }

        .financeProPage .chartPoint.active {
          stroke-width: 4;
        }

        .financeProPage .chartPoint.income {
          fill: var(--success, #22c55e);
        }

        .financeProPage .chartPoint.expense {
          fill: var(--danger, #ef4444);
        }

        .financeProPage .chartPoint.net {
          fill: var(--primary, #7c3aed);
        }

        .financeProPage .chartTooltip {
          position: absolute;
          top: 16px;
          transform: translateX(-50%);
          min-width: 128px;
          padding: 9px 10px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.94);
          color: #0f172a;
          box-shadow: 0 16px 34px rgba(15, 23, 42, 0.20), inset 0 0 0 1px rgba(148, 163, 184, 0.20);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          pointer-events: none;
          z-index: 3;
        }

        .dark .financeProPage .chartTooltip,
        .financeProPage.dark .chartTooltip {
          background: rgba(15, 23, 42, 0.94);
          color: #f8fafc;
          box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28), inset 0 0 0 1px rgba(148, 163, 184, 0.18);
        }

        .financeProPage .chartTooltip strong {
          display: block;
          margin-bottom: 6px;
          font-size: 12px;
          line-height: 1.1;
          font-weight: 900;
        }

        .financeProPage .chartTooltip span {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          font-size: 11px;
          line-height: 1.35;
          font-weight: 800;
        }

        .financeProPage .chartTooltip b {
          color: inherit;
          font-weight: 950;
        }

        .financeProPage .chartTooltip i {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          display: inline-block;
          margin-right: 4px;
          flex: 0 0 auto;
        }

        .financeProPage .chartTooltip .incomeDot { background: var(--success, #22c55e); }
        .financeProPage .chartTooltip .expenseDot { background: var(--danger, #ef4444); }
        .financeProPage .chartTooltip .netDot { background: var(--primary, #7c3aed); }

        .financeProPage .financeBarFocus {
          fill: rgba(124, 58, 237, .09);
          stroke: rgba(124, 58, 237, .16);
          stroke-width: 1;
        }
        .financeProPage .financeIncomeBar {
          fill: url(#financeIncomeBar);
          filter: drop-shadow(0 5px 7px rgba(5, 150, 105, .22));
        }
        .financeProPage .financeExpenseBar {
          fill: url(#financeExpenseBar);
          filter: drop-shadow(0 5px 7px rgba(225, 29, 72, .20));
        }
        .financeProPage .financeBarGroup { opacity: .86; transition: opacity 180ms ease; }
        .financeProPage .financeBarGroup.active { opacity: 1; }

        .financeProPage .financeBreakdownCard { touch-action: pan-y; align-self: start; isolation: isolate; }
        .financeProPage .financeBreakdownCard::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          opacity: .7;
          transition: opacity 280ms ease, background 280ms ease;
        }
        .financeProPage .financeBreakdownCard > * { position: relative; z-index: 1; }
        .financeProPage .financeBreakdownCard.incomeMode::before {
          background: radial-gradient(circle at 92% 0%, rgba(16,185,129,.13), transparent 34%);
        }
        .financeProPage .financeBreakdownCard.expenseMode::before {
          background: radial-gradient(circle at 92% 0%, rgba(244,63,94,.10), transparent 34%);
        }
        .financeProPage .financeBreakdownHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .financeProPage .financeBreakdownHeader .cardHeaderTitle {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .financeProPage .financeBreakdownTypeSwitch {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 2px;
          padding: 3px;
          border: 1px solid rgba(148,163,184,.16);
          border-radius: 11px;
          background: rgba(15,23,42,.20);
          box-shadow: inset 0 1px 2px rgba(2,6,23,.12);
        }
        .financeProPage .financeBreakdownTypeSwitch button {
          min-width: 48px;
          min-height: 25px;
          padding: 4px 8px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: var(--muted);
          font-size: 9.5px;
          line-height: 1;
          font-weight: 900;
          cursor: pointer;
          transition: color 180ms ease, background 220ms ease, box-shadow 220ms ease, transform 180ms ease;
        }
        .financeProPage .financeBreakdownTypeSwitch button:active { transform: scale(.96); }
        .financeProPage .financeBreakdownTypeSwitch button.active { color: #fff; }
        .financeProPage .financeBreakdownTypeSwitch button.active.income {
          background: linear-gradient(135deg,#10b981,#059669);
          box-shadow: 0 5px 12px rgba(5,150,105,.25);
        }
        .financeProPage .financeBreakdownTypeSwitch button.active.expense {
          background: linear-gradient(135deg,#f43f5e,#e11d48);
          box-shadow: 0 5px 12px rgba(225,29,72,.22);
        }
        .financeProPage .financeBreakdownViewport {
          display: block;
          width: 100%;
          min-height: 190px;
          overflow: hidden;
        }
        .financeProPage .financeBreakdownSlide {
          width: 100%;
          min-height: 190px;
          padding: 2px 1px 4px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          animation: financeBreakdownEnter 380ms cubic-bezier(.22, .8, .3, 1) both;
        }
        .financeProPage .financeBreakdownSlide > .miniBarList,
        .financeProPage .financeBreakdownSlide > .emptyState { width: 100%; }
        @keyframes financeBreakdownEnter {
          from { opacity: 0; transform: translateX(18px) scale(.985); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .financeProPage .financeBreakdownDots {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 7px;
          margin-top: 9px;
        }
        .financeProPage .financeBreakdownDots button {
          width: 7px;
          height: 7px;
          padding: 0;
          border: 0;
          border-radius: 999px;
          background: rgba(148, 163, 184, .38);
          transition: width 240ms ease, background-color 240ms ease, box-shadow 240ms ease;
        }
        .financeProPage .financeBreakdownDots button.active {
          width: 22px;
          background: var(--primary, #7c3aed);
          box-shadow: 0 4px 12px rgba(124, 58, 237, .34);
        }
        .financeProPage .financeBreakdownPieLayout {
          display: grid;
          grid-template-columns: minmax(105px, .8fr) minmax(0, 1.35fr);
          align-items: center;
          gap: 12px;
          min-height: 150px;
          width: min(100%, 390px);
          margin-inline: auto;
          justify-content: center;
        }
        .financeProPage .financeBreakdownDonut {
          position: relative;
          width: 116px;
          max-width: 100%;
          aspect-ratio: 1;
          margin: 0 auto;
        }
        .financeProPage .financeBreakdownDonut svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
          filter: drop-shadow(0 10px 18px rgba(15, 23, 42, .12));
        }
        .financeProPage .financeBreakdownDonut circle { fill: none; stroke-width: 13; }
        .financeProPage .financeBreakdownDonut .donutTrack { stroke: rgba(148, 163, 184, .16); }
        .financeProPage .financeBreakdownDonut .donutSegment {
          stroke-linecap: butt;
          transition: stroke-dasharray 520ms ease, stroke-dashoffset 520ms ease;
        }
        .financeProPage .financeBreakdownDonut > div {
          position: absolute;
          inset: 23%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        .financeProPage .financeBreakdownDonut strong { font-size: 17px; font-weight: 950; }
        .financeProPage .financeBreakdownDonut span {
          max-width: 72px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 9px;
          color: var(--muted);
          font-weight: 750;
        }
        .financeProPage .financeBreakdownLegend { display: grid; gap: 7px; }
        .financeProPage .financeBreakdownLegend > div {
          display: grid;
          grid-template-columns: 8px minmax(0, 1fr) auto;
          align-items: center;
          gap: 7px;
          min-width: 0;
          font-size: 10.5px;
        }
        .financeProPage .financeBreakdownLegend i { width: 7px; height: 7px; border-radius: 50%; }
        .financeProPage .financeBreakdownLegend span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--muted); font-weight: 750; }
        .financeProPage .financeBreakdownLegend b { white-space: nowrap; font-size: 10.5px; font-weight: 900; }

        @media (prefers-reduced-motion: reduce) {
          .financeProPage .financeBreakdownViewport,
          .financeProPage .financeBreakdownDots button,
          .financeProPage .financeBreakdownDonut .donutSegment { transition: none; }
          .financeProPage .financeBreakdownSlide { animation: none; }
        }


        .financeProPage .financeReportSummaryCard {
          padding: 11px 10px;
          border-radius: 17px;
          gap: 5px;
          background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(148, 163, 184, 0.05));
        }

        .financeProPage .financeReportSummaryCard span {
          font-size: 11.6px;
          font-weight: 800;
          opacity: 0.76;
        }

        .financeProPage .financeReportSummaryCard b {
          font-size: 14.2px;
          line-height: 1.1;
          letter-spacing: -0.2px;
        }

        .financeProPage .financeReportGridCompact {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          min-height: 0;
          overflow: visible;
        }

        .financeProPage .financeReportMiniCard .miniBarItem {
          min-height: 30px;
          gap: 8px;
          padding: 5px 0;
        }

        .financeProPage .financeReportMiniCard .miniBarItem span,
        .financeProPage .financeReportMiniCard .miniBarItem b {
          font-size: 12.2px;
          line-height: 1.2;
          font-weight: 800;
        }

        .financeProPage .financeReportInsightCard {
          background: linear-gradient(135deg, rgba(124, 58, 237, 0.13), rgba(34, 197, 94, 0.08));
        }

        .financeProPage .financeReportInsightCard p {
          position: relative;
          z-index: 1;
          margin: 0;
          font-size: 12.8px;
          line-height: 1.45;
          opacity: 0.88;
          font-weight: 700;
        }

        .financeProPage .billCarryoverSection {
          margin: 0 0 12px;
          padding: 12px;
          overflow: hidden;
          border-radius: 20px;
          background: linear-gradient(135deg, rgba(245,158,11,0.14), rgba(239,68,68,0.09));
          box-shadow: inset 0 0 0 1px rgba(251,191,36,0.20), 0 12px 28px rgba(2,6,23,0.10);
        }

        .financeProPage .billCarryoverHeader,
        .financeProPage .billCurrentPeriodHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .financeProPage .billCarryoverTitleGroup {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .financeProPage .billCarryoverIcon {
          width: 34px;
          height: 34px;
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          color: #fbbf24;
          background: rgba(245,158,11,0.14);
          box-shadow: inset 0 0 0 1px rgba(251,191,36,0.20);
          font-size: 16px;
        }

        .financeProPage .billCarryoverEyebrow,
        .financeProPage .billCurrentPeriodEyebrow {
          display: block;
          margin-bottom: 2px;
          color: #f59e0b;
          font-size: 10px;
          font-weight: 850;
          letter-spacing: .09em;
          text-transform: uppercase;
        }

        .financeProPage .billCarryoverHeader h3,
        .financeProPage .billCurrentPeriodHeader h3 {
          margin: 0;
          color: var(--text);
          font-size: 14px;
          line-height: 1.2;
        }

        .financeProPage .billCarryoverCount,
        .financeProPage .billCurrentPeriodCount {
          min-width: 28px;
          height: 28px;
          padding: 0 9px;
          flex: 0 0 auto;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #fbbf24;
          background: rgba(245,158,11,0.14);
          font-size: 12px;
          font-weight: 850;
        }

        .financeProPage .billCarryoverHint {
          margin: 7px 0 10px;
          color: var(--muted);
          font-size: 11px;
          line-height: 1.4;
        }

        .financeProPage .billCarryoverList {
          max-height: none;
          overflow: visible;
          padding: 0;
          margin: 0;
        }

        .financeProPage .billCarryoverList .premiumBillTile:last-child {
          margin-bottom: 0;
        }

        .financeProPage .billCarryoverList .billCarriedOver {
          border-color: rgba(245,158,11,0.28);
          box-shadow: 0 12px 26px rgba(2,6,23,0.14);
        }

        .financeProPage .billCarryoverBadge {
          width: max-content;
          max-width: 100%;
          margin-top: 3px;
          padding: 3px 7px;
          overflow: hidden;
          border-radius: 999px;
          color: #fbbf24;
          background: rgba(245,158,11,0.12);
          box-shadow: inset 0 0 0 1px rgba(251,191,36,0.20);
          font-size: 8.5px !important;
          font-weight: 850;
          line-height: 1.2;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .financeProPage .billCurrentPeriodHeader {
          margin: 2px 0 9px;
          padding: 0 2px;
        }

        .financeProPage .billCurrentPeriodEyebrow {
          color: #818cf8;
        }

        .financeProPage .billCurrentPeriodCount {
          color: #a5b4fc;
          background: rgba(99,102,241,0.12);
        }

        body[data-tema="light"] .financeProPage .billCarryoverSection,
        html[data-tema="light"] .financeProPage .billCarryoverSection {
          background: linear-gradient(135deg, rgba(255,251,235,0.98), rgba(254,242,242,0.92));
          box-shadow: inset 0 0 0 1px rgba(245,158,11,0.20), 0 12px 28px rgba(180,83,9,0.07);
        }

        body[data-tema="light"] .financeProPage .billCarryoverIcon,
        html[data-tema="light"] .financeProPage .billCarryoverIcon,
        body[data-tema="light"] .financeProPage .billCarryoverCount,
        html[data-tema="light"] .financeProPage .billCarryoverCount,
        body[data-tema="light"] .financeProPage .billCarryoverBadge,
        html[data-tema="light"] .financeProPage .billCarryoverBadge {
          color: #b45309;
        }

        .financeProPage .quickBillRailSection {
          margin: 0 0 12px;
          padding: 12px;
          overflow: hidden;
          border-radius: 20px;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(59, 130, 246, 0.08));
          box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.20), 0 10px 24px rgba(15, 23, 42, 0.05);
        }

        .financeProPage .quickBillRailHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .financeProPage .quickBillRailEyebrow {
          display: block;
          margin-bottom: 2px;
          color: #16a34a;
          font-size: 10px;
          font-weight: 850;
          letter-spacing: .09em;
          text-transform: uppercase;
        }

        .financeProPage .quickBillRailHeader h3 {
          margin: 0;
          color: var(--text);
          font-size: 14px;
          line-height: 1.2;
        }

        .financeProPage .quickBillRailCount {
          min-width: 26px;
          height: 26px;
          padding: 0 8px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #15803d;
          background: rgba(34, 197, 94, 0.14);
          font-size: 12px;
          font-weight: 850;
        }

        .financeProPage .quickBillRailHint {
          margin: 5px 0 10px;
          color: var(--muted);
          font-size: 11px;
          line-height: 1.35;
        }

        .financeProPage .quickBillRail {
          display: flex;
          gap: 9px;
          overflow-x: auto;
          padding: 1px 1px 5px;
          scroll-snap-type: x proximity;
          overscroll-behavior-inline: contain;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .financeProPage .quickBillRail::-webkit-scrollbar { display: none; }

        .financeProPage .quickBillCard {
          flex: 0 0 min(292px, calc(100vw - 58px));
          scroll-snap-align: start;
          padding: 11px;
          border-radius: 17px;
          background: var(--card);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16), 0 7px 18px rgba(15, 23, 42, 0.06);
        }

        .financeProPage .quickBillCardTop {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 9px;
        }

        .financeProPage .quickBillIcon {
          width: 32px;
          height: 32px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(34, 197, 94, 0.12);
          flex: 0 0 auto;
        }

        .financeProPage .quickBillCardTop > div {
          min-width: 0;
          display: grid;
          gap: 2px;
        }

        .financeProPage .quickBillCardTop b {
          overflow: hidden;
          color: var(--text);
          font-size: 13px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .financeProPage .quickBillCardTop span:last-child {
          color: var(--muted);
          font-size: 10px;
        }

        .financeProPage .quickBillInputs {
          display: grid;
          grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
          gap: 7px;
        }

        .financeProPage .quickBillInputs label {
          min-width: 0;
          display: grid;
          gap: 4px;
          color: var(--muted);
          font-size: 10px;
          font-weight: 700;
        }

        .financeProPage .quickBillInputs .darkInput {
          width: 100%;
          min-width: 0;
          height: 36px;
          padding: 0 8px;
          font-size: 11px;
        }

        .financeProPage .quickBillAddButton {
          width: 100%;
          min-height: 34px;
          margin-top: 8px;
          border: 0;
          border-radius: 12px;
          color: #fff;
          background: linear-gradient(135deg, #16a34a, #059669);
          font-size: 11px;
          font-weight: 850;
          cursor: pointer;
          box-shadow: 0 7px 15px rgba(5, 150, 105, 0.20);
        }

        .financeProPage .quickBillAddButton:disabled {
          opacity: .6;
          cursor: wait;
        }

        .dark .financeProPage .quickBillRailSection,
        .financeProPage.dark .quickBillRailSection {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.13), rgba(30, 64, 175, 0.10));
          box-shadow: inset 0 0 0 1px rgba(74, 222, 128, 0.17);
        }

        @media (max-height: 760px) {
          .financeProPage .financeReportPanel {
            gap: 7px;
          }

          .financeProPage .financeReportSummaryRow {
            gap: 6px;
          }

          .financeProPage .financeReportSummaryCard {
            padding: 9px 8px;
            border-radius: 15px;
          }

          .financeProPage .financeReportSummaryCard span {
            font-size: 10.8px;
          }

          .financeProPage .financeReportSummaryCard b {
            font-size: 13px;
          }

          .financeProPage .financeReportChartCard,
          .financeProPage .financeReportMiniCard {
            padding: 9px 9px;
            border-radius: 15px;
          }

          .financeProPage .premiumTrendChart {
            height: 176px;
            min-height: 176px;
          }

          .financeProPage .financeReportPanel .cardHeaderTitle {
            font-size: 13px;
          }

          .financeProPage .financeReportMiniCard .miniBarItem {
            min-height: 26px;
          }

          .financeProPage .financeReportInsightCard p {
            font-size: 12px;
            line-height: 1.35;
          }
        }

        @media (max-width: 560px) {
          .financeProPage .financeCompactFilterCard {
            padding: 6px 7px;
            gap: 6px;
          }

          .financeProPage .financeCompactFilterHead .cardHeaderTitle {
            font-size: 11px;
          }

          .financeProPage .financePeriodPills .periodBtn {
            min-height: 28px;
            padding: 0 8px;
          }

          .financeProPage .compactFilterInputGroup .darkInput {
            max-width: 104px;
            font-size: 11px;
          }

          .financeProPage .compactFilterInputGroup:last-child .darkInput {
            min-width: 88px;
            max-width: 92px;
          }

          .financeProPage .financeHeroTop {
            flex-wrap: wrap;
          }

          .financeProPage .financeHeroTop > div:first-child,
          .financeProPage .financeHeroSmartMessage {
            flex: 1 1 100%;
            width: 100%;
          }

          .financeProPage .familyExpenseHeader {
            flex-direction: column;
          }

          .financeProPage .familyExpenseHeader .smallPremiumBtn {
            width: 100%;
          }

          .financeProPage .financeMainFormCard .formRow {
            grid-template-columns: 1fr 1fr;
          }
        }

        /* Mobile resilience: keep every finance tool usable without clipping. */
        .financeProPage .financeTabBar {
          display: flex;
          width: 100%;
          max-width: 100%;
          gap: 7px;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 3px 2px 8px;
          scroll-padding-inline: 2px;
          scroll-snap-type: x mandatory;
          overscroll-behavior-inline: contain;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .financeProPage .financeTabBar::-webkit-scrollbar { display: none; }
        .financeProPage .financeTabBar button { flex: 0 0 auto; scroll-snap-align: start; }
        .financeProPage .financeTabBar button:last-child {
          scroll-snap-align: end;
          margin-inline-end: 2px;
        }
        .financeProPage {
          min-width: 0;
          overflow-x: clip;
          scroll-padding-top: calc(var(--sa-top, env(safe-area-inset-top, 0px)) + 84px);
        }
        .financeProPage :is(.financeHeroCard, .financeCompactFilterCard, .financeReportPanel, .financeSavingsSection) {
          scroll-margin-top: calc(var(--sa-top, env(safe-area-inset-top, 0px)) + 84px);
        }
        .financeProPage :is(.financeHeroCard h2, .financeFeatureCard strong, .financeFeatureCard em, .financeHeroStats b) {
          min-width: 0;
          max-width: 100%;
          overflow-wrap: anywhere;
          font-variant-numeric: tabular-nums;
        }
        .financeProPage .financeFeatureCard strong {
          font-size: clamp(12.5px, 3.35vw, 15px);
          line-height: 1.12;
        }
        .financeProPage .financeFeatureCard em {
          display: block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .financeProPage .financePeriodPills .periodBtn.active {
          color: #fff !important;
        }
        .financeProPage :is(.darkInput, .assetUpdateInput)::placeholder {
          color: rgba(100, 116, 139, .78);
          opacity: 1;
        }
        .financeProPage .amountNeutral,
        .financeProPage .financeZeroAmount {
          color: var(--text-main, #0f172a) !important;
        }
        .financeProPage .chartGridLine { stroke: rgba(100, 116, 139, .30); }
        body.dark .financeProPage .chartGridLine,
        [data-theme="dark"] .financeProPage .chartGridLine,
        body[data-tema="dark"] .financeProPage .chartGridLine { stroke: rgba(203, 213, 225, .24); }
        body.dark .financeProPage :is(.emptyState, .financeReportInsightCard p, .financeBreakdownLegend span),
        [data-theme="dark"] .financeProPage :is(.emptyState, .financeReportInsightCard p, .financeBreakdownLegend span),
        body[data-tema="dark"] .financeProPage :is(.emptyState, .financeReportInsightCard p, .financeBreakdownLegend span) {
          color: rgba(226, 232, 240, .76) !important;
          opacity: 1;
        }
        body.dark .financeProPage .financeHeroSmartMessage,
        [data-theme="dark"] .financeProPage .financeHeroSmartMessage,
        body[data-tema="dark"] .financeProPage .financeHeroSmartMessage {
          color: rgba(248, 250, 252, .92);
          background: rgba(15, 23, 42, .62);
          border-color: rgba(148, 163, 184, .24);
        }
        body.dark .financeProPage :is(.automationOptionRow, .financeNotifyPanel),
        [data-theme="dark"] .financeProPage :is(.automationOptionRow, .financeNotifyPanel),
        body[data-tema="dark"] .financeProPage :is(.automationOptionRow, .financeNotifyPanel) {
          color: rgba(241, 245, 249, .90);
          background: rgba(30, 41, 59, .72);
          border-color: rgba(148, 163, 184, .22);
        }
        body.dark .financeProPage :is(.assetTileTitle, .tableRowTitle, .cardHeaderTitle),
        [data-theme="dark"] .financeProPage :is(.assetTileTitle, .tableRowTitle, .cardHeaderTitle),
        body[data-tema="dark"] .financeProPage :is(.assetTileTitle, .tableRowTitle, .cardHeaderTitle) {
          color: #f8fafc !important;
        }
        body.dark .financeProPage .amountNeutral,
        body.dark .financeProPage .financeZeroAmount,
        [data-theme="dark"] .financeProPage .amountNeutral,
        [data-theme="dark"] .financeProPage .financeZeroAmount,
        body[data-tema="dark"] .financeProPage .amountNeutral,
        body[data-tema="dark"] .financeProPage .financeZeroAmount {
          color: rgba(241, 245, 249, .90) !important;
        }
        .financeProPage :is(.assetTileTitle, .tableRowTitle, .quickBillCardTop b) {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .financeProPage :is(.assetMetricGrid, .installmentMetrics) > div { min-width: 0; }
        .financeProPage :is(.assetMetricGrid, .installmentMetrics) b,
        .financeProPage :is(.assetMetricGrid, .installmentMetrics) small {
          max-width: 100%;
          overflow-wrap: anywhere;
        }
        .dark .financeProPage :is(.darkLabel, .tableRowSub, .assetTileSub, .financeEyebrow, .financeReportChartHeader p),
        .financeProPage.dark :is(.darkLabel, .tableRowSub, .assetTileSub, .financeEyebrow, .financeReportChartHeader p),
        body[data-tema="dark"] .financeProPage :is(.darkLabel, .tableRowSub, .assetTileSub, .financeEyebrow, .financeReportChartHeader p) {
          color: rgba(226, 232, 240, .78);
          opacity: 1;
        }
        .dark .financeProPage .darkInput::placeholder,
        .financeProPage.dark .darkInput::placeholder,
        body[data-tema="dark"] .financeProPage .darkInput::placeholder {
          color: rgba(203, 213, 225, .68);
          opacity: 1;
        }
        .dark .financeProPage input[type="date"],
        .financeProPage.dark input[type="date"],
        body[data-tema="dark"] .financeProPage input[type="date"] { color-scheme: dark; }

        @media (max-width: 430px) {
          .financeProPage .financeToolEmptyPremium { min-height: 132px; padding: 14px 16px; }
          .financeProPage .financeToolEmptyPremiumImage { width: 54px; height: 54px; }
          .financeProPage :is(.formRow, .financeMainFormCard .formRow, .financeDebtTwoColumn, .quickBillInputs) {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            width: 100%;
          }
          .financeProPage .formRow > *,
          .financeProPage .financeDebtTwoColumn > * {
            width: 100% !important;
            min-width: 0 !important;
            max-width: none !important;
          }
          .financeProPage .financeReportSummaryRow,
          .financeProPage .assetMetricGrid,
          .financeProPage .installmentMetrics {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .financeProPage .quickBillRail { scroll-padding-inline: 1px; }
          .financeProPage .quickBillCard { flex-basis: min(86vw, 280px); width: min(86vw, 280px); }
          .financeProPage .unifiedPremiumAddHeader {
            grid-template-columns: 34px minmax(0, 1fr) 24px;
            gap: 8px;
          }
          .financeProPage .financeSubHero { align-items: stretch; flex-direction: column; }
          .financeProPage .financeSubHero .smallPremiumBtn { width: 100%; }
          .financeProPage .financeFeatureGrid4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .financeProPage .financeFeatureCard { min-width: 0; overflow: hidden; }
          .financeProPage .familyExpenseGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .financeProPage .familyExpenseGrid b { overflow-wrap: anywhere; }
          .financeProPage .financeCompactFilterCard {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            overflow: visible;
            padding: 10px;
          }
          .financeProPage .financeCompactFilterBody {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            min-width: 0;
            width: 100%;
          }
          .financeProPage .financePeriodPills {
            grid-column: 1 / -1;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .financeProPage .financePeriodPills .periodBtn,
          .financeProPage .compactFilterInputGroup .darkInput,
          .financeProPage .compactFilterInputGroup:last-child .darkInput {
            width: 100%;
            min-width: 0;
            max-width: none;
          }
        }

      `}</style>
      {aktifSekme === "genel" && (
        <FinansOzetKarti
          toplamGelir={toplamGelir} toplamGider={toplamGider}
          netBakiye={netBakiye} gelirYuzde={gelirYuzde}
          paraFormatla={financeMoneyFormat}
          oncekiDonemNet={oncekiDonemNet}
          enYogunKategori={enYogunKategori}
          islemSayisi={finansFiltreli.length}
          ortakHarcamaToplami={ortakToplam}
          onlyHero={false}
          smartMessage={akilliFinansMesaji}
          amountsHidden={financeAmountsHidden}
          onToggleAmounts={toggleFinanceAmounts}
        />
      )}


      {aktifSekme === "genel" && (
        <div className="financeFeatureGrid financeFeatureGrid4 financeFeatureGridBeforeTabs">
          <button className="financeFeatureCard savings" onClick={() => handleSekmeClick("birikim")}>
            <span className="financeFeatureAsset savingsAsset"><img src={birikimAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" /></span>
            <small>{fl(lang, "Birikim")}</small>
            <strong className={savingsSummary.toplamDeger > 0 ? "amountGreen" : ""}>{financeMoneyFormat(savingsSummary.toplamDeger)}</strong>
            <em className={savingsSummary.kar > 0 ? "amountGreen" : savingsSummary.kar < 0 ? "amountRed" : ""}>
              {savingsSummary.kar > 0 ? "+" : savingsSummary.kar < 0 ? "-" : ""}
              {financeMoneyFormat(Math.abs(savingsSummary.kar))}
              {savingsSummary.kar !== 0 && (savingsSummary.kar > 0 ? ` ${fl(lang, "Kâr")}` : ` ${fl(lang, "Zarar")}`)}
            </em>
          </button>
          <button className="financeFeatureCard installments" onClick={() => handleSekmeClick("taksitler")}>
            <span className="financeFeatureAsset installmentsAsset"><img src={taksitlerAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" /></span>
            <small>{fl(lang, "Taksitler")}</small>
            <strong className={installmentSummary.buAy > 0 ? "amountRed" : ""}>{financeMoneyFormat(installmentSummary.buAy)}</strong>
            <em>{installmentSummary.aktif.length} {fl(lang, "aktif")} · {fl(lang, "Kalan")} {financeMoneyFormat(installmentSummary.kalan)}</em>
          </button>
          <button className="financeFeatureCard subscriptions" onClick={() => handleSekmeClick("abonelikler")}>
            <span className="financeFeatureAsset subscriptionsAsset"><img src={aboneliklerAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" /></span>
            <small>{fl(lang, "Abonelikler")}</small>
            <strong className={subscriptionSummary.aylikToplam > 0 ? "amountRed" : ""}>{financeMoneyFormat(subscriptionSummary.aylikToplam)}</strong>
            <em>{subscriptionSummary.aktif.length} {fl(lang, "aktif")} · {fl(lang, "Aylık maliyet")}</em>
          </button>
          <button className="financeFeatureCard bills" onClick={() => handleSekmeClick("faturalar")}>
            <span className="financeFeatureAsset billsAsset"><img src={faturalarAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" /></span>
            <small>{fl(lang, "Faturalar")}</small>
            <strong className="billFeatureAmountLine">
              <span className={billSummary.toplam > 0 ? "amountRed" : ""}>{financeMoneyFormat(billSummary.toplam)}</span>
              
            </strong>
            <em className="billFeatureSubLine">
  <span>{billSummary.odenmemis.length} {fl(lang, "bekleyen")}</span>
  <span className="billPaidBreak">
    
    <span className="amountGreen">{financeMoneyFormat(billSummary.odenmisToplam)}</span>
    <span>{fl(lang, "ödendi")}</span>
  </span>
</em>
          </button>
        </div>
      )}

      {(aktifSekme === "genel" || aktifSekme === "rapor") && (
        <div className="categoryChips financeCategoryChips noSwipeBack" data-no-swipe-back="true">
          {[
            ["genel", t("finance.tab.general"), null], ["rapor", t("finance.tab.reports"), "finansRapor"], ["birikim", t("finance.tab.savings"), "finansBirikim"],
            ["taksitler", t("finance.tab.installments"), "finansTaksit"], ["abonelikler", t("finance.tab.subscriptions"), "finansAbonelik"], ["faturalar", t("finance.tab.bills"), "finansFatura"],
          ].map(([id, label, lockKey]) => {
            const isLocked = !isPremium && lockKey && lockedFeatures[lockKey];
            return (
              <button
                key={id}
                type="button"
                className={aktifSekme === id ? "chip activeBlue" : "chip"}
                onClick={() => handleSekmeClick(id)}
              >
                {label}{isLocked ? " 🔒" : ""}
              </button>
            );
          })}
        </div>
      )}

      {aktifSekme === "genel" && (
        <>
          <div className="familyExpensePanel">
            <div className="familyExpenseHeader">
              <div>
                <span className="financeEyebrow">{fl(lang, "Finansa Bağlı Aile Harcamaları")}</span>
                <h3 className={ortakToplam > 0 ? "amountRed" : "financeZeroAmount"}>{financeMoneyFormat(ortakToplam)}</h3>
                <p>{fl(lang, "Ortak harcamalar otomatik olarak finans giderlerine bağlı kalır.")}</p>
              </div>
              <button className="smallPremiumBtn" onClick={() => navigateTo?.("harcama")}>{lang === "en" ? "Go to Expenses" : "Harcamalara Git"}</button>
            </div>
            <div className="familyExpenseGrid">
              <div><span>{fl(lang, "Kişi Sayısı")}</span><b>{kisiBazli.length}</b></div>
              <div><span>{t("finance.topCategory")}</span><b>{ortakKategoriOzet[0]?.kategori ? listLabel(tList, "const.expenseCategories", ortakKategoriOzet[0].kategori) : t("common.none")}</b></div>
              <div><span>{fl(lang, "Kayıt")}</span><b>{buAyHarcama.length}</b></div>
            </div>
            <MiniBarList lang={lang} items={ortakKategoriOzet} total={ortakToplam} paraFormatla={financeMoneyFormat} emptyText={lang === "en" ? "No shared expenses this month" : "Bu ay ortak harcama yok"} labelFormatter={(v) => financeDisplayLabel(tList, lang, "const.expenseCategories", v)} />
            {borcOzeti?.length > 0 && (
              <div className="compactDebtList">
                {borcOzeti.slice(0, 4).map((b) => (
                  <span key={b.isim} className={b.fark >= 0 ? "positive" : "negative"}>{b.isim}: {b.fark >= 0 ? "+" : "-"}{financeMoneyFormat(Math.abs(b.fark))}</span>
                ))}
              </div>
            )}
          </div>

          <div className="darkCard financeCompactFilterCard">
            <div className="financeCompactFilterHead">
              <span className="cardHeaderTitle">{fl(lang, "Dönem Filtresi")}</span>
            </div>
            <div className="financeCompactFilterBody">
              <div className="financePeriodPills noSwipeBack" data-no-swipe-back="true">
                {["Aylık","Yıllık"].map((x) => (
                  <button key={x} className={finansPeriyot === x ? "periodBtn active" : "periodBtn"} onClick={() => setFinansPeriyot(x)}>{fl(lang, x)}</button>
                ))}
              </div>
              {finansPeriyot === "Aylık" && (
                <div className="inputLabelGroup compactFilterInputGroup">
                  <label className="darkLabel">{lang === "en" ? "Month" : "Ay"}</label>
                  <select className="darkInput" value={finansSeciliAy} onChange={(e) => setFinansSeciliAy(Number(e.target.value))}>
                    <option value={-1}>{fl(lang, "Tüm Aylar")}</option>
                    {AYLAR.map((a, i) => <option key={i} value={i}>{a}</option>)}
                  </select>
                </div>
              )}
              <div className="inputLabelGroup compactFilterInputGroup">
                <label className="darkLabel">{fl(lang, "Yıl")}</label>
                <select className="darkInput" value={finansSeciliYil} onChange={(e) => setFinansSeciliYil(Number(e.target.value))}>
                  {yilSecenekleri.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>

          {canAdd && (
            <div className="darkCard unifiedPremiumAddCard financeMainFormCard">
              <button
                className="unifiedPremiumAddHeader"
                type="button"
                onClick={() => setFinansFormAcik(!finansFormAcik)}
                aria-expanded={finansFormAcik}
              >
                <span className="unifiedPremiumAddIcon" aria-hidden="true">+</span>
                <span className="unifiedPremiumAddTitle">{t("finance.addRecord")}</span>
                <span className="unifiedPremiumAddArrow" aria-hidden="true">{finansFormAcik ? "⌃" : "⌄"}</span>
              </button>
              {finansFormAcik && (
                <div className="formBody shoppingCompactForm">
                  <input className={financeFieldClass("main", "baslik", "darkInput")} data-finance-error="main-baslik" aria-invalid={hasFinanceFieldError("main", "baslik")} placeholder={t("finance.titlePlaceholder")} value={finansBaslik} onChange={(e) => { setFinansBaslik(e.target.value); clearFinanceFieldError("main", "baslik"); }} />
                  <input className={financeFieldClass("main", "tutar", "darkInput")} data-finance-error="main-tutar" aria-invalid={hasFinanceFieldError("main", "tutar")} {...moneyInputProps} placeholder={t("finance.amountPlaceholder")} value={finansTutar} onChange={(e) => { setFinansTutar(formatMoneyInput(e.target.value)); clearFinanceFieldError("main", "tutar"); }} />
                  <div className="formRow">
                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("common.type")}</label>
                      <select className="darkInput" value={finansTur} onChange={(e) => { setFinansTur(e.target.value); setFinansKategori(e.target.value === "Gelir" ? "Maaş" : "Market"); }}>
                        <option value="Gelir">{t("finance.income")}</option><option value="Gider">{t("finance.expense")}</option>
                      </select>
                    </div>
                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("common.category")}</label>
                      <select className="darkInput" value={finansKategori} onChange={(e) => setFinansKategori(e.target.value)}>
                        {(finansTur === "Gelir" ? gelirKategorileri : giderKategorileri).map((k) => <option key={k} value={k}>{listLabel(tList, finansTur === "Gelir" ? "const.incomeCategories" : "const.expenseCategories", k)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("common.date")}</label>
                    <input className="darkInput" type="date" value={finansTarih} onChange={(e) => setFinansTarih(e.target.value)} />
                  </div>

                  <label className="automationOptionRow financeRecurringToggle">
                    <span><strong>{t("finance.recurring.toggle")}</strong><small>{t("finance.recurring.hint")}</small></span>
                    <div className={`toggleSwitch sm ${finansTekrar.enabled ? "on" : ""}`} onClick={() => setFinansTekrar?.((current) => ({ ...current, enabled: !current.enabled }))}>
                      <div className="toggleThumb" />
                    </div>
                  </label>
                  {finansTekrar.enabled && (
                    <div className="formRow financeRecurringFields">
                      <div className="inputLabelGroup">
                        <label className="darkLabel">{t("finance.recurring.frequency")}</label>
                        <select className="darkInput" value={finansTekrar.frequency} onChange={(e) => setFinansTekrar?.((current) => ({ ...current, frequency: e.target.value }))}>
                          <option value="monthly">{t("finance.recurring.monthly")}</option>
                          <option value="quarterly">{t("finance.recurring.quarterly")}</option>
                          <option value="semiannual">{t("finance.recurring.semiannual")}</option>
                          <option value="yearly">{t("finance.recurring.yearly")}</option>
                        </select>
                      </div>
                      <div className="inputLabelGroup">
                        <label className="darkLabel">{t("finance.recurring.endDate")}</label>
                        <input className="darkInput" type="date" min={finansTarih} value={finansTekrar.endDate || ""} onChange={(e) => setFinansTekrar?.((current) => ({ ...current, endDate: e.target.value }))} />
                      </div>
                    </div>
                  )}

                  {renderFinanceValidationMessage("main", financeFormErrors.main.map((field) => ({ baslik: t("finance.titlePlaceholder"), tutar: t("finance.amountPlaceholder") }[field])).filter(Boolean))}
                  <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={financeSubmitting.main} onAction={handleMainFinanceAdd}>{t("common.add")}</AsyncActionButton>
                </div>
              )}
            </div>
          )}

          <div className="tableCard financeSplitList" style={{marginBottom: 10}}>
            <div
              className="tableCardHeader stickyFinanceHeader financeListeBaslik"
              onClick={() => setGelirGenisletildi(v => !v)}
            >
              <span>{t("finance.income")}</span>
              <button
                type="button"
                className={`financeListeBtn${gelirGenisletildi ? " acik" : ""}`}
                onClick={e => { e.stopPropagation(); setGelirGenisletildi(v => !v); }}
              >▾</button>
            </div>
            {gelirler.length === 0
              ? <div className="emptyState compact">{t("finance.noRecords")}</div>
              : <div className={`financeScrollSub${gelirGenisletildi ? " genisletildi" : ""}`}>
                  {gorunenGelirler.map((f, idx) => renderFinanceRow(f, idx, idx === gorunenGelirler.length - 1))}
                </div>
            }
          </div>
          <div className="tableCard financeSplitList">
            <div
              className="tableCardHeader stickyFinanceHeader expenseHeader financeListeBaslik"
              onClick={() => setGiderGenisletildi(v => !v)}
            >
              <span>{t("finance.expense")}</span>
              <button
                type="button"
                className={`financeListeBtn gider${giderGenisletildi ? " acik" : ""}`}
                onClick={e => { e.stopPropagation(); setGiderGenisletildi(v => !v); }}
              >▾</button>
            </div>
            {giderler.length === 0
              ? <div className="emptyState compact">{t("finance.noRecords")}</div>
              : <div className={`financeScrollSub${giderGenisletildi ? " genisletildi" : ""}`}>
                  {gorunenGiderler.map((f, idx) => renderFinanceRow(f, idx, idx === gorunenGiderler.length - 1))}
                </div>
            }
          </div>
        </>
      )}

      {aktifSekme === "rapor" && (
        <div className="financeReportPanel">
          <div className="financeReportSummaryRow">
            <div className="darkCard financeReportSummaryCard">
              <span>{financeReportText(lang, "thirtyDayIncome")}</span>
              <b className={sonOtuzGunToplam.gelir > 0 ? "amountGreen" : "financeZeroAmount"}>{financeMoneyFormat(sonOtuzGunToplam.gelir)}</b>
            </div>
            <div className="darkCard financeReportSummaryCard">
              <span>{financeReportText(lang, "thirtyDayExpense")}</span>
              <b className={sonOtuzGunToplam.gider > 0 ? "amountRed" : "financeZeroAmount"}>{financeMoneyFormat(sonOtuzGunToplam.gider)}</b>
            </div>
            <div className="darkCard financeReportSummaryCard">
              <span>{financeReportText(lang, "netResult")}</span>
              <b className={sonOtuzGunToplam.net > 0 ? "amountGreen" : sonOtuzGunToplam.net < 0 ? "amountRed" : "financeZeroAmount"}>{financeMoneyFormat(sonOtuzGunToplam.net)}</b>
            </div>
          </div>

          <div className="darkCard financeReportChartCard">
            <div className="cardHeader financeReportChartHeader">
              <div>
                <span className="cardHeaderTitle">{financeReportText(lang, "comparisonTitle")}</span>
                <p>{financeReportText(lang, "comparisonDesc")}</p>
              </div>
              <div className="financeReportLegend" aria-label={financeReportText(lang, "comparisonTitle")}>
                <span><i className="incomeDot" />{financeReportText(lang, "income")}</span>
                <span><i className="expenseDot" />{financeReportText(lang, "expense")}</span>
              </div>
            </div>
            {sonOtuzGunToplam.gelir === 0 && sonOtuzGunToplam.gider === 0 ? (
              <div className="emptyState compact">{t("finance.report.last30empty")}</div>
            ) : (
              <div
                className="premiumTrendChart"
                title={`${financeReportText(lang, "income")}: ${financeMoneyFormat(sonOtuzGunToplam.gelir)} · ${financeReportText(lang, "expense")}: ${financeMoneyFormat(sonOtuzGunToplam.gider)}`}
                onPointerMove={handleTrendPointerMove}
                onPointerDown={handleTrendPointerMove}
                onPointerLeave={() => setAktifTrendIndex(null)}
              >
                <svg className="premiumTrendSvg" viewBox={`0 0 ${premiumTrendChart.width} ${premiumTrendChart.height}`} preserveAspectRatio="none" role="img" aria-label={financeReportText(lang, "comparisonTitle")}>
                  <defs>
                    <linearGradient id="financeIncomeBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                    <linearGradient id="financeExpenseBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fb7185" />
                      <stop offset="100%" stopColor="#e11d48" />
                    </linearGradient>
                  </defs>
                  {premiumTrendChart.gridValues.map((grid) => (
                    <line key={`grid-${grid.y}`} className="chartGridLine" x1={premiumTrendChart.paddingX} y1={grid.y} x2={premiumTrendChart.width - premiumTrendChart.paddingX} y2={grid.y} />
                  ))}
                  {premiumTrendChart.bars.map((group) => {
                    const active = aktifTrend?.index === group.index;
                    return (
                      <g key={group.item.tarih} className={`financeBarGroup ${active ? "active" : ""}`}>
                        <title>{`${formatReportDay(group.item.tarih)}–${formatReportDay(group.item.bitisTarihi)} · ${financeReportText(lang, "income")}: ${financeMoneyFormat(group.item.gelir)} · ${financeReportText(lang, "expense")}: ${financeMoneyFormat(group.item.gider)}`}</title>
                        {active && <rect className="financeBarFocus" x={group.centerX - 27} y={premiumTrendChart.paddingTop - 6} width="54" height={premiumTrendChart.baseline - premiumTrendChart.paddingTop + 12} rx="15" />}
                        <rect className="financeIncomeBar" x={group.income.x} y={group.income.y} width={group.income.width} height={group.income.height} rx={group.income.width / 2} />
                        <rect className="financeExpenseBar" x={group.expense.x} y={group.expense.y} width={group.expense.width} height={group.expense.height} rx={group.expense.width / 2} />
                        <text className="chartDayLabel" x={group.centerX} y={premiumTrendChart.height - 13} textAnchor="middle">{formatReportDay(group.item.tarih, false)}–{formatReportDay(group.item.bitisTarihi, false)}</text>
                      </g>
                    );
                  })}
                </svg>
                {aktifTrend && (
                  <div className="chartTooltip" style={{ left: `${Math.min(78, Math.max(22, (aktifTrend.bar.centerX / premiumTrendChart.width) * 100))}%` }}>
                    <strong>{aktifTrend.label}</strong>
                    <span><i className="incomeDot" />{financeReportText(lang, "income")} <b>{financeMoneyFormat(aktifTrend.income)}</b></span>
                    <span><i className="expenseDot" />{financeReportText(lang, "expense")} <b>{financeMoneyFormat(aktifTrend.expense)}</b></span>
                    <span><i className="netDot" />{financeReportText(lang, "net")} <b>{financeMoneyFormat(aktifTrend.net)}</b></span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="reportGrid financeReportGridCompact">
            <div
              className={`darkCard financeReportMiniCard financeBreakdownCard noSwipeBack ${breakdownIsIncome ? "incomeMode" : "expenseMode"}`}
              data-no-swipe-back="true"
              onTouchStart={handleBreakdownTouchStart}
              onTouchEnd={handleBreakdownTouchEnd}
            >
              <div className="cardHeader financeBreakdownHeader">
                <span className="cardHeaderTitle">{aktifDagilimBaslik}</span>
                <div className="financeBreakdownTypeSwitch" role="tablist" aria-label={lang === "en" ? "Distribution type" : "Dağılım türü"}>
                  <button type="button" role="tab" aria-selected={breakdownIsIncome} className={breakdownIsIncome ? "active income" : ""} onClick={() => selectBreakdownType("income")}>{financeReportText(lang, "income")}</button>
                  <button type="button" role="tab" aria-selected={!breakdownIsIncome} className={!breakdownIsIncome ? "active expense" : ""} onClick={() => selectBreakdownType("expense")}>{financeReportText(lang, "expense")}</button>
                </div>
              </div>
              <div className="financeBreakdownViewport">
                {breakdownView === "pie" ? (
                  <div className="financeBreakdownSlide" key={`${breakdownType}-pie`}>
                    <FinanceBreakdownPie items={aktifDagilimOzet} total={aktifDagilimToplam} paraFormatla={financeMoneyFormat} emptyText={aktifDagilimBosMetin} labelFormatter={(v) => financeDisplayLabel(tList, lang, aktifDagilimKategoriListesi, v)} />
                  </div>
                ) : (
                  <div className="financeBreakdownSlide" key={`${breakdownType}-bars`}>
                    <MiniBarList lang={lang} items={aktifDagilimOzet.slice(0, 5)} total={aktifDagilimToplam} paraFormatla={financeMoneyFormat} emptyText={aktifDagilimBosMetin} labelFormatter={(v) => financeDisplayLabel(tList, lang, aktifDagilimKategoriListesi, v)} />
                  </div>
                )}
              </div>
              <div className="financeBreakdownDots" role="tablist" aria-label={aktifDagilimBaslik}>
                <button type="button" className={breakdownView === "bars" ? "active" : ""} onClick={() => selectBreakdownView("bars")} aria-label={lang === "en" ? "List view" : "Liste görünümü"} />
                <button type="button" className={breakdownView === "pie" ? "active" : ""} onClick={() => selectBreakdownView("pie")} aria-label={lang === "en" ? "Pie chart" : "Pasta grafik"} />
              </div>
            </div>
            <div className="darkCard financeReportMiniCard financeReportInsightCard">
              <div className="cardHeader"><span className="cardHeaderTitle">{financeReportText(lang, "financialInsight")}</span></div>
              <p>{finansRaporYorumu}</p>
            </div>
          </div>
        </div>
      )}

      {aktifSekme === "birikim" && (
        <div className="financeSavingsSection">
          <div className="financeSubHero savingsHero">
            <div>
              <span className="financeEyebrow">{t("finance.savings.portfolio")}</span>
              <h2 className={savingsSummary.toplamDeger > 0 ? "amountGreen" : savingsSummary.toplamDeger < 0 ? "amountRed" : ""}>{financeMoneyFormat(savingsSummary.toplamDeger)}</h2>
            </div>
            <button className="smallPremiumBtn" onClick={handleFinanceBack}>{t("finance.backToFinance")}</button>
          </div>
          <div className="darkCard unifiedPremiumAddCard premiumFormCard">
            <button className="unifiedPremiumAddHeader" type="button" onClick={() => setSavingFormAcik(!savingFormAcik)} aria-expanded={savingFormAcik}>
              <span className="unifiedPremiumAddIcon" aria-hidden="true">+</span>
              <span className="unifiedPremiumAddTitle">{savingEditId ? t("common.edit") : t("finance.savings.add")}</span>
              <span className="unifiedPremiumAddArrow" aria-hidden="true">{savingFormAcik ? "⌃" : "⌄"}</span>
            </button>
            {savingFormAcik && (
              <div className="formBody inlineToolForm">
                <select className="darkInput" value={savingDraft.tur} onChange={(e) => {
                  setSavingDraft({ ...emptySavingDraft(defaultFormCurrency), tur: e.target.value });
                  setFinanceFormErrors((current) => ({ ...current, saving: [] }));
                }}>{birikimTurleri.map((tur) => <option key={tur} value={tur}>{fl(lang, tur)}</option>)}</select>

                {(savingDraft.tur === "Gayrimenkul" || savingDraft.tur === "Real Estate") && (
                  <div className="realEstateForm">
                    <div className="realEstateFormIntro">
                      <span aria-hidden="true">🏡</span>
                      <div>
                        <b>{t("finance.realEstate.formTitle")}</b>
                        <small>{t("finance.realEstate.formHint")}</small>
                      </div>
                    </div>

                    <div className="realEstateFormSection">
                      <div className="realEstateSectionTitle"><span>1</span><b>{t("finance.realEstate.identitySection")}</b></div>
                      <div className="realEstateFormGrid twoColumns">
                        <div className={financeFieldClass("saving", "ad", "inputLabelGroup")} data-finance-error="saving-ad">
                          <label className="darkLabel">{t("finance.realEstate.name")} *</label>
                          <input className="darkInput" placeholder={t("finance.realEstate.namePlaceholder")} value={savingDraft.ad} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "ad", e.target.value)} aria-invalid={hasFinanceFieldError("saving", "ad")} />
                        </div>
                        <div className={financeFieldClass("saving", "gayrimenkulTuru", "inputLabelGroup")} data-finance-error="saving-gayrimenkulTuru">
                          <label className="darkLabel">{t("finance.realEstate.type")} *</label>
                          <select className="darkInput" value={savingDraft.gayrimenkulTuru} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "gayrimenkulTuru", e.target.value)} aria-invalid={hasFinanceFieldError("saving", "gayrimenkulTuru")}>
                            <option value="">{t("finance.realEstate.typePlaceholder")}</option>
                            {REAL_ESTATE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{t(`finance.realEstate.type.${option}`)}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="realEstateFormGrid threeColumns">
                        <div className={financeFieldClass("saving", "sehir", "inputLabelGroup")} data-finance-error="saving-sehir">
                          <label className="darkLabel">{t("finance.realEstate.city")} *</label>
                          <input className="darkInput" placeholder={t("finance.realEstate.cityPlaceholder")} value={savingDraft.sehir} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "sehir", e.target.value)} aria-invalid={hasFinanceFieldError("saving", "sehir")} />
                        </div>
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.district")}</label>
                          <input className="darkInput" placeholder={t("finance.realEstate.districtPlaceholder")} value={savingDraft.ilce} onChange={(e) => setSavingDraft({ ...savingDraft, ilce: e.target.value })} />
                        </div>
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.neighborhood")}</label>
                          <input className="darkInput" placeholder={t("finance.realEstate.neighborhoodPlaceholder")} value={savingDraft.mahalle} onChange={(e) => setSavingDraft({ ...savingDraft, mahalle: e.target.value })} />
                        </div>
                      </div>
                      <div className="realEstateFormGrid threeColumns">
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.area")}</label>
                          <input className="darkInput" {...moneyInputProps} placeholder="m²" value={savingDraft.brutMetrekare} onChange={handleMoneyDraftChange(setSavingDraft, "brutMetrekare")} />
                        </div>
                        {!["land", "warehouse"].includes(savingDraft.gayrimenkulTuru) && (
                          <div className="inputLabelGroup">
                            <label className="darkLabel">{t("finance.realEstate.rooms")}</label>
                            <input className="darkInput" placeholder={t("finance.realEstate.roomsPlaceholder")} value={savingDraft.odaSayisi} onChange={(e) => setSavingDraft({ ...savingDraft, odaSayisi: e.target.value })} />
                          </div>
                        )}
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.currency")}</label>
                          <select className="darkInput" value={savingDraft.paraBirimi} onChange={(e) => setSavingDraft({ ...savingDraft, paraBirimi: e.target.value })}>{paraBirimleri.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                        </div>
                      </div>
                    </div>

                    <div className="realEstateFormSection">
                      <div className="realEstateSectionTitle"><span>2</span><b>{t("finance.realEstate.valueSection")}</b></div>
                      <div className="realEstateFormGrid twoColumns">
                        <div className={financeFieldClass("saving", "alisFiyati", "inputLabelGroup")} data-finance-error="saving-alisFiyati">
                          <label className="darkLabel">{t("finance.realEstate.purchasePrice")} *</label>
                          <input className="darkInput" {...moneyInputProps} placeholder="0" value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} />
                        </div>
                        <div className={financeFieldClass("saving", "guncelFiyat", "inputLabelGroup")} data-finance-error="saving-guncelFiyat">
                          <label className="darkLabel">{t("finance.realEstate.currentValue")} *</label>
                          <input className="darkInput" {...moneyInputProps} placeholder="0" value={savingDraft.guncelFiyat} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "guncelFiyat", formatMoneyInput(e.target.value))} aria-invalid={hasFinanceFieldError("saving", "guncelFiyat")} />
                        </div>
                      </div>
                      <div className="realEstateFormGrid twoColumns">
                        <DateInputField label={t("finance.realEstate.purchaseDate")} value={savingDraft.tarih} onChange={(value) => setSavingDraft({ ...savingDraft, tarih: value })} />
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.useStatus")}</label>
                          <select className="darkInput" value={savingDraft.kullanimDurumu} onChange={(e) => setSavingDraft({ ...savingDraft, kullanimDurumu: e.target.value })}>{REAL_ESTATE_USE_OPTIONS.map((option) => <option key={option} value={option}>{t(`finance.realEstate.use.${option}`)}</option>)}</select>
                        </div>
                      </div>
                      {savingDraft.kullanimDurumu === "rented" && (
                        <div className="realEstateDynamicPanel">
                          <div className="realEstateFormGrid twoColumns">
                            <div className="inputLabelGroup">
                              <label className="darkLabel">{t("finance.realEstate.monthlyRent")}</label>
                              <input className="darkInput" {...moneyInputProps} placeholder="0" value={savingDraft.aylikKiraGeliri} onChange={handleMoneyDraftChange(setSavingDraft, "aylikKiraGeliri")} />
                            </div>
                            <DateInputField label={t("finance.realEstate.leaseStart")} value={savingDraft.kiraBaslangic} onChange={(value) => setSavingDraft({ ...savingDraft, kiraBaslangic: value })} />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="realEstateFormSection">
                      <div className="realEstateSectionTitle"><span>3</span><b>{t("finance.realEstate.ownershipSection")}</b></div>
                      <div className="realEstateFormGrid threeColumns">
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.deedStatus")}</label>
                          <select className="darkInput" value={savingDraft.tapuDurumu} onChange={(e) => setSavingDraft({ ...savingDraft, tapuDurumu: e.target.value })}>{REAL_ESTATE_DEED_OPTIONS.map((option) => <option key={option} value={option}>{t(`finance.realEstate.deed.${option}`)}</option>)}</select>
                        </div>
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.ownershipShare")}</label>
                          <input className="darkInput" type="number" min="1" max="100" placeholder="100" value={savingDraft.sahiplikPayi} onChange={(e) => setSavingDraft({ ...savingDraft, sahiplikPayi: e.target.value })} />
                        </div>
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("finance.realEstate.remainingLoan")}</label>
                          <input className="darkInput" {...moneyInputProps} placeholder="0" value={savingDraft.kalanKrediBorcu} onChange={handleMoneyDraftChange(setSavingDraft, "kalanKrediBorcu")} />
                        </div>
                      </div>
                      {parseMoneyInput(savingDraft.kalanKrediBorcu) > 0 && (
                        <div className="realEstateDynamicPanel">
                          <div className="inputLabelGroup">
                            <label className="darkLabel">{t("finance.realEstate.monthlyLoanPayment")}</label>
                            <input className="darkInput" {...moneyInputProps} placeholder="0" value={savingDraft.aylikKrediOdemesi} onChange={handleMoneyDraftChange(setSavingDraft, "aylikKrediOdemesi")} />
                          </div>
                        </div>
                      )}
                    </div>

                    {(parseMoneyInput(savingDraft.alisFiyati) > 0 || parseMoneyInput(savingDraft.guncelFiyat) > 0) && (
                      <div className="realEstateLiveSummary" aria-live="polite">
                        <div><span>{t("finance.realEstate.valueChange")}</span><b className={parseMoneyInput(savingDraft.guncelFiyat) - parseMoneyInput(savingDraft.alisFiyati) >= 0 ? "amountGreen" : "amountRed"}>{financeMoneyFormat((parseMoneyInput(savingDraft.guncelFiyat) - parseMoneyInput(savingDraft.alisFiyati)) * getRealEstateShareFactor(savingDraft))}</b></div>
                        <div><span>{t("finance.realEstate.netEquity")}</span><b>{financeMoneyFormat(Math.max(0, parseMoneyInput(savingDraft.guncelFiyat) * getRealEstateShareFactor(savingDraft) - parseMoneyInput(savingDraft.kalanKrediBorcu)))}</b></div>
                        {parseMoneyInput(savingDraft.aylikKiraGeliri) > 0 && parseMoneyInput(savingDraft.guncelFiyat) > 0 && <div><span>{t("finance.realEstate.grossYield")}</span><b>%{((parseMoneyInput(savingDraft.aylikKiraGeliri) * 12 / parseMoneyInput(savingDraft.guncelFiyat)) * 100).toFixed(2)}</b></div>}
                      </div>
                    )}
                  </div>
                )}

                {savingDraft.tur === "Altın" && (
                  <>
                    <select className="darkInput" value={savingDraft.altinTuru} onChange={(e) => setSavingDraft({ ...savingDraft, altinTuru: e.target.value })}>{goldTypeOptions.map((tur) => <option key={tur} value={tur}>{fl(lang, tur)}</option>)}</select>
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.goldAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "miktar", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("saving", "alisFiyati", "darkInput")} data-finance-error="saving-alisFiyati" aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} {...moneyInputProps} placeholder={t("finance.savings.buyUnitPricePlaceholder")} value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentUnitPricePlaceholder")} value={savingDraft.guncelFiyat} onChange={handleMoneyDraftChange(setSavingDraft, "guncelFiyat")} />
                  </>
                )}

                {savingDraft.tur === "Mevduat" && (
                  <>
                    <input className="darkInput" placeholder={t("finance.savings.bankPlaceholder")} value={savingDraft.banka} onChange={(e) => setSavingDraft({ ...savingDraft, banka: e.target.value })} />
                    <select className="darkInput" value={savingDraft.paraBirimi} onChange={(e) => setSavingDraft({ ...savingDraft, paraBirimi: e.target.value })}>{paraBirimleri.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.principalPlaceholder")} value={savingDraft.miktar} onChange={(e) => {
                      setSavingDraft({ ...savingDraft, miktar: formatMoneyInput(e.target.value), alisFiyati: "1", guncelFiyat: "1" });
                      clearFinanceFieldError("saving", "miktar");
                    }} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.interestRatePlaceholder")} value={savingDraft.faizOrani} onChange={handleMoneyDraftChange(setSavingDraft, "faizOrani")} />
                    <DateInputField label={t("finance.savings.termStartDatePlaceholder")} value={savingDraft.vadeBaslangic} onChange={(value) => setSavingDraft({ ...savingDraft, vadeBaslangic: value })} />
                    <DateInputField label={t("finance.savings.termEndDatePlaceholder")} value={savingDraft.vadeBitis} onChange={(value) => setSavingDraft({ ...savingDraft, vadeBitis: value })} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentTotalValuePlaceholder")} value={savingDraft.guncelFiyat && parseMoneyInput(savingDraft.miktar) ? formatMoneyInput(String(parseMoneyInput(savingDraft.guncelFiyat) * parseMoneyInput(savingDraft.miktar))) : ""} onChange={(e) => { const total = parseMoneyInput(e.target.value); const principal = parseMoneyInput(savingDraft.miktar); setSavingDraft({ ...savingDraft, guncelFiyat: principal > 0 ? formatMoneyInput(String(total / principal)) : "1" }); }} />
                  </>
                )}

                {savingDraft.tur === "Borsa" && (
                  <>
                    <input className="darkInput" placeholder={t("finance.savings.stockCodePlaceholder")} value={savingDraft.hisseKodu} onChange={(e) => setSavingDraft({ ...savingDraft, hisseKodu: e.target.value.toUpperCase() })} />
                    <input className="darkInput" placeholder={t("finance.savings.stockNamePlaceholder")} value={savingDraft.hisseAdi} onChange={(e) => setSavingDraft({ ...savingDraft, hisseAdi: e.target.value })} />
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.lotAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "miktar", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("saving", "alisFiyati", "darkInput")} data-finance-error="saving-alisFiyati" aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} {...moneyInputProps} placeholder={t("finance.savings.buyUnitPricePlaceholder")} value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentUnitPricePlaceholder")} value={savingDraft.guncelFiyat} onChange={handleMoneyDraftChange(setSavingDraft, "guncelFiyat")} />
                  </>
                )}

                {savingDraft.tur === "Kripto" && (
                  <>
                    <input className="darkInput" placeholder={t("finance.savings.coinSymbolPlaceholder")} value={savingDraft.coinSembol} onChange={(e) => setSavingDraft({ ...savingDraft, coinSembol: e.target.value.toUpperCase() })} />
                    <input className="darkInput" placeholder={t("finance.savings.coinNamePlaceholder")} value={savingDraft.coinAdi} onChange={(e) => setSavingDraft({ ...savingDraft, coinAdi: e.target.value })} />
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.cryptoAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "miktar", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("saving", "alisFiyati", "darkInput")} data-finance-error="saving-alisFiyati" aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} {...moneyInputProps} placeholder={t("finance.savings.buyUnitPricePlaceholder")} value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentUnitPricePlaceholder")} value={savingDraft.guncelFiyat} onChange={handleMoneyDraftChange(setSavingDraft, "guncelFiyat")} />
                    <input className="darkInput" placeholder={t("finance.savings.platformPlaceholder")} value={savingDraft.platform} onChange={(e) => setSavingDraft({ ...savingDraft, platform: e.target.value })} />
                  </>
                )}

                {savingDraft.tur === "Döviz" && (
                  <>
                    <select className="darkInput" value={savingDraft.dovizCinsi} onChange={(e) => setSavingDraft({ ...savingDraft, dovizCinsi: e.target.value })}>{["USD", "EUR", "GBP"].map((item) => <option key={item} value={item}>{item}</option>)}</select>
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.currencyAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "miktar", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("saving", "alisFiyati", "darkInput")} data-finance-error="saving-alisFiyati" aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} {...moneyInputProps} placeholder={t("finance.savings.buyRatePlaceholder")} value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentRatePlaceholder")} value={savingDraft.guncelFiyat} onChange={handleMoneyDraftChange(setSavingDraft, "guncelFiyat")} />
                  </>
                )}

                {savingDraft.tur === "Fon" && (
                  <>
                    <input className="darkInput" placeholder={t("finance.savings.fundCodePlaceholder")} value={savingDraft.fonKodu} onChange={(e) => setSavingDraft({ ...savingDraft, fonKodu: e.target.value.toUpperCase() })} />
                    <input className="darkInput" placeholder={t("finance.savings.fundNamePlaceholder")} value={savingDraft.fonAdi} onChange={(e) => setSavingDraft({ ...savingDraft, fonAdi: e.target.value })} />
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.shareAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "miktar", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("saving", "alisFiyati", "darkInput")} data-finance-error="saving-alisFiyati" aria-invalid={hasFinanceFieldError("saving", "alisFiyati")} {...moneyInputProps} placeholder={t("finance.savings.buyUnitPricePlaceholder")} value={savingDraft.alisFiyati} onChange={(e) => updateFinanceDraftField(setSavingDraft, "saving", "alisFiyati", formatMoneyInput(e.target.value))} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.savings.currentUnitPricePlaceholder")} value={savingDraft.guncelFiyat} onChange={handleMoneyDraftChange(setSavingDraft, "guncelFiyat")} />
                  </>
                )}

                {savingDraft.tur === "Nakit" && (
                  <>
                    <select className="darkInput" value={savingDraft.paraBirimi} onChange={(e) => setSavingDraft({ ...savingDraft, paraBirimi: e.target.value })}>{paraBirimleri.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                    <input className={financeFieldClass("saving", "miktar", "darkInput")} data-finance-error="saving-miktar" aria-invalid={hasFinanceFieldError("saving", "miktar")} {...moneyInputProps} placeholder={t("finance.savings.cashAmountPlaceholder")} value={savingDraft.miktar} onChange={(e) => {
                      setSavingDraft({ ...savingDraft, miktar: formatMoneyInput(e.target.value), alisFiyati: "1", guncelFiyat: "1" });
                      clearFinanceFieldError("saving", "miktar");
                    }} />
                    <input className="darkInput" placeholder={t("finance.savings.storagePlacePlaceholder")} value={savingDraft.saklamaYeri} onChange={(e) => setSavingDraft({ ...savingDraft, saklamaYeri: e.target.value })} />
                  </>
                )}

                {savingDraft.tur !== "Gayrimenkul" && savingDraft.tur !== "Real Estate" && (
                  <DateInputField label={t("finance.savings.datePlaceholder")} value={savingDraft.tarih} onChange={(value) => setSavingDraft({ ...savingDraft, tarih: value })} />
                )}
                <input className="darkInput" placeholder={t("finance.savings.notePlaceholder")} value={savingDraft.not} onChange={(e) => setSavingDraft({ ...savingDraft, not: e.target.value })} />
                <ReminderWarning message={savingReminder.aktif ? savingReminderError : ""} />
                {renderFinanceValidationMessage("saving", (savingDraft.tur === "Gayrimenkul" || savingDraft.tur === "Real Estate")
                  ? financeFormErrors.saving.map((field) => ({ ad: t("finance.realEstate.name"), gayrimenkulTuru: t("finance.realEstate.type"), sehir: t("finance.realEstate.city"), alisFiyati: t("finance.realEstate.purchasePrice"), guncelFiyat: t("finance.realEstate.currentValue") }[field])).filter(Boolean)
                  : financeFormErrors.saving.map((field) => ({ miktar: t("finance.savings.amountPlaceholder"), alisFiyati: t("finance.savings.buyPricePlaceholder") }[field])).filter(Boolean))}
                <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={financeSubmitting.saving} busyText={savingEditId ? t("common.saving") : undefined} onAction={saveSaving}>{savingEditId ? t("common.save") : t("finance.savings.add")}</AsyncActionButton>
                {savingEditId && <button className="smallPremiumBtn compact" type="button" onClick={cancelSavingEdit}>{t("common.cancel")}</button>}
                <ReminderPanel
                  aktif={savingReminder.aktif}
                  setAktif={(v) => setSavingReminder(p => ({ ...p, aktif: v }))}
                  tarih={savingReminder.tarih}
                  setTarih={(v) => setSavingReminder(p => ({ ...p, tarih: v }))}
                  saat={savingReminder.saat}
                  setSaat={(v) => setSavingReminder(p => ({ ...p, saat: v }))}
                  tekrar={savingReminder.tekrar}
                  setTekrar={(v) => setSavingReminder(p => ({ ...p, tekrar: v }))}
                  mod="datetime"
                  etiket={t("finance.savings.reminderLabel")}
                />
              </div>
            )}
          </div>
          <div className="budgetList financeDetailList financeSavingsList">
            {savings.length === 0 && (
              <div className="financeToolEmptyPremium" role="status" aria-live="polite">
                <img className="financeToolEmptyPremiumImage" src={finansAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" draggable="false" />
                <h3 className="financeToolEmptyPremiumTitle">
                  {lang === "en" ? "Your savings journey starts here" : "Birikim yolculuğunuz burada başlıyor"}
                </h3>
                <p className="financeToolEmptyPremiumText">
                  {lang === "en"
                    ? "Add your first saving and watch your goals grow step by step."
                    : "İlk birikiminizi ekleyin, hedeflerinizin adım adım büyümesini birlikte takip edin."}
                </p>
              </div>
            )}
            {newestSavings.map((s) => {
              const realEstate = s.tur === "Gayrimenkul" || s.tur === "Real Estate";
              const maliyet = Number(s.miktar || 0) * Number(s.alisFiyati || 0);
              const currentUnitPrice = getSavingCurrentUnitPrice(s);
              const deger = Number(s.miktar || 0) * currentUnitPrice;
              const kar = deger - maliyet;
              const yuzde = maliyet > 0 ? (kar / maliyet) * 100 : 0;
              const netEquity = Math.max(0, deger - Number(s.kalanKrediBorcu || 0));
              const propertyLocation = [s.mahalle, s.ilce, s.sehir].filter(Boolean).join(", ");
              const propertyIcon = { apartment: "🏢", house: "🏠", villa: "🏡", land: "🌱", commercial: "🏬", office: "🏙️", shop: "🏪", warehouse: "🏭", other: "📍" }[s.gayrimenkulTuru] || "🏡";
              return (
                <div className={`premiumAssetTile ${realEstate ? "realEstateAssetTile" : ""}`} key={s.id}>
                  <div className="assetTileTop">
                    <div className={`assetAvatar ${realEstate ? "realEstate" : ""}`}>{realEstate ? propertyIcon : s.tur === "Altın" ? "🥇" : s.tur === "Kripto" ? "₿" : s.tur === "Döviz" ? "💱" : s.tur === "Borsa" ? "📈" : s.tur === "Mevduat" ? "🏦" : "💼"}</div>
                    <div className="assetTitleBlock">
                      <ExpandableText text={s.ad} className="assetTileTitle" maxLength={32} />
                      <span>{realEstate
                        ? [s.gayrimenkulTuru ? t(`finance.realEstate.type.${s.gayrimenkulTuru}`) : t("finance.realEstate.type.other"), propertyLocation || null, s.brutMetrekare ? `${formatMoneyDisplayNumber(Number(s.brutMetrekare), 0)} m²` : null].filter(Boolean).join(" · ")
                        : `${fl(lang, s.tur)} · ${Number(s.miktar || 0)} ${t("finance.savings.units")} · ${t("finance.savings.buyDate")} ${tarihOkunakli(s.tarih, AYLAR)}`}</span>
                    </div>
                    <div className="subTileActions financeSavingsTileActions">
                      <FinanceRowActionMenu
                        menuId={`saving-${s.id}`}
                        onEdit={() => {
                          setSavingUpdatePanelId(null);
                          editSaving(s);
                        }}
                        onDelete={() => {
                          setSavingUpdatePanelId(null);
                          deleteSaving(s.id);
                        }}
                        menuClassName="financeSavingsActionDropdown"
                      >
                        {!isAutoMarketPricedSaving(s) && (
                          <button
                            type="button"
                            className="smallPremiumBtn compact savingMenuUpdateBtn"
                            onClick={() => {
                              setFinanceActionMenuId(null);
                              setSavingUpdatePanelId((prev) => prev === s.id ? null : s.id);
                              setSavingUpdateDrafts((prev) => ({
                                ...prev,
                                [s.id]: prev[s.id] ?? formatMoneyInput(s.guncelFiyat ?? s.alisFiyati ?? ""),
                              }));
                            }}
                            style={{ width: "100%", justifyContent: "flex-start" }}
                          >
                            🔄 {realEstate ? t("finance.realEstate.updateValue") : t("finance.savings.update")}
                          </button>
                        )}
                      </FinanceRowActionMenu>
                    </div>
                  </div>
                  <div className="assetMetricGrid">
                    <div><span>{realEstate ? t("finance.realEstate.purchasePrice") : t("finance.savings.cost")}</span><b>{financeMoneyFormat(maliyet)}</b></div>
                    <div><span>{t("finance.savings.currentValue")}</span><b className={deger > 0 ? "amountGreen" : ""}>{financeMoneyFormat(deger)}</b></div>
                    <div>
                      <span>{realEstate ? t("finance.realEstate.valueChange") : t("finance.savings.profitLoss")}</span>
                      <b className={kar > 0 ? "amountGreen" : kar < 0 ? "amountRed" : "amountNeutral"}>
                        {kar > 0 ? "+" : kar < 0 ? "-" : ""}
                        {financeMoneyFormat(Math.abs(kar))}
                        · %{formatMoneyDisplayNumber(Math.abs(yuzde), 1)}
                      </b>
                    </div>
                  </div>
                  {realEstate && (
                    <div className="realEstateCardDetails">
                      <span><i aria-hidden="true">{s.kullanimDurumu === "rented" ? "🔑" : "📌"}</i>{t(`finance.realEstate.use.${s.kullanimDurumu || "owner_occupied"}`)}</span>
                      <span><i aria-hidden="true">◈</i>{t("finance.realEstate.netEquity")}: <b>{financeMoneyFormat(netEquity)}</b></span>
                      {Number(s.aylikKiraGeliri || 0) > 0 && <span><i aria-hidden="true">↗</i>{t("finance.realEstate.monthlyRent")}: <b>{financeMoneyFormat(Number(s.aylikKiraGeliri))}</b></span>}
                    </div>
                  )}
                  {!isAutoMarketPricedSaving(s) && savingUpdatePanelId === s.id && (
                    <div className="assetUpdatePanel savingInlineUpdatePanel noSwipeBack" data-no-swipe-back="true">
                      <div>
                        <span>{realEstate ? t("finance.realEstate.currentValue") : t("finance.savings.updatePrice")}</span>
                        <small>{realEstate ? t("finance.realEstate.updateValueHint") : t("finance.savings.currentPricePlaceholder")}</small>
                      </div>
                      <input
                        className="assetUpdateInput premium"
                        {...moneyInputProps}
                        placeholder={realEstate ? t("finance.realEstate.currentValue") : t("finance.savings.currentPricePlaceholder")}
                        value={savingUpdateDrafts[s.id] ?? formatMoneyInput(s.guncelFiyat ?? s.alisFiyati ?? "")}
                        onChange={(e) => setSavingUpdateDrafts((prev) => ({ ...prev, [s.id]: formatMoneyInput(e.target.value) }))}
                      />
                      <button
                        type="button"
                        className="smallPremiumBtn compact savingInlineUpdateBtn"
                        onClick={async () => {
                          const nextPrice = savingUpdateDrafts[s.id] ?? s.guncelFiyat ?? s.alisFiyati ?? "";
                          await updateSaving(s, { guncelFiyat: nextPrice });
                          setSavingUpdatePanelId(null);
                          setSavingUpdateDrafts((prev) => {
                            const next = { ...prev };
                            delete next[s.id];
                            return next;
                          });
                        }}
                      >
                        {realEstate ? t("finance.realEstate.updateValue") : t("finance.savings.update")}
                      </button>
                    </div>
                  )}
                  {s.not && <ExpandableText text={s.not} className="assetNoteText" maxLength={56} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {aktifSekme === "taksitler" && (
        <>
          <div className="financeSubHero installmentHero">
            <div>
              <span className="financeEyebrow">{t("finance.installments.hero")}</span>
              <h2 className="amountRed">{financeMoneyFormat(installmentSummary.buAy)}</h2>
              <p>{t("finance.installments.heroDesc")}</p>
            </div>
            <button className="smallPremiumBtn" onClick={handleFinanceBack}>{t("finance.backToFinance")}</button>
          </div>
          <div className="darkCard unifiedPremiumAddCard premiumFormCard">
            <button className="unifiedPremiumAddHeader" type="button" onClick={() => setInstallmentFormAcik(!installmentFormAcik)} aria-expanded={installmentFormAcik}>
              <span className="unifiedPremiumAddIcon" aria-hidden="true">+</span>
              <span className="unifiedPremiumAddTitle">{installmentEditId ? t("common.edit") : t("finance.installments.add")}</span>
              <span className="unifiedPremiumAddArrow" aria-hidden="true">{installmentFormAcik ? "⌃" : "⌄"}</span>
            </button>
            {installmentFormAcik && (
              <div className="formBody inlineToolForm">
                <input className={financeFieldClass("installment", "ad", "darkInput")} data-finance-error="installment-ad" aria-invalid={hasFinanceFieldError("installment", "ad")} placeholder={t("finance.installments.namePlaceholder")} value={installmentDraft.ad} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "ad", e.target.value)} />
                <select className="darkInput" value={installmentDraft.kategori} onChange={(e) => handleInstallmentCategoryChange(e.target.value)}>{taksitKategorileri.map((kat) => <option key={kat} value={kat}>{installmentCategoryLabel(kat)}</option>)}</select>
                {installmentDebtKind === "standard" ? (
                  <>
                    <input className={financeFieldClass("installment", "toplam", "darkInput")} data-finance-error="installment-toplam" aria-invalid={hasFinanceFieldError("installment", "toplam")} {...moneyInputProps} placeholder={t("finance.installments.totalPlaceholder")} value={installmentDraft.toplam} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "toplam", formatMoneyInput(e.target.value))} />
                    <input className={financeFieldClass("installment", "taksitSayisi", "darkInput")} data-finance-error="installment-taksitSayisi" aria-invalid={hasFinanceFieldError("installment", "taksitSayisi")} type="number" min="1" max={MAX_STANDARD_INSTALLMENT_TOTAL} step="1" placeholder={t("finance.installments.countPlaceholder")} value={installmentDraft.taksitSayisi} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "taksitSayisi", e.target.value)} />
                    <input className={financeFieldClass("installment", "odenenTaksit", "darkInput")} data-finance-error="installment-odenenTaksit" aria-invalid={hasFinanceFieldError("installment", "odenenTaksit")} type="number" min="0" max={Math.max(0, Number(installmentDraft.taksitSayisi) || 0)} step="1" placeholder={t("finance.installments.paidCountPlaceholder")} value={installmentDraft.odenenTaksit} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "odenenTaksit", e.target.value)} />
                    <input className="darkInput" {...moneyInputProps} placeholder={t("finance.installments.monthlyPlaceholder")} value={installmentDraft.aylikOdeme} onChange={handleMoneyDraftChange(setInstallmentDraft, "aylikOdeme")} />
                  </>
                ) : (
                  <div className="financeDebtFormSection">
                    <div className={`financeDebtIntro ${installmentDebtKind === "gold" ? "gold" : "fx"}`}>
                      <span aria-hidden="true">{installmentDebtKind === "gold" ? "✦" : "↗"}</span>
                      <div><b>{installmentDebtKind === "gold" ? t("finance.debt.gold.title") : t("finance.debt.fx.title")}</b><small>{t(`finance.debt.${installmentDebtKind === "gold" ? "gold" : "fx"}.hint`)}</small></div>
                    </div>
                    {installmentDebtKind === "gold" ? (
                      <div className="financeGoldDebtItems">
                        {(installmentDraft.goldItems || []).map((item, index) => {
                          const option = goldDebtOptions.find((candidate) => candidate.value === item.type) || goldDebtOptions[0];
                          return (
                            <div className="financeGoldDebtRow" key={item.id || index}>
                              <div className="financeDebtRowHead"><b>{t("finance.debt.gold.item")} {index + 1}</b>{installmentDraft.goldItems.length > 1 && <button type="button" onClick={() => removeGoldDebtItem(index)}>{t("common.delete")}</button>}</div>
                              <select className="darkInput" value={item.type} onChange={(e) => updateGoldDebtItem(index, { type: e.target.value })}>{goldDebtOptions.map((goldOption) => <option key={goldOption.value} value={goldOption.value}>{goldTypeLabel(goldOption.value)}</option>)}</select>
                              <input className={financeFieldClass("installment", `goldItem${index}`, "darkInput")} data-finance-error={`installment-goldItem${index}`} aria-invalid={hasFinanceFieldError("installment", `goldItem${index}`)} {...moneyInputProps} placeholder={item.unit === "piece" ? t("finance.debt.gold.quantityPieces") : item.unit === "ounce" ? t("finance.debt.gold.quantityOunce") : t("finance.debt.gold.quantityGram")} value={item.quantity} onChange={(e) => updateGoldDebtItem(index, { quantity: formatMoneyInput(e.target.value) })} />
                              {option.requiresKarat && <select className={financeFieldClass("installment", `goldKarat${index}`, "darkInput")} data-finance-error={`installment-goldKarat${index}`} aria-invalid={hasFinanceFieldError("installment", `goldKarat${index}`)} value={item.karat || ""} onChange={(e) => updateGoldDebtItem(index, { karat: e.target.value })}><option value="">{t("finance.debt.gold.karat")}</option>{[9,10,14,18,21,22,24].map((karat) => <option key={karat} value={karat}>{karat}K</option>)}</select>}
                              {option.requiresPurity && <select className={financeFieldClass("installment", `goldPurity${index}`, "darkInput")} data-finance-error={`installment-goldPurity${index}`} aria-invalid={hasFinanceFieldError("installment", `goldPurity${index}`)} value={item.purity || ""} onChange={(e) => updateGoldDebtItem(index, { purity: e.target.value })}><option value="">{t("finance.debt.gold.purity")}</option>{[999.9,995,916,750,585].map((purity) => <option key={purity} value={purity}>{purity}</option>)}</select>}
                            </div>
                          );
                        })}
                        <button className="financeDebtAddLine" type="button" onClick={addGoldDebtItem}>＋ {t("finance.debt.gold.addItem")}</button>
                      </div>
                    ) : (
                      <div className="financeDebtTwoColumn">
                        <select className={financeFieldClass("installment", "debtCurrency", "darkInput")} data-finance-error="installment-debtCurrency" aria-invalid={hasFinanceFieldError("installment", "debtCurrency")} value={installmentDraft.debtCurrency || "USD"} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "debtCurrency", e.target.value)}>{["USD","EUR","GBP","CHF","CAD","AUD","JPY","TRY"].map((code) => <option key={code} value={code}>{code}</option>)}</select>
                        <input className={financeFieldClass("installment", "foreignPrincipal", "darkInput")} data-finance-error="installment-foreignPrincipal" aria-invalid={hasFinanceFieldError("installment", "foreignPrincipal")} {...moneyInputProps} placeholder={t("finance.debt.fx.principal")} value={installmentDraft.foreignPrincipal} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "foreignPrincipal", formatMoneyInput(e.target.value))} />
                      </div>
                    )}
                    <div className="financeDebtTwoColumn">
                      <input className={financeFieldClass("installment", "monthlyPaymentAmount", "darkInput")} data-finance-error="installment-monthlyPaymentAmount" aria-invalid={hasFinanceFieldError("installment", "monthlyPaymentAmount")} {...moneyInputProps} placeholder={t("finance.debt.monthlyPayment")} value={installmentDraft.monthlyPaymentAmount} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "monthlyPaymentAmount", formatMoneyInput(e.target.value))} />
                      <select className="darkInput" value={installmentDraft.monthlyPaymentCurrency || normalizedBaseCurrency} onChange={(e) => setInstallmentDraft({ ...installmentDraft, monthlyPaymentCurrency: e.target.value })}>{Array.from(new Set([normalizedBaseCurrency,"TRY","USD","EUR","GBP","CHF","CAD","AUD","JPY"])).map((code) => <option key={code} value={code}>{code}</option>)}</select>
                    </div>
                  </div>
                )}
                <DateInputField label={t("finance.installments.startDatePlaceholder")} value={installmentDraft.baslangic} onChange={(value) => setInstallmentDraft({ ...installmentDraft, baslangic: value })} />
                <input className={financeFieldClass("installment", "odemeGunu", "darkInput")} data-finance-error="installment-odemeGunu" aria-invalid={hasFinanceFieldError("installment", "odemeGunu")} type="number" min="1" max="31" placeholder={t("finance.installments.dueDayPlaceholder")} value={installmentDraft.odemeGunu} onChange={(e) => updateFinanceDraftField(setInstallmentDraft, "installment", "odemeGunu", e.target.value)} />
                <input className="darkInput" placeholder={t("finance.installments.notePlaceholder")} value={installmentDraft.not} onChange={(e) => setInstallmentDraft({ ...installmentDraft, not: e.target.value })} />
                {renderFinanceNotificationTarget(installmentDraft, setInstallmentDraft)}
                {isDebtInstallmentDraft && <div className="financeDebtReminder"><ReminderWarning message={installmentReminder.aktif ? installmentReminderError : ""} /><ReminderPanel aktif={installmentReminder.aktif} setAktif={(aktif) => { setInstallmentReminder((current) => ({ ...current, aktif })); if (!aktif) setInstallmentReminderError(""); }} tarih={installmentReminder.tarih} setTarih={(tarih) => setInstallmentReminder((current) => ({ ...current, tarih }))} saat={installmentReminder.saat} setSaat={(saat) => setInstallmentReminder((current) => ({ ...current, saat }))} tekrar={installmentReminder.tekrar} setTekrar={(tekrar) => setInstallmentReminder((current) => ({ ...current, tekrar }))} mod="datetime" etiket={t("finance.debt.setReminder")} /></div>}
                {renderInstallmentValidationMessage()}
                <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={financeSubmitting.installment} busyText={installmentEditId ? t("common.saving") : undefined} onAction={saveInstallment}>{installmentEditId ? t("common.save") : t("finance.installments.add")}</AsyncActionButton>
                {installmentEditId && <button className="smallPremiumBtn compact" type="button" onClick={resetInstallmentDraft}>{t("common.cancel")}</button>}
              </div>
            )}
          </div>
          <div className="budgetList financeDetailList financeInstallmentList">
            {installments.length === 0 && (
              <div className="financeToolEmptyPremium" role="status" aria-live="polite">
                <img className="financeToolEmptyPremiumImage" src={finansAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" draggable="false" />
                <h3 className="financeToolEmptyPremiumTitle">
                  {lang === "en" ? "No installments yet" : "Henüz taksit kaydı yok"}
                </h3>
                <p className="financeToolEmptyPremiumText">
                  {lang === "en"
                    ? "Add your installment plans and keep every payment comfortably on track."
                    : "Taksit planlarınızı ekleyin, tüm ödemelerinizi rahatça ve düzenli şekilde takip edin."}
                </p>
              </div>
            )}
            {newestInstallments.map((ins) => {
              const isDebt = ins.debtKind === "gold" || ins.debtKind === "foreign_currency";
              const valuationCurrency = normalizeCurrencyCode(ins.valuationCurrency || ins.paraBirimi || normalizedBaseCurrency);
              const aylik = Number(ins.monthlyPaymentAmount || ins.aylikOdeme || 0) || (Number(ins.toplam || 0) / Math.max(1, Number(ins.taksitSayisi || 1)));
              const kalanTaksit = Math.max(0, Number(ins.taksitSayisi || 0) - Number(ins.odenenTaksit || 0));
              const currentDebtValue = isDebt ? currentDebtValueFor(ins) : kalanTaksit * aylik;
              const originalDebtValue = ins.debtKind === "gold"
                ? (goldDebtValue(ins.originalGoldItems || ins.goldItems || [], marketPrices, valuationCurrency) ?? Number(ins.toplam || currentDebtValue))
                : ins.debtKind === "foreign_currency"
                  ? (convertDebtCurrency(Number(ins.originalForeignPrincipal ?? ins.foreignPrincipal ?? 0), ins.debtCurrency || "USD", valuationCurrency, marketPrices) || Number(ins.toplam || currentDebtValue))
                  : Number(ins.toplam || 0);
              const pct = isDebt ? Math.max(0, Math.min(100, originalDebtValue > 0 ? ((originalDebtValue - currentDebtValue) / originalDebtValue) * 100 : 0)) : (Number(ins.taksitSayisi || 0) > 0 ? Math.min(100, (Number(ins.odenenTaksit || 0) / Number(ins.taksitSayisi || 1)) * 100) : 0);
              const estimatedMonths = isDebt && aylik > 0 ? Math.max(0, Math.ceil(currentDebtValue / Math.max(1, convertDebtCurrency(aylik, ins.monthlyPaymentCurrency || valuationCurrency, valuationCurrency, marketPrices) || aylik))) : kalanTaksit;
              const estimatedEndDate = new Date();
              estimatedEndDate.setMonth(estimatedEndDate.getMonth() + estimatedMonths);
              const paymentDraft = isDebt ? debtPaymentDraftFor(ins) : null;
              const remainingGoldItems = ins.remainingGoldItems || ins.goldItems || [];
              return (
                <div className={`premiumInstallmentTile${isDebt ? ` financeDebtTile ${ins.debtKind}` : ""}`} key={ins.id}>
                  <div className="installmentTileTop">
                    <div className="assetAvatar installment">{ins.debtKind === "gold" ? "✦" : ins.debtKind === "foreign_currency" ? "↗" : "🧾"}</div>
                    <div className="assetTitleBlock">
                      <ExpandableText text={ins.ad} className="assetTileTitle" maxLength={32} />
                      <span>{installmentCategoryLabel(ins.kategori)}{ins.odemeGunu ? ` · ${t("finance.installments.eachMonth")} ${ins.odemeGunu}${t("finance.installments.day")}` : ""}</span>
                    </div>
                    <div className="subTileActions financeInstallmentTileActions">
                      <FinanceRowActionMenu
                        menuId={`installment-${ins.id}`}
                        onEdit={() => startEditInstallment(ins)}
                        onDelete={() => deleteInstallment(ins.id)}
                      />
                    </div>
                  </div>
                  <div className="installmentProgressHeader">
                    <span>{isDebt ? t("finance.debt.progress") : `${Number(ins.odenenTaksit || 0)} / ${ins.taksitSayisi} ${t("finance.installments.paidOf")}`}</span>
                    <b>%{pct.toFixed(0)}</b>
                  </div>
                  <button
                    type="button"
                    className="installmentProgressTrack installmentProgressButton"
                    onClick={() => !isDebt && setInstallmentProgressPanelId(installmentProgressPanelId === ins.id ? null : ins.id)}
                    aria-expanded={installmentProgressPanelId === ins.id}
                    aria-label={isDebt ? t("finance.debt.progress") : (lang === "en" ? "Update paid installment count" : "Ödenen taksit sayısını güncelle")}
                  >
                    <span style={{ width: `${pct}%` }} />
                  </button>
                  {!isDebt && installmentProgressPanelId === ins.id && (
                    <div className="installmentStepPicker noSwipeBack" data-no-swipe-back="true">
                      {Array.from({ length: Math.max(0, Number(ins.taksitSayisi || 0)) }, (_, i) => {
                        const step = i + 1;
                        const paid = step <= Number(ins.odenenTaksit || 0);
                        return (
                          <button
                            key={step}
                            type="button"
                            className={paid ? "paid" : ""}
                            onClick={() => updateInstallmentPaidCount(ins, step)}
                            title={lang === "en" ? `Mark ${step}. installment as paid` : `${step}. taksiti ödenmiş işaretle`}
                          >
                            <span>{step}</span>
                            {paid && <b>✓</b>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="assetMetricGrid installmentMetrics">
                    <div><span>{t("finance.installments.monthlyPayment")}</span><b className="amountRed">{financeAmountsHidden ? "••••••" : isDebt ? debtMoneyFormat(aylik, ins.monthlyPaymentCurrency || valuationCurrency, lang) : financeMoneyFormat(aylik)}</b></div>
                    <div><span>{isDebt ? t("finance.debt.estimatedMonths") : t("finance.installments.remaining")}</span><b>{isDebt ? estimatedMonths : kalanTaksit}</b></div>
                    <div><span>{t("finance.installments.remainingDebt")}</span><b className="amountRed">{financeAmountsHidden ? "••••••" : isDebt ? debtMoneyFormat(currentDebtValue, valuationCurrency, lang) : financeMoneyFormat(kalanTaksit * aylik)}</b></div>
                  </div>
                  {isDebt && <div className="financeDebtBreakdown">
                    {ins.debtKind === "gold" ? remainingGoldItems.map((item, index) => <span key={`${item.assetKey}-${index}`}><b>{financeAmountsHidden ? "••••••" : formatMoneyDisplayNumber(Number(item.quantity || 0), 4)}</b> {goldTypeLabel(item.type)}{item.karat ? ` · ${item.karat}K` : ""}{item.purity ? ` · ${item.purity}` : ""}</span>) : <span><b>{financeAmountsHidden ? "••••••" : `${formatMoneyDisplayNumber(Number(ins.remainingForeignAmount ?? ins.foreignPrincipal ?? 0), 2)} ${ins.debtCurrency}`}</b> {t("finance.debt.remainingPrincipal")}</span>}
                  </div>}
                  {isDebt && <div className="financeDebtMeta"><span>🗓 {t("finance.debt.nextPayment")}: <b>{tarihOkunakli(ins.nextDueDate || ins.baslangic, AYLAR)}</b></span><span>◎ {t("finance.debt.estimatedEnd")}: <b>{estimatedEndDate.toLocaleDateString(lang === "en" ? "en-US" : "tr-TR", { month: "long", year: "numeric" })}</b></span><span className={marketPriceIsStale ? "stale" : ""}>↻ {marketPriceIsStale ? t("finance.debt.marketMayBeStale") : `${t("finance.debt.marketUpdated")}: ${new Date(marketUpdatedAt).toLocaleString(lang === "en" ? "en-US" : "tr-TR")}`}</span></div>}
                  {isDebt && <button className="financeDebtPaymentButton" type="button" onClick={() => openDebtPayment(ins)}>＋ {t("finance.debt.addPayment")}</button>}
                  {isDebt && debtPaymentPanelId === ins.id && <div className="financeDebtPaymentPanel noSwipeBack" data-no-swipe-back="true">
                    <div className="financeDebtPaymentTitle"><b>{t("finance.debt.paymentTitle")}</b><button type="button" onClick={() => setDebtPaymentPanelId(null)}>×</button></div>
                    {ins.debtKind === "gold" && <select className="darkInput" value={paymentDraft.method} onChange={(e) => setDebtPaymentDraft(ins.id, (current) => ({ ...current, method: e.target.value }))}><option value="money">{t("finance.debt.payment.money")}</option><option value="gold">{t("finance.debt.payment.gold")}</option><option value="mixed">{t("finance.debt.payment.mixed")}</option></select>}
                    {(paymentDraft.method === "money" || paymentDraft.method === "mixed" || ins.debtKind === "foreign_currency") && <div className="financeDebtTwoColumn"><input className="darkInput" {...moneyInputProps} placeholder={t("finance.debt.payment.amount")} value={paymentDraft.amount} onChange={(e) => setDebtPaymentDraft(ins.id, (current) => ({ ...current, amount: formatMoneyInput(e.target.value) }))} /><select className="darkInput" value={paymentDraft.currency} onChange={(e) => setDebtPaymentDraft(ins.id, (current) => ({ ...current, currency: e.target.value }))}>{Array.from(new Set([ins.debtCurrency, normalizedBaseCurrency,"TRY","USD","EUR","GBP"].filter(Boolean))).map((code) => <option key={code} value={code}>{code}</option>)}</select></div>}
                    {ins.debtKind === "gold" && (paymentDraft.method === "gold" || paymentDraft.method === "mixed") && <div className="financeDebtPaymentGoldItems">{(paymentDraft.goldItems || []).map((goldItem, index) => {
                      const identity = `${goldItem.assetKey}|${goldItem.unit}|${goldItem.karat || ""}|${goldItem.purity || ""}|${goldItem.label || ""}`;
                      return <div className="financeDebtTwoColumn" key={`${identity}-${index}`}><select className="darkInput" value={identity} onChange={(e) => updateDebtPaymentGoldItem(ins, index, e.target.value)}>{remainingGoldItems.map((remaining, remainingIndex) => { const value = `${remaining.assetKey}|${remaining.unit}|${remaining.karat || ""}|${remaining.purity || ""}|${remaining.label || ""}`; return <option key={`${value}-${remainingIndex}`} value={value}>{goldTypeLabel(remaining.type)} · {formatMoneyDisplayNumber(Number(remaining.quantity || 0), 4)}</option>; })}</select><input className="darkInput" {...moneyInputProps} placeholder={t("finance.debt.gold.amount")} value={goldItem.quantity} onChange={(e) => setDebtPaymentDraft(ins.id, (current) => { const items = [...current.goldItems]; items[index] = { ...items[index], quantity: formatMoneyInput(e.target.value) }; return { ...current, goldItems: items }; })} /></div>;
                    })}<button className="financeDebtAddLine" type="button" onClick={() => setDebtPaymentDraft(ins.id, (current) => ({ ...current, goldItems: [...current.goldItems, { ...(remainingGoldItems[0] || newGoldDebtItem(debtRegion)), quantity: "" }] }))}>＋ {t("finance.debt.gold.addPaymentItem")}</button></div>}
                    <DateInputField label={t("finance.debt.payment.date")} value={paymentDraft.paidAt} onChange={(paidAt) => setDebtPaymentDraft(ins.id, (current) => ({ ...current, paidAt }))} />
                    <input className="darkInput" placeholder={t("finance.debt.payment.note")} value={paymentDraft.note} onChange={(e) => setDebtPaymentDraft(ins.id, (current) => ({ ...current, note: e.target.value }))} />
                    <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={debtPaymentSubmittingId === ins.id} busyText={t("common.saving")} onAction={() => saveDebtPayment(ins)}>{t("finance.debt.payment.save")}</AsyncActionButton>
                  </div>}
                  {isDebt && (ins.debtPayments || []).length > 0 && <div className="financeDebtPaymentHistory"><b>{t("finance.debt.payment.history")}</b>{[...(ins.debtPayments || [])].slice(-3).reverse().map((payment) => <div key={payment.id}><span>{new Date(payment.paidAt).toLocaleDateString(lang === "en" ? "en-US" : "tr-TR")}</span><strong>{financeAmountsHidden ? "••••••" : payment.method === "gold" ? (payment.goldItems || []).map((item) => `${formatMoneyDisplayNumber(Number(item.quantity || 0), 4)} ${goldTypeLabel(item.type)}`).join(" + ") : payment.method === "mixed" ? `${debtMoneyFormat(payment.amount, payment.currency, lang)} + ${(payment.goldItems || []).map((item) => `${formatMoneyDisplayNumber(Number(item.quantity || 0), 4)} ${goldTypeLabel(item.type)}`).join(" + ")}` : debtMoneyFormat(payment.amount, payment.currency, lang)}</strong></div>)}</div>}
                  {isDebt && <small className="financeDebtEstimateNote">{t("finance.debt.estimateDisclaimer")}</small>}
                  {ins.not && <ExpandableText text={ins.not} className="assetNoteText" maxLength={56} />}
                </div>
              );
            })}
          </div>
        </>
      )}

      {aktifSekme === "abonelikler" && (
        <div className="financeSubscriptionsSection">
          <div className="financeSubHero subscriptionHero">
            <div>
              <span className="financeEyebrow">{t("finance.subs.hero")}</span>
              <h2 className={subscriptionSummary.aylikToplam > 0 ? "amountRed" : ""}>{financeMoneyFormat(subscriptionSummary.aylikToplam)}</h2>
              <p>{t("finance.subs.heroDesc")} · {subscriptionSummary.aktif.length} {t("finance.subs.activeCount")}</p>
            </div>
            <button className="smallPremiumBtn" onClick={handleFinanceBack}>{t("finance.backToFinance")}</button>
          </div>

          {canAdd && (
            <div className="darkCard unifiedPremiumAddCard premiumFormCard">
              <button className="unifiedPremiumAddHeader" type="button" onClick={() => setSubFormAcik(!subFormAcik)} aria-expanded={subFormAcik}>
                <span className="unifiedPremiumAddIcon" aria-hidden="true">+</span>
                <span className="unifiedPremiumAddTitle">{subscriptionEditId ? t("common.edit") : t("finance.subs.add")}</span>
                <span className="unifiedPremiumAddArrow" aria-hidden="true">{subFormAcik ? "⌃" : "⌄"}</span>
              </button>
              {subFormAcik && (
                <div className="formBody inlineToolForm">
                  <div className="formRow">
                    <div className={financeFieldClass("subscription", "ad", "inputLabelGroup")} style={{ flex: 2 }} data-finance-error="subscription-ad">
                      <label className="darkLabel">{t("finance.subs.platform")}</label>
                      <select className="darkInput" value={subDraft.ad} onChange={(e) => {
                        updateFinanceDraftField(setSubDraft, "subscription", "ad", e.target.value);
                        if (!isOtherValue(e.target.value)) clearFinanceFieldError("subscription", "adManuel");
                      }} aria-invalid={hasFinanceFieldError("subscription", "ad")}>
                        <option value="">{t("finance.subs.selectPlatform")}</option>
                        {abonelikPlatformlari.map((p) => <option key={p} value={p}>{fl(lang, p)}</option>)}
                      </select>
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("common.category")}</label>
                      <select className="darkInput" value={subDraft.kategori} onChange={(e) => setSubDraft({ ...subDraft, kategori: e.target.value })}>
                        {abonelikKategorileri.map((k) => <option key={k} value={k}>{fl(lang, k)}</option>)}
                      </select>
                    </div>
                  </div>
                  {isOtherValue(subDraft.ad) && (
                    <input className={financeFieldClass("subscription", "adManuel", "darkInput")} data-finance-error="subscription-adManuel" aria-invalid={hasFinanceFieldError("subscription", "adManuel")} placeholder={t("finance.subs.manualName")} value={subDraft.adManuel || ""} onChange={(e) => updateFinanceDraftField(setSubDraft, "subscription", "adManuel", e.target.value)} />
                  )}
                  <div className="formRow">
                    <div className={financeFieldClass("subscription", "tutar", "inputLabelGroup")} style={{ flex: 2 }} data-finance-error="subscription-tutar">
                      <label className="darkLabel">{t("finance.subs.amount")}</label>
                      <input className="darkInput" {...moneyInputProps} placeholder="0" value={subDraft.tutar} onChange={(e) => updateFinanceDraftField(setSubDraft, "subscription", "tutar", formatMoneyInput(e.target.value))} aria-invalid={hasFinanceFieldError("subscription", "tutar")} />
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.subs.currency")}</label>
                      <select className="darkInput" value={subDraft.paraBirimi} onChange={(e) => setSubDraft({ ...subDraft, paraBirimi: e.target.value })}>
                        {paraBirimleri.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="formRow">
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.subs.renewalPeriod")}</label>
                      <select className="darkInput" value={subDraft.periyot} onChange={(e) => setSubDraft({ ...subDraft, periyot: e.target.value })}>
                        {periyotlar.map((p) => <option key={p} value={p}>{fl(lang, p)}</option>)}
                      </select>
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.subs.nextPayment")}</label>
                      <input className="darkInput" type="date" value={subDraft.sonrakiOdeme} onChange={(e) => setSubDraft({ ...subDraft, sonrakiOdeme: e.target.value })} />
                    </div>
                  </div>
                  <div className="formRow">
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.subs.payer")}</label>
                      <input className="darkInput" placeholder={t("finance.subs.payerPlaceholder")} value={subDraft.odemeSahibi} onChange={(e) => setSubDraft({ ...subDraft, odemeSahibi: e.target.value })} />
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.subs.status")}</label>
                      <select className="darkInput" value={subDraft.aktif} onChange={(e) => setSubDraft({ ...subDraft, aktif: e.target.value })}>
                        <option value={lang === "en" ? "Yes" : "Evet"}>{t("finance.subs.statusActive")}</option>
                        <option value={lang === "en" ? "No" : "Hayır"}>{t("finance.subs.statusInactive")}</option>
                      </select>
                    </div>
                  </div>
                  <input className="darkInput" placeholder={t("finance.subs.notePlaceholder")} value={subDraft.not} onChange={(e) => setSubDraft({ ...subDraft, not: e.target.value })} />
                  {renderFinanceNotificationTarget(subDraft, setSubDraft)}
                  {renderFinanceValidationMessage("subscription", financeFormErrors.subscription.map((field) => ({ ad: t("finance.subs.platform"), adManuel: t("finance.subs.manualName"), tutar: t("finance.subs.amount") }[field])).filter(Boolean))}
                  <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={financeSubmitting.subscription} busyText={subscriptionEditId ? t("common.saving") : undefined} onAction={saveSubscriptionEdit}>{subscriptionEditId ? t("common.save") : t("finance.subs.add")}</AsyncActionButton>
                  {subscriptionEditId && <button className="smallPremiumBtn compact" type="button" onClick={resetSubscriptionDraft}>{t("common.cancel")}</button>}
                </div>
              )}
            </div>
          )}

          <div className="budgetList financeDetailList financeSubscriptionList">
            {subscriptions.length === 0 && (
              <div className="financeToolEmptyPremium" role="status" aria-live="polite">
                <img className="financeToolEmptyPremiumImage" src={finansAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" draggable="false" />
                <h3 className="financeToolEmptyPremiumTitle">
                  {lang === "en" ? "No subscriptions yet" : "Henüz abonelik yok"}
                </h3>
                <p className="financeToolEmptyPremiumText">
                  {lang === "en"
                    ? "Add the services you use and stay one step ahead of renewal dates."
                    : "Kullandığınız hizmetleri ekleyin, yenileme tarihlerini gözden kaçırmadan takip edin."}
                </p>
              </div>
            )}
            {newestSubscriptions.map((s) => {
              const tutar = Number(s.tutar || 0);
              const bazTutar = baseAmountFor(s);
              const aylikMaliyet = monthlyCostFromPeriod(bazTutar ?? 0, s.periyot);
              const yillikMaliyet = yearlyCostFromPeriod(bazTutar ?? 0, s.periyot);
              const aktifMi = !isInactiveValue(s.aktif);
              const platformAdi = isOtherValue(s.ad) && s.adManuel ? s.adManuel : s.ad;
              const subIcon = {
                Netflix: "🎬", Spotify: "🎵", "Apple TV+": "🍎", "Disney+": "✨",
                "YouTube Premium": "▶️", "Amazon Prime": "📦", Exxen: "🎭", BluTV: "🎥",
                "Apple Music": "🎵", "Apple One": "🍎", "iCloud+": "☁️", "Google One": "🔵",
                "Microsoft 365": "💼", "Adobe CC": "🎨", Notion: "📝", ChatGPT: "🤖",
                LinkedIn: "💼", "Gym/Spor": "💪",
              }[s.ad] || "🔄";

              return (
                <div className={`premiumSubscriptionTile ${!aktifMi ? "subscriptionPasif" : ""}`} key={s.id}>
                  <div className="assetTileTop">
                    <div className={`assetAvatar subscription ${!aktifMi ? "pasif" : ""}`}>{subIcon}</div>
                    <div className="assetTitleBlock">
                      <span className="assetTileTitle">{platformAdi}</span>
                      <span>{fl(lang, s.kategori)} · {fl(lang, s.periyot)}{s.odemeSahibi ? ` · ${s.odemeSahibi}` : ""}</span>
                    </div>
                    <div className="subTileActions">
                      <button
                        className={`subToggleBtn ${aktifMi ? "aktif" : "pasif"}`}
                        onClick={() => toggleSubscriptionAktif(s)}
                        title={aktifMi ? t("finance.subs.togglePassive") : t("finance.subs.toggleActive")}
                      >{aktifMi ? t("finance.subs.activeLabel") : t("finance.subs.passiveLabel")}</button>
                      <FinanceRowActionMenu
                        menuId={`subscription-${s.id}`}
                        onEdit={() => startEditSubscription(s)}
                        onDelete={() => deleteSubscription(s.id)}
                      />
                    </div>
                  </div>
                  <div className="assetMetricGrid">
                    <div>
                      <span>{t("common.amount")}</span>
                      <b className="amountRed">{financeAmountsHidden ? "••••••" : `${formatMoneyDisplayNumber(tutar, 2)} ${s.paraBirimi || "TRY"}`}</b>
                      {normalizeCurrencyCode(s.paraBirimi || normalizedBaseCurrency) !== normalizedBaseCurrency && (
                        <small>{bazTutar === null ? t("finance.currency.rateUnavailable") : `≈ ${financeMoneyFormat(bazTutar)}`}</small>
                      )}
                    </div>
                    <div>
                      <span>{t("finance.subs.monthlyCost")}</span>
                      <b className="amountRed">{financeMoneyFormat(aylikMaliyet)}</b>
                    </div>
                    <div>
                      <span>{t("finance.subs.yearlyCost")}</span>
                      <b className="amountRed">{financeMoneyFormat(yillikMaliyet)}</b>
                    </div>
                  </div>
                  {s.sonrakiOdeme && (
                    <div className="subNextPayment">
                      <span>📅 {t("finance.subs.nextPaymentLabel")}</span>
                      <b>{tarihOkunakli(s.sonrakiOdeme, AYLAR)}</b>
                    </div>
                  )}
                  {s.not && <ExpandableText text={s.not} className="assetNoteText" maxLength={56} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {aktifSekme === "faturalar" && (
        <div className="financeBillsSection">
          <div className="financeSubHero billsHero">
            <div>
              <span className="financeEyebrow">{t("finance.bills.hero")}</span>
              <h2 className={billSummary.toplam > 0 ? "amountRed" : ""}>{financeMoneyFormat(billSummary.toplam)}</h2>
              <p>{t("finance.bills.heroDesc")} · {billSummary.odenmemis.length} {t("finance.bills.pendingCount")}</p>
            </div>
            <button className="smallPremiumBtn" onClick={handleFinanceBack}>{t("finance.backToFinance")}</button>
          </div>

          {canAdd && (
            <div className="darkCard unifiedPremiumAddCard premiumFormCard">
              <button className="unifiedPremiumAddHeader" type="button" onClick={() => setBillFormAcik(!billFormAcik)} aria-expanded={billFormAcik}>
                <span className="unifiedPremiumAddIcon" aria-hidden="true">+</span>
                <span className="unifiedPremiumAddTitle">{billEditId ? t("common.edit") : t("finance.bills.add")}</span>
                <span className="unifiedPremiumAddArrow" aria-hidden="true">{billFormAcik ? "⌃" : "⌄"}</span>
              </button>
              {billFormAcik && (
                <div className="formBody inlineToolForm">
                  <div className="formRow">
                    <div className="inputLabelGroup" style={{ flex: 2 }}>
                      <label className="darkLabel">{t("finance.bills.type")}</label>
                      <select className="darkInput" value={billDraft.kategori} onChange={(e) => {
                        setBillDraft({ ...billDraft, kategori: e.target.value, ad: isOtherValue(e.target.value) ? "" : e.target.value });
                        if (!isOtherValue(e.target.value)) clearFinanceFieldError("bill", "ad");
                      }}>
                        {faturaKategorileri.map((k) => <option key={k} value={k}>{fl(lang, k)}</option>)}
                      </select>
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.bills.period")}</label>
                      <select className="darkInput" value={billDraft.periyot} onChange={(e) => setBillDraft({ ...billDraft, periyot: e.target.value })}>
                        {periyotlar.map((p) => <option key={p} value={p}>{fl(lang, p)}</option>)}
                      </select>
                    </div>
                  </div>
                  {isOtherValue(billDraft.kategori) && (
                    <input className={financeFieldClass("bill", "ad", "darkInput")} data-finance-error="bill-ad" aria-invalid={hasFinanceFieldError("bill", "ad")} placeholder={t("finance.bills.manualName")} value={billDraft.ad} onChange={(e) => updateFinanceDraftField(setBillDraft, "bill", "ad", e.target.value)} />
                  )}
                  <div className="formRow">
                    <div className={financeFieldClass("bill", "tutar", "inputLabelGroup")} style={{ flex: 2 }} data-finance-error="bill-tutar">
                      <label className="darkLabel">{t("finance.bills.amount")}</label>
                      <input className="darkInput" {...moneyInputProps} placeholder="0" value={billDraft.tutar} onChange={(e) => updateFinanceDraftField(setBillDraft, "bill", "tutar", formatMoneyInput(e.target.value))} aria-invalid={hasFinanceFieldError("bill", "tutar")} />
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.bills.currency")}</label>
                      <select className="darkInput" value={billDraft.paraBirimi} onChange={(e) => setBillDraft({ ...billDraft, paraBirimi: e.target.value })}>
                        {paraBirimleri.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="formRow">
                    <div className={financeFieldClass("bill", "sonOdemeTarihi", "inputLabelGroup")} style={{ flex: 1 }} data-finance-error="bill-sonOdemeTarihi">
                      <label className="darkLabel">{t("finance.bills.dueDate2")}</label>
                      <input className="darkInput" type="date" value={billDraft.sonOdemeTarihi} onChange={(e) => updateFinanceDraftField(setBillDraft, "bill", "sonOdemeTarihi", e.target.value)} aria-invalid={hasFinanceFieldError("bill", "sonOdemeTarihi")} />
                    </div>
                    <div className="inputLabelGroup" style={{ flex: 1 }}>
                      <label className="darkLabel">{t("finance.bills.statusLabel")}</label>
                      <select className="darkInput" value={billDraft.durum} onChange={(e) => setBillDraft({ ...billDraft, durum: e.target.value })}>
                        {faturaDurumlar.map((d) => <option key={d} value={d}>{fl(lang, d)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("finance.bills.payer")}</label>
                    <input className="darkInput" placeholder={t("finance.bills.payerPlaceholder")} value={billDraft.odemeSahibi} onChange={(e) => setBillDraft({ ...billDraft, odemeSahibi: e.target.value })} />
                  </div>
                  <input className="darkInput" placeholder={t("finance.bills.notePlaceholder")} value={billDraft.not} onChange={(e) => setBillDraft({ ...billDraft, not: e.target.value })} />
                  {renderFinanceNotificationTarget(billDraft, setBillDraft)}
                  {renderFinanceValidationMessage("bill", financeFormErrors.bill.map((field) => ({ ad: t("finance.bills.manualName"), tutar: t("finance.bills.amount"), sonOdemeTarihi: t("finance.bills.dueDate2") }[field])).filter(Boolean))}
                  <AsyncActionButton className="primaryButton financeSubmitButton" externalBusy={financeSubmitting.bill} busyText={billEditId ? t("common.saving") : undefined} onAction={saveBillEdit}>{billEditId ? t("common.save") : t("finance.bills.add")}</AsyncActionButton>
                  {billEditId && <button className="smallPremiumBtn compact" type="button" onClick={resetBillDraft}>{t("common.cancel")}</button>}
                </div>
              )}
            </div>
          )}

          <div className="darkCard financeBillFilterBar noSwipeBack" data-no-swipe-back="true" role="group" aria-label={lang === "en" ? "Bill filter" : "Fatura filtresi"}>
            <span className="financeBillFilterLabel">{lang === "en" ? "Filter" : "Filtre"}</span>
            <div className="financeBillFilterControl mode">
              <select
                className="darkInput"
                aria-label={lang === "en" ? "Bill period" : "Fatura dönemi"}
                value={billFilterMode}
                onChange={(e) => setBillFilterMode(e.target.value)}
              >
                <option value="currentMonth">{lang === "en" ? "This month" : "Bu ay"}</option>
                <option value="previousMonth">{lang === "en" ? "Previous month" : "Önceki ay"}</option>
                <option value="allYear">{lang === "en" ? "All bills in year" : "Yıl içindeki tüm faturalar"}</option>
                <option value="custom">{lang === "en" ? "Select month/year" : "Ay/Yıl seç"}</option>
              </select>
            </div>
            {(billFilterMode === "allYear" || billFilterMode === "custom") && (
              <div className="financeBillFilterControl year">
                <select
                  className="darkInput"
                  aria-label={fl(lang, "Yıl")}
                  value={billFilterYear}
                  onChange={(e) => setBillFilterYear(Number(e.target.value))}
                >
                  {yilSecenekleri.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            {billFilterMode === "custom" && (
              <div className="financeBillFilterControl month">
                <select
                  className="darkInput"
                  aria-label={lang === "en" ? "Month" : "Ay"}
                  value={billFilterMonth}
                  onChange={(e) => setBillFilterMonth(Number(e.target.value))}
                >
                  {AYLAR.map((a, i) => <option key={i} value={i}>{a}</option>)}
                </select>
              </div>
            )}
          </div>

          {billFilterMode === "currentMonth" && carriedOverBills.length > 0 && (
            <section className="billCarryoverSection" aria-labelledby="billCarryoverTitle">
              <div className="billCarryoverHeader">
                <div className="billCarryoverTitleGroup">
                  <span className="billCarryoverIcon" aria-hidden="true">↗</span>
                  <div>
                    <span className="billCarryoverEyebrow">{t("finance.bills.carryover.eyebrow")}</span>
                    <h3 id="billCarryoverTitle">{t("finance.bills.carryover.title")}</h3>
                  </div>
                </div>
                <span className="billCarryoverCount" aria-label={t("finance.bills.carryover.count", { count: carriedOverBills.length })}>
                  {carriedOverBills.length}
                </span>
              </div>
              <p className="billCarryoverHint">{t("finance.bills.carryover.hint")}</p>
              <div className="budgetList financeDetailList financeBillList billCarryoverList">
                {carriedOverBills.map((bill) => renderBillTile(bill, { carriedOver: true }))}
              </div>
            </section>
          )}

          {canAdd && billFilterMode === "currentMonth" && quickBillCandidates.length > 0 && (
            <section className="quickBillRailSection" aria-labelledby="quickBillRailTitle">
              <div className="quickBillRailHeader">
                <div>
                  <span className="quickBillRailEyebrow">{t("finance.bills.quick.eyebrow")}</span>
                  <h3 id="quickBillRailTitle">{t("finance.bills.quick.title")}</h3>
                </div>
                <span className="quickBillRailCount">{quickBillCandidates.length}</span>
              </div>
              <p className="quickBillRailHint">{t("finance.bills.quick.hint")}</p>
              <div className="quickBillRail noSwipeBack" data-no-swipe-back="true">
                {quickBillCandidates.map((bill) => {
                  const draft = quickBillDrafts[bill.id] || {};
                  const billName = isOtherValue(bill.kategori) && bill.ad ? bill.ad : fl(lang, bill.kategori || bill.ad);
                  const busy = quickBillAddingId === bill.id;
                  return (
                    <article className="quickBillCard" key={bill.id}>
                      <div className="quickBillCardTop">
                        <span className="quickBillIcon" aria-hidden="true">⚡</span>
                        <div>
                          <b>{billName}</b>
                          <span>{t("finance.bills.quick.lastMonth")}</span>
                        </div>
                      </div>
                      <div className="quickBillInputs">
                        <label>
                          <span>{t("finance.bills.amount")}</span>
                          <input
                            className="darkInput"
                            {...moneyInputProps}
                            value={draft.tutar || ""}
                            onChange={(event) => setQuickBillDrafts((current) => ({
                              ...current,
                              [bill.id]: { ...(current[bill.id] || {}), tutar: formatMoneyInput(event.target.value) },
                            }))}
                          />
                        </label>
                        <label>
                          <span>{t("finance.bills.dueDate2")}</span>
                          <input
                            className="darkInput"
                            type="date"
                            value={draft.sonOdemeTarihi || ""}
                            onChange={(event) => setQuickBillDrafts((current) => ({
                              ...current,
                              [bill.id]: { ...(current[bill.id] || {}), sonOdemeTarihi: event.target.value },
                            }))}
                          />
                        </label>
                      </div>
                      <AsyncActionButton className="quickBillAddButton" externalBusy={busy} busyText={t("finance.bills.quick.adding")} onAction={() => addQuickBill(bill)}>
                        {t("finance.bills.quick.add")}
                      </AsyncActionButton>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {billFilterMode === "currentMonth" && carriedOverBills.length > 0 && (
            <div className="billCurrentPeriodHeader" aria-label={t("finance.bills.currentMonth.title")}>
              <div>
                <span className="billCurrentPeriodEyebrow">{t("finance.bills.currentMonth.eyebrow")}</span>
                <h3>{t("finance.bills.currentMonth.title")}</h3>
              </div>
              <span className="billCurrentPeriodCount">{filteredBills.length}</span>
            </div>
          )}

          <div className="budgetList financeDetailList financeBillList">
            {filteredBills.length === 0 && carriedOverBills.length === 0 && (
              <div className="financeToolEmptyPremium" role="status" aria-live="polite">
                <img className="financeToolEmptyPremiumImage" src={finansAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" draggable="false" />
                <h3 className="financeToolEmptyPremiumTitle">
                  {lang === "en" ? "No bills here yet" : "Henüz fatura kaydı yok"}
                </h3>
                <p className="financeToolEmptyPremiumText">
                  {lang === "en"
                    ? "Add your bills and keep due dates safely under control."
                    : "Faturalarınızı ekleyin, son ödeme tarihlerini güvenle kontrol altında tutun."}
                </p>
              </div>
            )}
            {filteredBills.map((bill) => renderBillTile(bill))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FinancePage;
